import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FIREBASE_ENABLED, initFirebase,
  loginFirebase, logoutFirebase, createFirebaseUser, sendPasswordReset,
  fbSaveDoc, fbGetAll, fbDelete, fbGetDoc, fbSetDoc, getFBAuth,
  fbListenAll, fbListenDoc, fbListenWhere, fbCallFunction, fbEnablePush
} from './firebase';
import {
  syncCandidateToSheets, syncClientToSheets,
  deleteCandidateFromSheets, deleteClientFromSheets
} from './sheets';

// ─── Constants ────────────────────────────────────────────────────────────────
const SKILLS = ['Customer Service','Cash Handling','Food Safety','Night Shift','Bilingual',
  'Forklift','Hospitality','Cleaning','Bookkeeping','Driving','Security','Maintenance',
  'Front Desk','Cooking','Laundry','Management','Sales','Warehouse'];
const INDUSTRIES = ['Gas Station','Motel/Hotel','Restaurant','Convenience Store',
  'Laundromat','Retail','Warehouse','Construction','Other'];
const VISAS = ['US Citizen','Green Card','H-2B','H-1B','OPT/CPT','TN Visa','Other'];
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];
const STATE_NAMES = {AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',
  IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',
  MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',
  TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',
  WY:'Wyoming',DC:'District of Columbia'};
const stateName = (abbr) => STATE_NAMES[(abbr||'').toUpperCase().trim()] || abbr || '';
const DEFAULT_PROFILE = {
  companyName:'Three9 Services', city:'Borsad', address:'', country:'India',
  phone:'+916359933939', email:'info@three9.in', website:'www.three9.in',
  gst:'24FSTPM9244L1ZT', eic:'FSTPM9244L', ein:'', pan:'',
  bankName:'HDFC Bank', accountName:'Three9 Services', accountNo:'50100311128659',
  ifsc:'HDFC0000749', swift:'', zelle:'kaushal274patel@gmail.com', paypal:'',
  invoiceNote:'Thank you for your business!',
  paymentTerms:'Payment due once placement starts job.', logo:null,
  whatsappTemplate:'*{name}*\n🎂 Age: {age}\n📍 Currently in: {location}\n🛂 Visa: {visa}\n🌍 From: {origin}\n🇺🇸 Years in USA: {usaYears}\n⏰ Available: {availability}\n💼 Experience: {experience}\n🔧 Skills: {skills}\n📞 {phone}{noteSection}',
  contractTemplate:'This Placement Agreement is entered into between {companyName} ("Agency") and {clientName} ("Client"), located at {clientLocation}.\n\nThe Agency agrees to place the following candidate: {candidateName} for the position discussed, subject to a placement fee of {fee}, due {paymentTerms}\n\nThe Client agrees to notify the Agency of any termination within the first 90 days, which may affect fee adjustments per the Agency\'s replacement policy.\n\nBoth parties agree to the terms outlined by {companyName}\'s standard business practices, including the payment terms: {paymentTermsNote}\n\nSigned electronically on {date}.',
  partnerContractTemplate:'This Referral Partner Agreement is entered into between {companyName} ("Agency") and {partnerName} ("Referrer"), located at {partnerAddress}.\n\nThe Referrer agrees to refer candidates to the Agency for potential job placement. For each referred candidate who is successfully placed by the Agency and remains placed for 30 days, the Agency agrees to pay the Referrer a fee of {feeDescription}.\n\nPayment will be made within {paymentTerms} of the 30-day placement confirmation. The Agency reserves the right to decline any referred candidate at its sole discretion.\n\nEither party may terminate this agreement at any time with written notice; fees for placements confirmed prior to termination remain payable.\n\nSigned electronically on {date}.',
  clientContractTemplate:'This Service Agreement is entered into between {companyName} ("Agency") and {clientBusiness} ("Client"), represented by {clientName}, located at {clientLocation}.\n\nThe Agency agrees to source and refer candidates for job placement with the Client. For each candidate successfully placed and retained, the Client agrees to pay the Agency a placement fee of {feeDescription}.\n\nPayment is due {paymentTerms}. The Client agrees to notify the Agency promptly of any termination or issue with a placed candidate within the first 90 days, which may affect fee adjustments per the Agency\'s replacement policy.\n\nEither party may terminate this agreement at any time with written notice; fees for placements confirmed prior to termination remain payable.\n\nSigned electronically on {date}.',
  sheetsUrl:'',
  publicUrl:'',
  icon:'', // small square icon — used for the loading screen and browser tab favicon, separate from the big logo
};

// ─── Pipeline / activity / reminders constants ─────────────────────────────────
const PIPELINE_STAGES = ['New','Contacted','Interviewing','Offer Sent','Placed','Rejected'];
const STAGE_BG = {New:'#f0efe9',Contacted:'#E6F1FB',Interviewing:'#FAEEDA','Offer Sent':'#EAF3DE',Placed:'#185FA5',Rejected:'#FCEBEB'};
const STAGE_FG = {New:'#444',Contacted:'#0C447C',Interviewing:'#633806','Offer Sent':'#27500A',Placed:'#fff',Rejected:'#A32D2D'};
const STATE_PATHS = {
  WA:{d:'M 82,98 L 154,94 L 156,130 L 110,138 L 82,132 Z',cx:118,cy:114},
  OR:{d:'M 82,132 L 110,138 L 148,145 L 148,192 L 82,192 Z',cx:115,cy:163},
  CA:{d:'M 82,192 L 148,192 L 148,230 L 130,295 L 82,295 Z',cx:110,cy:243},
  ID:{d:'M 154,94 L 195,94 L 195,145 L 172,155 L 148,145 L 156,130 Z',cx:172,cy:122},
  NV:{d:'M 148,192 L 172,155 L 195,165 L 195,272 L 148,272 Z',cx:172,cy:218},
  AZ:{d:'M 148,272 L 195,272 L 225,272 L 225,322 L 148,322 Z',cx:187,cy:297},
  MT:{d:'M 195,94 L 260,94 L 260,155 L 195,155 Z',cx:227,cy:124},
  WY:{d:'M 195,155 L 260,155 L 260,208 L 195,208 Z',cx:228,cy:181},
  CO:{d:'M 195,208 L 260,208 L 260,255 L 195,255 Z',cx:228,cy:231},
  NM:{d:'M 195,255 L 260,255 L 260,310 L 195,310 Z',cx:228,cy:282},
  UT:{d:'M 195,165 L 218,165 L 218,255 L 195,255 Z',cx:206,cy:210},
  ND:{d:'M 260,94 L 322,94 L 322,140 L 260,140 Z',cx:291,cy:117},
  SD:{d:'M 260,140 L 322,140 L 322,185 L 260,185 Z',cx:291,cy:162},
  NE:{d:'M 260,185 L 322,185 L 322,222 L 260,222 Z',cx:291,cy:203},
  KS:{d:'M 260,222 L 332,222 L 332,255 L 260,255 Z',cx:296,cy:238},
  OK:{d:'M 260,255 L 340,255 L 340,288 L 260,288 Z',cx:300,cy:271},
  TX:{d:'M 225,272 L 340,272 L 350,355 L 270,370 L 220,340 Z',cx:285,cy:325},
  MN:{d:'M 322,94 L 368,94 L 368,158 L 322,158 Z',cx:345,cy:126},
  IA:{d:'M 322,158 L 368,158 L 375,200 L 322,200 Z',cx:348,cy:179},
  MO:{d:'M 322,200 L 375,200 L 378,255 L 322,255 Z',cx:349,cy:227},
  AR:{d:'M 322,255 L 378,255 L 378,295 L 322,295 Z',cx:350,cy:275},
  LA:{d:'M 322,295 L 378,295 L 378,335 L 348,345 L 322,332 Z',cx:348,cy:317},
  WI:{d:'M 368,94 L 405,94 L 408,165 L 368,165 Z',cx:387,cy:129},
  MI:{d:'M 405,128 L 440,120 L 445,165 L 405,168 Z',cx:423,cy:145},
  IL:{d:'M 375,165 L 408,165 L 408,252 L 375,252 Z',cx:391,cy:208},
  IN:{d:'M 408,165 L 432,165 L 432,248 L 408,248 Z',cx:420,cy:206},
  OH:{d:'M 432,165 L 462,165 L 462,245 L 432,245 Z',cx:447,cy:205},
  KY:{d:'M 375,252 L 450,252 L 450,278 L 375,278 Z',cx:412,cy:265},
  TN:{d:'M 375,278 L 455,278 L 455,302 L 375,302 Z',cx:415,cy:290},
  MS:{d:'M 355,295 L 390,295 L 390,348 L 355,348 Z',cx:372,cy:321},
  AL:{d:'M 390,295 L 422,295 L 422,350 L 390,350 Z',cx:406,cy:322},
  GA:{d:'M 422,285 L 462,285 L 462,342 L 422,342 Z',cx:442,cy:313},
  FL:{d:'M 422,342 L 462,342 L 472,382 L 440,400 L 408,378 Z',cx:440,cy:368},
  SC:{d:'M 455,268 L 490,268 L 490,305 L 455,305 Z',cx:472,cy:286},
  NC:{d:'M 455,248 L 510,248 L 510,270 L 455,270 Z',cx:482,cy:259},
  VA:{d:'M 450,228 L 512,228 L 512,252 L 450,252 Z',cx:481,cy:240},
  WV:{d:'M 450,222 L 475,222 L 475,252 L 450,252 Z',cx:462,cy:237},
  PA:{d:'M 462,190 L 518,190 L 518,218 L 462,218 Z',cx:490,cy:204},
  NY:{d:'M 475,162 L 535,155 L 535,195 L 475,195 Z',cx:505,cy:175},
  ME:{d:'M 555,112 L 578,112 L 578,158 L 555,158 Z',cx:566,cy:135},
  NH:{d:'M 543,148 L 560,148 L 560,180 L 543,180 Z',cx:551,cy:164},
  VT:{d:'M 525,148 L 543,148 L 543,178 L 525,178 Z',cx:534,cy:163},
  MA:{d:'M 530,178 L 568,178 L 568,192 L 530,192 Z',cx:549,cy:185},
  CT:{d:'M 530,192 L 550,192 L 550,205 L 530,205 Z',cx:540,cy:198},
  RI:{d:'M 550,192 L 562,192 L 562,206 L 550,206 Z',cx:556,cy:199},
  NJ:{d:'M 518,198 L 535,198 L 535,218 L 518,218 Z',cx:526,cy:208},
  DE:{d:'M 518,218 L 532,218 L 532,232 L 518,232 Z',cx:525,cy:225},
  MD:{d:'M 490,220 L 518,220 L 518,235 L 490,235 Z',cx:504,cy:227},
  DC:{d:'M 508,232 L 516,232 L 516,240 L 508,240 Z',cx:512,cy:236},
  AK:{d:'M 82,365 L 130,365 L 130,405 L 82,405 Z',cx:106,cy:385},
  HI:{d:'M 148,385 L 195,385 L 195,405 L 148,405 Z',cx:171,cy:395},
};

// ─── Utilities ────────────────────────────────────────────────────────────────
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2,6);
// A candidate marked as a couple placement represents 2 people, not 1.
// A candidate's "members" field represents how many people this record covers
// (e.g. a couple or family placed together), not just 1.
const personCount = (c) => c ? (parseInt(c.members,10) || 1) : 1;
function slugify(name, existing){
  let base = (name||'partner').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'') || 'partner';
  let slug = base, n = 2;
  const taken = new Set((existing||[]).map(r=>r.id));
  while (taken.has(slug)) { slug = `${base}-${n}`; n++; }
  return slug;
}
const today = () => new Date().toLocaleDateString('en-US',{year:'numeric',month:'2-digit',day:'2-digit'});
const addDaysISO = (base, days) => { const d = new Date(base); d.setDate(d.getDate()+days); return d.toISOString().slice(0,10); };
// Computes a referral partner's fee for a given client, checking category-specific
// overrides (e.g. $100 for Motel referrals, $50 for Gas Station) before falling
// back to the partner's default fee type/amount.
function computeReferralFee(partner, client, placementTotal) {
  const rule = (partner.feeRules||[]).find(r => r.industry === (client?.industry||''));
  const feeType = rule?.feeType || partner.feeType;
  if (feeType === 'percent') {
    const pct = parseFloat(rule ? rule.feePercent : partner.feePercent) || 0;
    return Math.round((placementTotal||0) * (pct/100) * 100) / 100;
  }
  return parseFloat(rule ? rule.feeAmount : partner.feeAmount) || 0;
}
// Date + time, e.g. "Oct 23, 2026, 8:04 PM"
function fmtDateTime(iso){
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d)) return String(iso);
  return d.toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'});
}
// When a candidate/client record came into the CRM: self-submission time if it came from
// a signup form, otherwise the time staff added it (older records only have a date).
function addedInfo(rec){
  if (rec.submittedAt) return {label:'Submitted', text:fmtDateTime(rec.submittedAt)};
  const ts = rec.addedAtTs || rec.createdAtTs;
  if (ts) return {label:'Added', text:fmtDateTime(ts)};
  return {label:'Added', text:rec.addedAt||''};
}
const initials = n => (n||'?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
// How many open slots a client still has, after subtracting people already placed
// there and people currently interviewing for one of their openings.
// Only actual placements close a position. A position stays interviewable —
// multiple candidates can interview for the same opening — until someone is
// actually placed there, which is when the slot is truly consumed.
function clientOpenSlots(client, candidates, placements) {
  const openings = parseInt(client.openings)||0;
  const filled = (placements||[]).filter(p=>p.clientId===client.id).reduce((a,p)=>a+(p.headcount||1),0);
  return Math.max(0, openings - filled);
}

// Shared placement flow — creates the invoice, updates the candidate's stage to
// Placed, and schedules any referral fee. Used from Match, Pipeline, and the
// Active Interviews roster so "mark as placed" always does the full job no
// matter where it's triggered from.
async function createPlacement({cand, client, placements, setPlacements, setCandidates, setClients, referralPartners, setReferralPayables, addAct, toast}) {
  if (!client || !cand) { if (toast) toast('Error finding records.'); return null; }
  if ((placements||[]).some(p=>p.candidateId===cand.id && p.clientId===client.id)) { if (toast) toast('Already placed together.'); return null; }
  const fee = parseFloat(client.placementFee||500);
  const disc = parseFloat(client.discPerUnit||0);
  const pl = {id:uid(),invNum:'INV-39S-'+Math.random().toString(36).slice(2,8).toUpperCase(),candidateId:cand.id,clientId:client.id,candidateName:cand.name,clientName:client.name,headcount:personCount(cand),items:[{desc:`Placement fee — ${client.name}${client.industry?' ('+client.industry+')':''}`,qty:1,rate:fee,discPerUnit:disc}],alreadyPaid:0,date:today(),dueDate:today(),status:'Active',total:fee-disc,candidateIndustry:cand.industry||'',candidateVisa:cand.visa==='Other'?(cand.visaOther||'Other'):(cand.visa||''),candidatePhoto:cand.photo||'',candidateExperience:cand.experience||'',candidateAge:cand.age||''};
  await DB.save('placements', pl.id, pl);
  if (setPlacements) setPlacements(p => [...p, pl]);
  if (setCandidates) {
    const updatedCand = {...cand, stage:'Placed', interviewClientId:'', interviewClientName:''};
    await DB.save('candidates', cand.id, updatedCand);
    setCandidates(p => p.map(c => c.id===cand.id ? updatedCand : c));
  }
  // Track this placement's ID on the client record — lets the public client
  // portal page fetch exactly (and only) this client's placements safely,
  // without needing a broad/unsafe public query across the whole collection.
  // Also drop them from the "currently interviewing" index now that they're placed.
  if (setClients) {
    const updatedClient = {...client,
      placementIds: [...(client.placementIds||[]), pl.id],
      interviewingCandidateIds: (client.interviewingCandidateIds||[]).filter(id=>id!==cand.id)};
    await DB.save('clients', client.id, updatedClient);
    setClients(p => p.map(c => c.id===client.id ? updatedClient : c));
  }
  // Referral fee owed to an immigration office / partner — scheduled now, confirmed as owed 30 days after placement
  if (cand.referralPartnerId) {
    const partner = (referralPartners||[]).find(rp=>rp.id===cand.referralPartnerId);
    if (partner && setReferralPayables) {
      const refAmount = computeReferralFee(partner, client, pl.total);
      const payable = {id:uid(), partnerId:partner.id, partnerName:partner.name, candidateId:cand.id, candidateName:cand.name,
        placementId:pl.id, clientName:client.name, clientIndustry:client.industry||'', amount:refAmount, placedDate:today(), dueDate:addDaysISO(new Date(),30),
        status:'Scheduled', paidDate:''};
      await DB.save('referralPayables', payable.id, payable);
      setReferralPayables(p => [...p, payable]);
      if (addAct) addAct(`🤝 Referral fee ${fmtUSD(refAmount)} scheduled for "${partner.name}" — confirm at 30 days on ${payable.dueDate}`);
    } else if (addAct) {
      // Happens when a placement is created without partner data on hand (e.g. a
      // client marking someone placed from their own portal) — flagged so staff
      // know to check whether a referral fee needs to be scheduled manually.
      addAct(`⚠ "${cand.name}" was referred but no referral fee was auto-scheduled for this placement — check the Referrals tab.`);
    }
  }
  if (toast) toast('✅ Placed! Invoice generated.');
  if (addAct) addAct(`✅ Placed "${cand.name}" at "${client.name}" — ${fmtUSD(fee-disc)}`);
  return pl;
}
// Schedules an interview and keeps the client's `interviewingCandidateIds` index
// in sync — that index is what lets a client-login account (and the portal)
// safely see only the specific candidates interviewing at their own business.
async function scheduleCandidateInterview({cand, client, setCandidates, setClients, addAct, toast}) {
  if (!client || !cand) return null;
  const updatedCand = {...cand, stage:'Interviewing', interviewClientId:client.id, interviewClientName:client.name};
  await DB.save('candidates', cand.id, updatedCand);
  if (setCandidates) setCandidates(p => p.map(c => c.id===cand.id ? updatedCand : c));
  if (setClients) {
    const updatedClient = {...client, interviewingCandidateIds: [...new Set([...(client.interviewingCandidateIds||[]), cand.id])]};
    await DB.save('clients', client.id, updatedClient);
    setClients(p => p.map(c => c.id===client.id ? updatedClient : c));
  }
  if (toast) toast(`🗓 Interview scheduled with ${client.name}`);
  if (addAct) addAct(`🗓 Scheduled interview — "${cand.name}" with "${client.name}"`);
  return updatedCand;
}
// Reverts a candidate out of Interviewing — used both when staff cancel an
// interview and when a client rejects a candidate from their own portal.
async function rejectCandidateInterview({cand, client, setCandidates, setClients, addAct, toast, byClient}) {
  if (!cand) return null;
  const updatedCand = {...cand, stage:'Contacted', interviewClientId:'', interviewClientName:''};
  await DB.save('candidates', cand.id, updatedCand);
  if (setCandidates) setCandidates(p => p.map(c => c.id===cand.id ? updatedCand : c));
  if (client && setClients) {
    const updatedClient = {...client, interviewingCandidateIds: (client.interviewingCandidateIds||[]).filter(id=>id!==cand.id)};
    await DB.save('clients', client.id, updatedClient);
    setClients(p => p.map(c => c.id===client.id ? updatedClient : c));
  }
  if (toast) toast(byClient ? 'Marked as not moving forward.' : 'Interview cancelled.');
  if (addAct) addAct(byClient ? `❌ ${client?.name||'Client'} passed on "${cand.name}"` : `↩ Cancelled interview for "${cand.name}"`);
  return updatedCand;
}
const fmtUSD = n => '$'+parseFloat(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

const LS = {
  get:(k,d=[])=>{try{return JSON.parse(localStorage.getItem(k))??d;}catch{return d;}},
  set:(k,v)=>{try{localStorage.setItem(k,JSON.stringify(v));}catch{}},
};

const DB = {
  async getAll(col){if(FIREBASE_ENABLED)return fbGetAll(col);return LS.get('t9_'+col,[]);},
  async save(col,id,data){if(FIREBASE_ENABLED)return fbSaveDoc(col,id,data);const items=LS.get('t9_'+col,[]);const idx=items.findIndex(i=>i.id===id);if(idx>=0)items[idx]=data;else items.push(data);LS.set('t9_'+col,items);},
  async delete(col,id){if(FIREBASE_ENABLED)return fbDelete(col,id);LS.set('t9_'+col,LS.get('t9_'+col,[]).filter(i=>i.id!==id));},
  async getOne(col,id){if(FIREBASE_ENABLED)return fbGetDoc(col,id);return LS.get('t9_'+col,[]).find(i=>i.id===id)||null;},
  async saveProfile(data){if(FIREBASE_ENABLED)return fbSetDoc('settings','profile',data);LS.set('t9_profile',data);},
  async getProfile(){if(FIREBASE_ENABLED)return fbGetDoc('settings','profile');return LS.get('t9_profile',null);},
  // Realtime listeners — keep the app synced in the background. In local (no-Firebase)
  // mode there's nothing to listen to, so this does a one-time load and returns a no-op unsubscribe.
  async listenAll(col,callback){
    if(FIREBASE_ENABLED) return fbListenAll(col,callback);
    callback(LS.get('t9_'+col,[]));
    return ()=>{};
  },
  async listenProfile(callback){
    if(FIREBASE_ENABLED) return fbListenDoc('settings','profile',callback);
    callback(LS.get('t9_profile',null));
    return ()=>{};
  },
  getLocalUsers:()=>LS.get('t9_users',[]),
  saveLocalUsers:(u)=>LS.set('t9_users',u),
};

// ─── Activity log helper ────────────────────────────────────────────────────────
async function logActivity(entry){
  const rec = {id:uid(), ts:new Date().toISOString(), ...entry};
  await DB.save('activity', rec.id, rec);
  return rec;
}

// ─── WhatsApp message builder ───────────────────────────────────────────────────
function buildWhatsAppMsg(cand, template){
  const tmpl = template || DEFAULT_PROFILE.whatsappTemplate;
  const noteSection = cand.notes ? `\n📝 Note: ${cand.notes}` : '';
  const location = [cand.currentCity, cand.currentState].filter(Boolean).join(', ') || '—';
  const visaDisplay = cand.visa==='Other' ? (cand.visaOther||'Other') : (cand.visa||'—');
  const map = { name:cand.name||'—', age:cand.age||'—', location, visa:visaDisplay, origin:cand.originCountry||'—',
    usaYears:cand.yearsInUSA||'—', availability:cand.availability||'—', members:String(parseInt(cand.members,10)||1),
    experience:cand.experience?(cand.experience+' yrs'):'—', skills:(cand.skills||[]).join(', ')||'—',
    phone:cand.phone||'—', noteSection };
  return Object.keys(map).reduce((out,k)=>out.split('{'+k+'}').join(map[k]), tmpl);
}
function waLink(phone, msg){
  const digits = (phone||'').replace(/[^0-9]/g,'');
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

// ─── Duplicate detection ────────────────────────────────────────────────────────
function normPhone(p){ return (p||'').replace(/[^0-9]/g,'').slice(-10); }
function findDuplicateGroups(list){
  const byPhone = {};
  list.forEach(r=>{
    const key = normPhone(r.phone);
    if(!key) return;
    if(!byPhone[key]) byPhone[key]=[];
    byPhone[key].push(r);
  });
  return Object.values(byPhone).filter(g=>g.length>1);
}
function checkDuplicate(list, phone, excludeId){
  const key = normPhone(phone);
  if(!key) return null;
  return list.find(r=>r.id!==excludeId && normPhone(r.phone)===key) || null;
}

// ─── Simple CSV parser (handles quoted fields) ─────────────────────────────────
function parseCSV(text){
  const rows = [];
  let row=[], field='', inQuotes=false;
  const pushField=()=>{row.push(field); field='';};
  const pushRow=()=>{pushField(); rows.push(row); row=[];};
  for(let i=0;i<text.length;i++){
    const c=text[i], n=text[i+1];
    if(inQuotes){
      if(c==='"'&&n==='"'){field+='"';i++;}
      else if(c==='"'){inQuotes=false;}
      else field+=c;
    } else {
      if(c==='"') inQuotes=true;
      else if(c===',') pushField();
      else if(c==='\n'){ if(field!==''||row.length>0) pushRow(); }
      else if(c==='\r'){ /* skip */ }
      else field+=c;
    }
  }
  if(field!==''||row.length>0) pushRow();
  if(rows.length===0) return [];
  const headers = rows[0].map(h=>h.trim());
  return rows.slice(1).filter(r=>r.some(c=>(c||'').trim()!=='')).map(r=>{
    const obj={};
    headers.forEach((h,idx)=>{obj[h]=(r[idx]||'').trim();});
    return obj;
  });
}

function matchScore(cand,client){
  let s=0;
  const needed=(client.skills||'').toLowerCase().split(',').map(x=>x.trim()).filter(Boolean);
  (cand.skills||[]).forEach(sk=>{if(needed.some(n=>sk.toLowerCase().includes(n)||n.includes(sk.toLowerCase())))s+=18;});
  if(client.industry&&cand.industry&&cand.industry===client.industry)s+=15;
  if(client.carNeeded==='Yes'&&cand.hasCar==='Yes')s+=10;
  if(client.legalOnly==='Yes'&&(cand.visa==='US Citizen'||cand.visa==='Green Card'))s+=15;
  if(cand.availability==='Immediate')s+=7;
  return Math.min(s,100);
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const bl='#185FA5',rd='#E24B4A',gr='#2d6a4f',br='#e4e4dc',bg='#f0efe9',tx='#1a1a18';
const S={
  card:{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,padding:20,marginBottom:12},
  fg2:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10},
  fg3:{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10},
  fg4:{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10},
  fgroup:{display:'flex',flexDirection:'column',gap:3},
  lbl:{fontSize:11,fontWeight:600,color:'#666',textTransform:'uppercase',letterSpacing:'.4px'},
  inp:{width:'100%',padding:'7px 9px',border:`0.5px solid #ccc`,borderRadius:7,fontSize:13,background:'#fff',color:tx,outline:'none',fontFamily:'inherit',boxSizing:'border-box'},
  sel:{width:'100%',padding:'7px 9px',border:`0.5px solid #ccc`,borderRadius:7,fontSize:13,background:'#fff',color:tx,outline:'none',cursor:'pointer',fontFamily:'inherit',boxSizing:'border-box'},
  ta:{width:'100%',padding:'7px 9px',border:`0.5px solid #ccc`,borderRadius:7,fontSize:13,background:'#fff',color:tx,outline:'none',resize:'vertical',minHeight:60,fontFamily:'inherit',boxSizing:'border-box'},
  btn:{padding:'7px 14px',borderRadius:7,fontSize:13,fontWeight:500,cursor:'pointer',border:`0.5px solid #ccc`,background:'#fff',color:tx,display:'inline-flex',alignItems:'center',gap:5,whiteSpace:'nowrap'},
  btnP:{padding:'7px 14px',borderRadius:7,fontSize:13,fontWeight:500,cursor:'pointer',border:'none',background:bl,color:'#fff',display:'inline-flex',alignItems:'center',gap:5},
  btnR:{padding:'7px 14px',borderRadius:7,fontSize:13,fontWeight:500,cursor:'pointer',border:'none',background:rd,color:'#fff',display:'inline-flex',alignItems:'center',gap:5},
  btnG:{padding:'7px 14px',borderRadius:7,fontSize:13,fontWeight:500,cursor:'pointer',border:'none',background:gr,color:'#fff',display:'inline-flex',alignItems:'center',gap:5},
  btnSm:{padding:'4px 9px',borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',border:`0.5px solid #ccc`,background:'#fff',color:tx,display:'inline-flex',alignItems:'center',gap:4},
  li:{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px',marginBottom:7,display:'flex',alignItems:'center',gap:10},
  av:{width:36,height:36,borderRadius:'50%',background:'#B5D4F4',color:'#0C447C',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:600,flexShrink:0},
  avC:{width:36,height:36,borderRadius:'50%',background:'#9FE1CB',color:'#085041',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:600,flexShrink:0},
  tB:{display:'inline-flex',alignItems:'center',padding:'2px 7px',borderRadius:20,fontSize:11,fontWeight:500,background:'#E6F1FB',color:'#0C447C'},
  tG:{display:'inline-flex',alignItems:'center',padding:'2px 7px',borderRadius:20,fontSize:11,fontWeight:500,background:'#EAF3DE',color:'#27500A'},
  tA:{display:'inline-flex',alignItems:'center',padding:'2px 7px',borderRadius:20,fontSize:11,fontWeight:500,background:'#FAEEDA',color:'#633806'},
  tR:{display:'inline-flex',alignItems:'center',padding:'2px 7px',borderRadius:20,fontSize:11,fontWeight:500,background:'#FCEBEB',color:'#A32D2D'},
  tGr:{display:'inline-flex',alignItems:'center',padding:'2px 7px',borderRadius:20,fontSize:11,fontWeight:500,background:'#F1EFE8',color:'#444'},
  tT:{display:'inline-flex',alignItems:'center',padding:'2px 7px',borderRadius:20,fontSize:11,fontWeight:500,background:'#E1F5EE',color:'#085041'},
};

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Btn({style,prim,red,grn,sm,disabled,...p}){
  const base=sm?S.btnSm:prim?S.btnP:red?S.btnR:grn?S.btnG:S.btn;
  return <button style={{...base,...(disabled?{opacity:.5,cursor:'not-allowed'}:{}),...style}} disabled={disabled} {...p}/>;
}
function FG({label:lb,children,style}){return <div style={{...S.fgroup,...style}}><label style={S.lbl}>{lb}</label>{children}</div>;}
function Inp({style,...p}){return <input style={{...S.inp,...style}} {...p}/>;}
// Auto-formats US-style phone numbers as (xxx) xxx-xxxx while typing.
// Leaves anything with a leading + (international) or 11+ digits untouched
// beyond digit-stripping, so foreign numbers aren't mangled.
function formatPhone(raw){
  if (!raw) return raw;
  if (raw.trim().startsWith('+')) return raw; // international — don't reformat
  const digits = raw.replace(/\D/g,'').slice(0,10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}
function PhoneInput({value,onChange,...p}){
  return <Inp {...p} type="tel" value={value} onChange={e=>onChange({target:{value:formatPhone(e.target.value)}})}/>;
}
// Resizes/compresses an image file client-side before storing it as a base64
// data URL, so a candidate photo doesn't blow past Firestore's per-doc size limit.
function resizeImageFile(file, maxDim=480, quality=0.82, keepAlpha=false){
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not read image'));
      img.onload = () => {
        let {width, height} = img;
        if (width > height && width > maxDim) { height = Math.round(height*(maxDim/width)); width = maxDim; }
        else if (height > maxDim) { width = Math.round(width*(maxDim/height)); height = maxDim; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(keepAlpha ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', quality));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}
function PhotoUpload({value, onChange, size=72}){
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try { onChange(await resizeImageFile(file)); } catch { /* ignore — photo stays as-is */ }
    setBusy(false);
  };
  return (
    <div style={{display:'flex',alignItems:'center',gap:12}}>
      <div style={{width:size,height:size,borderRadius:'50%',overflow:'hidden',background:'#f0efe9',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,border:`0.5px solid ${br}`}}>
        {value ? <img src={value} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : <span style={{fontSize:22,color:'#ccc'}}>👤</span>}
      </div>
      <div>
        <label style={{display:'inline-block',padding:'6px 12px',borderRadius:7,border:`1.5px solid ${br}`,cursor:'pointer',fontSize:12,color:tx,fontWeight:600}}>
          {busy?'Processing...':(value?'Change photo':'Upload photo')}
          <input type="file" accept="image/*" style={{display:'none'}} onChange={handleFile} disabled={busy}/>
        </label>
        {value && <Btn sm style={{marginLeft:6,color:rd,borderColor:rd}} onClick={()=>onChange('')}>Remove</Btn>}
      </div>
    </div>
  );
}
// Shows the company's actual uploaded logo when available; otherwise falls back
// to a badge with the company's real initials (never the hardcoded word "T9") —
// used on every public-facing page so branding reflects what's in My Business.
function BrandBadge({profile, size=52, preferIcon=false}){
  if (!profile) return <div style={{height:size,margin:'0 auto 12px'}}/>; // profile still loading — don't flash placeholder initials
  const p = profile;
  const img = preferIcon ? (p.icon || p.logo) : (p.logo || p.icon);
  if (img) return <img src={img} alt="logo" style={{maxHeight:size,maxWidth:size*2.8,objectFit:'contain',margin:'0 auto 12px',display:'block'}}/>;
  const initials = (p.companyName||'').split(' ').map(w=>w[0]).filter(Boolean).join('').toUpperCase().slice(0,3) || 'CO';
  return <div style={{width:size,height:size,background:bl,borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px',fontSize:size*0.36,fontWeight:900,color:'#fff'}}>{initials}</div>;
}
// Public pages have no login, so they can't use the authenticated profile listener —
// this does a one-off public read instead (requires the Firestore rule from
// CANDIDATE_LINKS_SETUP.txt allowing `get` on settings/profile).
// Sets the browser tab icon dynamically from the uploaded icon (or logo as a
// fallback) — the <link rel="icon"> tag can't be styled via React, so this
// reaches into the document head directly whenever the image URL changes.
function useFavicon(iconUrl){
  useEffect(() => {
    if (!iconUrl) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = iconUrl;
  }, [iconUrl]);
}
function usePublicProfile(){
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { if (FIREBASE_ENABLED) await initFirebase(); const p = await DB.getProfile(); if (!cancelled && p) setProfile({...DEFAULT_PROFILE, ...p}); }
      catch { /* falls back to DEFAULT_PROFILE branding */ }
      if (!cancelled) setProfile(prev => prev || DEFAULT_PROFILE);
    })();
    return () => { cancelled = true; };
  }, []);
  useFavicon(profile?.icon || profile?.logo);
  return profile;
}
function Sel({children,style,...p}){return <select style={{...S.sel,...style}} {...p}>{children}</select>;}
function TA({style,...p}){return <textarea style={{...S.ta,...style}} {...p}/>;}
function Div(){return <div style={{height:'0.5px',background:br,margin:'14px 0'}}/>;}
function SecLabel({children}){return <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',color:bl,margin:'16px 0 8px',paddingBottom:4,borderBottom:`1px solid ${br}`}}>{children}</div>;}
function Toggle({label:lb,value,onChange,options=['Yes','No']}){
  return(<FG label={lb}><div style={{display:'flex',gap:6,marginTop:2}}>{options.map(o=>(<div key={o} onClick={()=>onChange(o)} style={{padding:'5px 12px',borderRadius:6,fontSize:12,cursor:'pointer',border:`0.5px solid ${value===o?bl:'#ccc'}`,background:value===o?'#E6F1FB':'#fff',color:value===o?'#0C447C':tx,fontWeight:value===o?600:400}}>{o}</div>))}</div></FG>);
}
function SkillChips({selected,onToggle}){
  const sel = selected||[];
  return(<div style={{display:'flex',flexWrap:'wrap',gap:5,marginTop:4}}>{SKILLS.map(o=>(<div key={o} onClick={()=>onToggle(o)} style={{padding:'3px 9px',borderRadius:20,fontSize:11,cursor:'pointer',userSelect:'none',border:`0.5px solid ${sel.includes(o)?bl:'#e4e4dc'}`,background:sel.includes(o)?'#E6F1FB':'#f0efe9',color:sel.includes(o)?'#0C447C':'#555'}}>{sel.includes(o)?'✓ ':''}{o}</div>))}</div>);
}
function StatCard({n,label}){return(<div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px'}}><div style={{fontSize:22,fontWeight:700,color:'#0f172a'}}>{n}</div><div style={{fontSize:11,color:'#999',marginTop:1,textTransform:'uppercase',letterSpacing:'.4px'}}>{label}</div></div>);}
function SBar({value,onChange,placeholder}){return(<div style={{display:'flex',gap:8,marginBottom:14}}><input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{...S.inp,flex:1,padding:'8px 12px'}}/>{value&&<Btn sm onClick={()=>onChange('')}>✕ Clear</Btn>}</div>);}
function Toast({msg,onDone}){useEffect(()=>{const t=setTimeout(onDone,3000);return()=>clearTimeout(t);},[]);return <div style={{position:'fixed',bottom:24,right:24,background:'#1a1a18',color:'#fff',padding:'10px 16px',borderRadius:8,fontSize:13,zIndex:9999,display:'flex',alignItems:'center',gap:7,boxShadow:'0 4px 20px rgba(0,0,0,.3)'}}><span style={{color:'#4ade80'}}>✓</span> {msg}</div>;}
function Modal({children,onClose,maxWidth=700}){return(<div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:500,padding:20}}><div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:'100%',maxWidth,maxHeight:'90vh',overflowY:'auto'}}>{children}</div></div>);}

// ═══════════════════════════════════════════════════════════════════════════════
// FORM COMPONENTS — defined at TOP LEVEL so they never remount on parent render
// (This is the fix for the one-character-at-a-time typing bug)
// ═══════════════════════════════════════════════════════════════════════════════

// ── Candidate Form ────────────────────────────────────────────────────────────
function CandidateForm({value, onChange, onSave, onCancel, saveLabel, referralPartners, existingCandidates}) {
  const set = useCallback((k) => (v) => onChange(prev => ({...prev, [k]: v})), [onChange]);
  const setE = useCallback((k) => (e) => onChange(prev => ({...prev, [k]: e.target.value})), [onChange]);
  const toggleSkill = useCallback((sk) => {
    onChange(prev => ({...prev, skills: prev.skills.includes(sk) ? prev.skills.filter(s=>s!==sk) : [...prev.skills, sk]}));
  }, [onChange]);
  const setReferral = useCallback((e) => {
    const id = e.target.value;
    const rp = (referralPartners||[]).find(r=>r.id===id);
    onChange(prev => ({...prev, referralPartnerId:id, referralPartnerName: rp?rp.name:''}));
  }, [onChange, referralPartners]);
  const phoneDigits = (value.phone||'').replace(/\D/g,'');
  const phoneDupe = phoneDigits.length>=10 && existingCandidates
    ? checkDuplicate(existingCandidates, value.phone, value.id||null)
    : null;
  return (
    <div>
      <div style={{marginBottom:14}}>
        <label style={S.lbl}>Photo (optional)</label>
        <div style={{marginTop:5}}><PhotoUpload value={value.photo} onChange={p=>onChange(prev=>({...prev, photo:p}))}/></div>
      </div>
      <div style={S.fg2}>
        <FG label="Full name *"><Inp value={value.name} onChange={setE('name')} placeholder="John Martinez"/></FG>
        <FG label="Phone *"><PhoneInput value={value.phone} onChange={setE('phone')} placeholder="(555) 000-0000"/></FG>
        <FG label="Age"><Inp type="number" min={0} max={120} value={value.age} onChange={setE('age')} placeholder="32"/></FG>
        <FG label="Email"><Inp value={value.email} onChange={setE('email')} placeholder="john@email.com"/></FG>
        <FG label="Work authorization"><Sel value={value.visa} onChange={setE('visa')}><option value="">Select...</option>{VISAS.map(v=><option key={v}>{v}</option>)}</Sel></FG>
        {value.visa==='Other' && <FG label="Specify visa/authorization type"><Inp value={value.visaOther} onChange={setE('visaOther')} placeholder="e.g. Asylum pending, EAD..."/></FG>}
        <FG label="Industry"><Sel value={value.industry} onChange={setE('industry')}><option value="">Select...</option>{INDUSTRIES.map(v=><option key={v}>{v}</option>)}</Sel></FG>
        <FG label="Experience (years)"><Inp type="number" min={0} value={value.experience} onChange={setE('experience')} placeholder="3"/></FG>
        <FG label="Originally from"><Inp value={value.originCountry} onChange={setE('originCountry')} placeholder="India, Mexico..."/></FG>
        <FG label="Currently living in — city"><Inp value={value.currentCity} onChange={setE('currentCity')} placeholder="Birmingham"/></FG>
        <FG label="Currently living in — state"><Sel value={value.currentState} onChange={setE('currentState')}><option value="">Select...</option>{STATES.map(s=><option key={s}>{s}</option>)}</Sel></FG>
        <FG label="Years in USA"><Inp type="number" min={0} value={value.yearsInUSA} onChange={setE('yearsInUSA')} placeholder="5"/></FG>
        <FG label="Availability"><Sel value={value.availability} onChange={setE('availability')}><option>Immediate</option><option>2 weeks</option><option>1 month</option></Sel></FG>
        <FG label="Members (people this covers)"><Inp type="number" min={1} value={value.members} onChange={setE('members')} placeholder="1"/></FG>
      </div>
      {phoneDupe && (
        <div style={{background:'#FCEBEB',border:'0.5px solid #f3c9c9',borderRadius:8,padding:'8px 12px',marginTop:10,fontSize:12,color:'#A32D2D'}}>
          ⚠ This phone number is already on file for <strong>{phoneDupe.name}</strong>. Double check before saving.
        </div>
      )}
      <div style={{...S.fg3, marginTop:10}}>
        <Toggle label="Has a car" value={value.hasCar} onChange={set('hasCar')}/>
      </div>
      {referralPartners && referralPartners.length>0 && (
        <FG label="Referred by (partner or employee)" style={{marginTop:10}}>
          <Sel value={value.referralPartnerId||''} onChange={setReferral}>
            <option value="">— No referral —</option>
            {referralPartners.map(rp=><option key={rp.id} value={rp.id}>{rp.refType==='employee'?'👤 ':'🏛 '}{rp.name}</option>)}
          </Sel>
        </FG>
      )}
      <FG label="Skills" style={{marginTop:10}}><SkillChips selected={value.skills} onToggle={toggleSkill}/></FG>
      <FG label="Notes" style={{marginTop:10}}><TA value={value.notes} onChange={setE('notes')} placeholder="Additional info..."/></FG>
      <div style={S.fg2}>
        <FG label="Follow-up date" style={{marginTop:10}}><Inp type="date" value={value.followUpDate||''} onChange={setE('followUpDate')}/></FG>
        <FG label="Follow-up note" style={{marginTop:10}}><Inp value={value.followUpNote||''} onChange={setE('followUpNote')} placeholder="Call back about..."/></FG>
      </div>
      <div style={{display:'flex',gap:8,marginTop:14}}>
        <Btn prim onClick={onSave}>{saveLabel||'Add candidate'}</Btn>
        {onCancel && <Btn onClick={onCancel}>Cancel</Btn>}
      </div>
    </div>
  );
}

// ── Client Form ───────────────────────────────────────────────────────────────
function ClientForm({value, onChange, onSave, onCancel, saveLabel}) {
  const set = useCallback((k) => (v) => onChange(prev => ({...prev, [k]: v})), [onChange]);
  const setE = useCallback((k) => (e) => onChange(prev => ({...prev, [k]: e.target.value})), [onChange]);
  return (
    <div>
      <div style={S.fg2}>
        <FG label="Client name *"><Inp value={value.contact} onChange={setE('contact')} placeholder="Sam Patel"/></FG>
        <FG label="Business name"><Inp value={value.name} onChange={setE('name')} placeholder="Green Valley Motel"/></FG>
        <FG label="Phone"><PhoneInput value={value.phone} onChange={setE('phone')} placeholder="(555) 000-0000"/></FG>
        <FG label="Email"><Inp value={value.email} onChange={setE('email')} placeholder="owner@business.com"/></FG>
        <FG label="Industry"><Sel value={value.industry} onChange={setE('industry')}><option value="">Select...</option>{INDUSTRIES.map(v=><option key={v}>{v}</option>)}</Sel></FG>
        <FG label="City"><Inp value={value.city} onChange={setE('city')} placeholder="Birmingham"/></FG>
        <FG label="State"><Sel value={value.state} onChange={setE('state')}><option value="">Select...</option>{STATES.map(s=><option key={s}>{s}</option>)}</Sel></FG>
        <FG label="Open positions"><Inp type="number" min={1} value={value.openings} onChange={setE('openings')}/></FG>
        <FG label="Skills needed"><Inp value={value.skills} onChange={setE('skills')} placeholder="Customer Service, Cleaning"/></FG>
      </div>
      <SecLabel>💵 Pay structure</SecLabel>
      <Toggle label="Pay type" value={value.payType==='fixed'?'Fixed':'Hourly'} onChange={v=>set('payType')(v==='Fixed'?'fixed':'hourly')} options={['Hourly','Fixed']}/>
      <div style={S.fg2}>
        {value.payType==='fixed' ? (
          <>
            <FG label="Fixed pay amount ($)"><Inp type="number" min={0} value={value.fixedPay} onChange={setE('fixedPay')} placeholder="3000"/></FG>
            <FG label="Pay period"><Sel value={value.fixedPeriod||'Monthly'} onChange={setE('fixedPeriod')}><option>Weekly</option><option>Bi-weekly</option><option>Monthly</option><option>Per shift</option></Sel></FG>
          </>
        ) : (
          <FG label="Hourly wage ($)"><Inp value={value.wage} onChange={setE('wage')} placeholder="15.00"/></FG>
        )}
      </div>
      <SecLabel>⭐ Job details & priority</SecLabel>
      <div style={S.fg2}>
        <FG label="Priority"><Sel value={value.priority||''} onChange={setE('priority')}><option value="">Normal</option><option value="High">⭐ High priority</option><option value="Urgent">🔥 Urgent</option></Sel></FG>
        <FG label="Hours (e.g. 40/week)"><Inp value={value.hours||''} onChange={setE('hours')} placeholder="40 hrs/week"/></FG>
        <FG label="Shift"><Inp value={value.shift||''} onChange={setE('shift')} placeholder="Day, 8am–4pm"/></FG>
      </div>
      <FG label="Job description" style={{marginTop:10}}><TA value={value.jobDesc||''} onChange={setE('jobDesc')} placeholder="Duties, expectations, dress code..."/></FG>
      <div style={{fontSize:11,color:'#999',marginTop:4}}>Priority clients with open positions appear in the Priority tab for the whole team (without phone/email), and drop off automatically once filled.</div>
      <SecLabel>💰 Placement fee</SecLabel>
      <div style={S.fg2}>
        <FG label="Fee per worker ($)"><Inp type="number" min={0} value={value.placementFee} onChange={setE('placementFee')}/></FG>
        <FG label="Discount per unit ($)"><Inp type="number" min={0} value={value.discPerUnit} onChange={setE('discPerUnit')}/></FG>
      </div>
      <SecLabel>⚙️ Requirements & perks</SecLabel>
      <div style={S.fg2}>
        <Toggle label="Car required" value={value.carNeeded} onChange={set('carNeeded')}/>
        <Toggle label="Legal workers only" value={value.legalOnly} onChange={set('legalOnly')}/>
        <Toggle label="Pays in cash" value={value.cashPay} onChange={set('cashPay')}/>
        <Toggle label="Accommodation provided" value={value.accommodation} onChange={set('accommodation')}/>
      </div>
      <FG label="Notes" style={{marginTop:10}}><TA value={value.notes} onChange={setE('notes')} placeholder="Shift details, housing..."/></FG>
      <div style={S.fg2}>
        <FG label="Follow-up date" style={{marginTop:10}}><Inp type="date" value={value.followUpDate||''} onChange={setE('followUpDate')}/></FG>
        <FG label="Follow-up note" style={{marginTop:10}}><Inp value={value.followUpNote||''} onChange={setE('followUpNote')} placeholder="Check on opening..."/></FG>
      </div>
      <div style={{display:'flex',gap:8,marginTop:14}}>
        <Btn prim onClick={onSave}>{saveLabel||'Save client'}</Btn>
        {onCancel && <Btn onClick={onCancel}>Cancel</Btn>}
      </div>
    </div>
  );
}

// ── Profile Form ──────────────────────────────────────────────────────────────
function ProfileForm({value, onChange}) {
  const setE = useCallback((k) => (e) => onChange(prev => ({...prev, [k]: e.target.value})), [onChange]);
  return (
    <div>
      <SecLabel>🏢 Company information</SecLabel>
      <div style={S.fg2}>
        <FG label="Company name"><Inp value={value.companyName} onChange={setE('companyName')}/></FG>
        <FG label="City"><Inp value={value.city} onChange={setE('city')}/></FG>
        <FG label="Address"><Inp value={value.address} onChange={setE('address')}/></FG>
        <FG label="Country"><Inp value={value.country} onChange={setE('country')}/></FG>
        <FG label="Phone"><PhoneInput value={value.phone} onChange={setE('phone')} placeholder="(555) 000-0000"/></FG>
        <FG label="Email"><Inp value={value.email} onChange={setE('email')}/></FG>
        <FG label="Website"><Inp value={value.website} onChange={setE('website')}/></FG>
      </div>
      <SecLabel>🪪 Tax & registration</SecLabel>
      <div style={S.fg4}>
        <FG label="GST"><Inp value={value.gst} onChange={setE('gst')}/></FG>
        <FG label="EIC"><Inp value={value.eic} onChange={setE('eic')}/></FG>
        <FG label="EIN (US)"><Inp value={value.ein} onChange={setE('ein')}/></FG>
        <FG label="PAN"><Inp value={value.pan} onChange={setE('pan')}/></FG>
      </div>
      <SecLabel>🏦 Bank & payment</SecLabel>
      <div style={S.fg2}>
        <FG label="Bank name"><Inp value={value.bankName} onChange={setE('bankName')}/></FG>
        <FG label="Account name"><Inp value={value.accountName} onChange={setE('accountName')}/></FG>
        <FG label="Account number"><Inp value={value.accountNo} onChange={setE('accountNo')}/></FG>
        <FG label="IFSC / SWIFT"><Inp value={value.ifsc} onChange={setE('ifsc')}/></FG>
        <FG label="Zelle"><Inp value={value.zelle} onChange={setE('zelle')}/></FG>
        <FG label="PayPal"><Inp value={value.paypal} onChange={setE('paypal')}/></FG>
      </div>
      <SecLabel>📄 Invoice defaults</SecLabel>
      <div style={S.fg2}>
        <FG label="Footer note"><Inp value={value.invoiceNote} onChange={setE('invoiceNote')}/></FG>
        <FG label="Payment terms"><Inp value={value.paymentTerms} onChange={setE('paymentTerms')}/></FG>
      </div>
    </div>
  );
}

// ── Team / Employee Form ──────────────────────────────────────────────────────
function EmployeeForm({value, onChange}) {
  const setE = useCallback((k) => (e) => onChange(prev => ({...prev, [k]: e.target.value})), [onChange]);
  return (
    <div style={S.fg2}>
      <FG label="Full name"><Inp value={value.name} onChange={setE('name')} placeholder="Jane Smith"/></FG>
      <FG label="Email address"><Inp type="email" value={value.email} onChange={setE('email')} placeholder="jane@email.com"/></FG>
      <FG label="Password (min 6 chars)"><Inp type="password" value={value.password} onChange={setE('password')} placeholder="••••••••"/></FG>
      <FG label="Role">
        <Sel value={value.role} onChange={setE('role')}>
          <option value="employee">Employee (limited access)</option>
          <option value="admin">Admin (full access)</option>
        </Sel>
      </FG>
    </div>
  );
}

// ── Invoice Line Item Form ────────────────────────────────────────────────────
function LineItems({items, onChange}) {
  const update = useCallback((i,k,v) => onChange(its => its.map((it,idx) => idx===i ? {...it,[k]:v} : it)), [onChange]);
  const add = useCallback(() => onChange(its => [...its, {desc:'',qty:1,rate:500,discPerUnit:0}]), [onChange]);
  const remove = useCallback((i) => onChange(its => its.filter((_,idx)=>idx!==i)), [onChange]);
  return (
    <div>
      {items.map((it,i) => (
        <div key={i} style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
          <input value={it.desc} onChange={e=>update(i,'desc',e.target.value)} placeholder="Description" style={{...S.inp,flex:3,fontSize:12,padding:'5px 7px'}}/>
          <input type="number" min={1} value={it.qty} onChange={e=>update(i,'qty',e.target.value)} style={{...S.inp,width:50,fontSize:12,padding:'5px 7px',textAlign:'center'}}/>
          <input type="number" min={0} value={it.rate} onChange={e=>update(i,'rate',e.target.value)} placeholder="Rate" style={{...S.inp,width:80,fontSize:12,padding:'5px 7px'}}/>
          <input type="number" min={0} value={it.discPerUnit||0} onChange={e=>update(i,'discPerUnit',e.target.value)} placeholder="Disc" style={{...S.inp,width:70,fontSize:12,padding:'5px 7px'}}/>
          {items.length>1 && <button onClick={()=>remove(i)} style={{background:'none',border:'none',color:rd,cursor:'pointer',fontSize:18,padding:'0 3px'}}>×</button>}
        </div>
      ))}
      <Btn sm onClick={add} style={{marginTop:4}}>+ Add line</Btn>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// WHATSAPP MESSAGE BUILDER
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// WHATSAPP INBOX (WhatsApp Business Cloud API — see WHATSAPP_API_SETUP.txt)
// ═══════════════════════════════════════════════════════════════════════════════
const waDigits = p => { const d=String(p||'').replace(/\D/g,''); return d.length===10 ? '1'+d : d; };
const waLast10 = p => String(p||'').replace(/\D/g,'').slice(-10);
function parseWaTemplates(text){
  const rows = (text||'hello_world|en_US|0|Test message').split('\n').map(l=>l.trim()).filter(Boolean).map(l=>{
    const [name,language,vars,label] = l.split('|').map(x=>(x||'').trim());
    return {name, language:language||'en_US', vars:parseInt(vars,10)||0, label:label||name};
  }).filter(t=>t.name);
  return rows;
}
async function waSend(payload){
  try { return {ok:true, data: await fbCallFunction('sendWhatsApp', payload)}; }
  catch (e) {
    const m = String(e.message||'');
    return {ok:false, windowClosed:m.includes('WINDOW_CLOSED'), error:m.replace(/^.*WINDOW_CLOSED:\s*/,'') || 'Could not send.'};
  }
}

function WhatsAppTab({chats, candidates, clients, profile, toast}) {
  const [sel, setSel] = useState('');
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [showTpl, setShowTpl] = useState(false);
  const [tplIdx, setTplIdx] = useState(0);
  const [tplVars, setTplVars] = useState([]);
  const [narrow, setNarrow] = useState(typeof window!=='undefined' && window.innerWidth<720);
  const endRef = useRef(null);
  const templates = parseWaTemplates(profile?.waTemplates);
  const [pushState, setPushState] = useState(() => (typeof Notification!=='undefined' && Notification.permission==='granted' && localStorage.getItem('t9_push')==='1') ? 'on' : 'off');
  const [pushMsg, setPushMsg] = useState('');
  const enableNotifications = async () => {
    setPushState('working'); setPushMsg('');
    try {
      await fbEnablePush(profile?.fcmVapidKey);
      try { localStorage.setItem('t9_push','1'); } catch(e) {}
      setPushState('on'); setPushMsg('You will get a notification for every incoming WhatsApp message.');
    } catch (e) { setPushState('off'); setPushMsg(e.message || 'Could not enable notifications.'); }
  };

  useEffect(() => {
    const on = () => setNarrow(window.innerWidth<720);
    window.addEventListener('resize', on); return () => window.removeEventListener('resize', on);
  }, []);

  const nameFor = useCallback((phone, fallback) => {
    const k = waLast10(phone);
    const c = (candidates||[]).find(x=>waLast10(x.phone)===k);
    if (c) return {name:c.name, kind:'Candidate'};
    const cl = (clients||[]).find(x=>waLast10(x.phone)===k);
    if (cl) return {name:cl.contact||cl.name, kind:'Client'+(cl.name&&cl.contact?` · ${cl.name}`:'')};
    return {name:fallback||('+'+phone), kind:''};
  }, [candidates, clients]);

  const chat = (chats||[]).find(c=>c.phone===sel);
  useEffect(() => {
    if (!sel || !FIREBASE_ENABLED) { setMsgs([]); return; }
    let unsub = null, dead = false;
    (async () => { const u = await fbListenWhere('waMessages','phone',sel, d => !dead && setMsgs(d||[])); if (dead) u&&u(); else unsub = u; })();
    return () => { dead = true; unsub && unsub(); };
  }, [sel]);
  useEffect(() => {
    if (sel && chat && chat.unread>0) fbCallFunction('markWhatsAppRead',{phone:sel}).catch(()=>{});
  }, [sel, chat?.unread]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({block:'end'}); }, [msgs.length, sel]);

  const sorted = [...(chats||[])].sort((a,b)=>(b.lastTs||0)-(a.lastTs||0));
  const sortedMsgs = [...msgs].sort((a,b)=>(a.ts||0)-(b.ts||0));
  const windowOpen = chat && chat.lastInboundTs && (Date.now()-chat.lastInboundTs) < 24*3600*1000;
  const who = sel ? nameFor(sel, chat?.name) : null;

  const sendText = async () => {
    const text = draft.trim(); if (!text || sending) return;
    setSending(true);
    const r = await waSend({to:sel, text});
    setSending(false);
    if (r.ok) setDraft('');
    else { toast(r.error); if (r.windowClosed) setShowTpl(true); }
  };
  const sendTemplate = async () => {
    const t = templates[tplIdx]; if (!t) return;
    setSending(true);
    const r = await waSend({to:sel, template:{name:t.name, language:t.language, params:tplVars.slice(0,t.vars)}});
    setSending(false);
    if (r.ok) { setShowTpl(false); setTplVars([]); toast('Template sent ✓'); } else toast(r.error);
  };
  const startChat = () => {
    const d = waDigits(newPhone);
    if (d.length<11) { toast('Enter a full phone number.'); return; }
    setSel(d); setNewPhone(''); setShowTpl(true);
  };
  const tick = m => m.direction!=='out' ? '' : m.status==='failed' ? ' ⚠ failed' : m.status==='read' ? ' ✓✓' : m.status==='delivered' ? ' ✓✓' : ' ✓';

  const showList = !narrow || !sel;
  const showThread = !narrow || !!sel;
  return (
    <div>
      <div style={{fontSize:12,color:'#888',marginBottom:8}}>Two-way WhatsApp through your business number. You can reply freely for 24 hours after someone messages you; after that, use an approved template.</div>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:12}}>
        <Btn sm onClick={enableNotifications} disabled={pushState==='working'}>{pushState==='on'?'🔔 Notifications on (this device)':pushState==='working'?'Enabling...':'🔔 Enable notifications on this device'}</Btn>
        {pushMsg && <span style={{fontSize:11.5,color:pushState==='on'?gr:'#A32D2D'}}>{pushMsg}</span>}
      </div>
      {!FIREBASE_ENABLED && <div style={{background:'#FAEEDA',borderRadius:8,padding:'10px 14px',fontSize:12,marginBottom:12}}>WhatsApp API needs cloud sync (Firebase). See WHATSAPP_API_SETUP.txt.</div>}
      <div style={{display:'flex',gap:12,alignItems:'stretch',height:'calc(100vh - 230px)',minHeight:420}}>
        {showList && (
          <div style={{width:narrow?'100%':300,flexShrink:0,background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,display:'flex',flexDirection:'column',overflow:'hidden'}}>
            <div style={{padding:10,borderBottom:`0.5px solid ${br}`,display:'flex',gap:6}}>
              <div style={{flex:1}}><PhoneInput value={newPhone} onChange={e=>setNewPhone(e.target.value)} placeholder="New chat: (555) 000-0000"/></div>
              <Btn sm onClick={startChat}>＋</Btn>
            </div>
            <div style={{flex:1,overflowY:'auto'}}>
              {sorted.length===0 && <div style={{padding:24,textAlign:'center',color:'#aaa',fontSize:13}}>No conversations yet.<br/>Messages sent to your business number appear here.</div>}
              {sorted.map(c => { const w = nameFor(c.phone, c.name); const active = c.phone===sel; return (
                <div key={c.phone} onClick={()=>setSel(c.phone)} style={{padding:'10px 12px',cursor:'pointer',borderBottom:`0.5px solid #f0efe9`,background:active?'#E6F1FB':'#fff'}}>
                  <div style={{display:'flex',justifyContent:'space-between',gap:8}}>
                    <div style={{fontWeight:c.unread>0?800:600,fontSize:13.5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{w.name}</div>
                    <div style={{fontSize:10.5,color:'#999',flexShrink:0}}>{c.lastTs?new Date(c.lastTs).toLocaleDateString('en-US',{month:'short',day:'numeric'}):''}</div>
                  </div>
                  {w.kind && <div style={{fontSize:10.5,color:bl}}>{w.kind}</div>}
                  <div style={{display:'flex',justifyContent:'space-between',gap:8,marginTop:2}}>
                    <div style={{fontSize:12,color:'#777',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.lastDirection==='out'?'You: ':''}{c.lastText}</div>
                    {c.unread>0 && <span style={{background:gr,color:'#fff',borderRadius:10,fontSize:10.5,fontWeight:700,padding:'1px 7px',flexShrink:0}}>{c.unread}</span>}
                  </div>
                </div>
              ); })}
            </div>
          </div>
        )}
        {showThread && (
          <div style={{flex:1,minWidth:0,background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,display:'flex',flexDirection:'column',overflow:'hidden'}}>
            {!sel ? <div style={{margin:'auto',color:'#aaa',fontSize:13}}>Select a conversation</div> : (<>
              <div style={{padding:'10px 14px',borderBottom:`0.5px solid ${br}`,display:'flex',alignItems:'center',gap:10}}>
                {narrow && <Btn sm onClick={()=>setSel('')}>←</Btn>}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14}}>{who.name}</div>
                  <div style={{fontSize:11,color:'#888'}}>+{sel}{who.kind?` · ${who.kind}`:''}</div>
                </div>
                <span style={windowOpen?S.tG:S.tA}>{windowOpen?'● Reply window open':'Template only'}</span>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:'12px 14px',background:'#f6f5f0'}}>
                {sortedMsgs.length===0 && <div style={{textAlign:'center',color:'#aaa',fontSize:12,padding:20}}>No messages yet. Send a template to start the conversation.</div>}
                {sortedMsgs.map(m => (
                  <div key={m.id} style={{display:'flex',justifyContent:m.direction==='out'?'flex-end':'flex-start',marginBottom:6}}>
                    <div style={{maxWidth:'78%',background:m.direction==='out'?'#DCF8C6':'#fff',borderRadius:10,padding:'7px 10px',fontSize:13.5,whiteSpace:'pre-wrap',wordBreak:'break-word',boxShadow:'0 1px 1px rgba(0,0,0,.06)'}}>
                      {m.text}
                      <div style={{fontSize:10,color:m.status==='failed'?rd:'#999',marginTop:3,textAlign:'right'}}>{m.sentBy?`${m.sentBy} · `:''}{m.ts?new Date(m.ts).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}):''}{tick(m)}{m.status==='failed'&&m.error?` — ${m.error}`:''}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef}/>
              </div>
              {showTpl && (
                <div style={{padding:'10px 14px',borderTop:`0.5px solid ${br}`,background:'#FAFAF7'}}>
                  <div style={{fontSize:12,fontWeight:700,marginBottom:6}}>Send an approved template</div>
                  <Sel value={tplIdx} onChange={e=>{setTplIdx(parseInt(e.target.value,10)); setTplVars([]);}}>{templates.map((t,i)=><option key={i} value={i}>{t.label} ({t.name})</option>)}</Sel>
                  {Array.from({length:templates[tplIdx]?.vars||0}).map((_,i)=>(
                    <Inp key={i} style={{marginTop:6}} placeholder={`Variable {{${i+1}}}`} value={tplVars[i]||''} onChange={e=>setTplVars(v=>{const n=[...v]; n[i]=e.target.value; return n;})}/>
                  ))}
                  <div style={{display:'flex',gap:6,marginTop:8}}><Btn grn sm onClick={sendTemplate} disabled={sending}>{sending?'Sending...':'Send template'}</Btn><Btn sm onClick={()=>setShowTpl(false)}>Cancel</Btn></div>
                </div>
              )}
              <div style={{padding:10,borderTop:`0.5px solid ${br}`,display:'flex',gap:8,alignItems:'flex-end'}}>
                <TA value={draft} onChange={e=>setDraft(e.target.value)} placeholder={windowOpen?'Type a reply...':'Window closed — send a template instead'} disabled={!windowOpen}
                  onKeyDown={e=>{ if (e.key==='Enter' && (e.ctrlKey||e.metaKey)) { e.preventDefault(); sendText(); } }}
                  style={{minHeight:42,maxHeight:120,flex:1}}/>
                <div style={{display:'flex',flexDirection:'column',gap:5}}>
                  <Btn grn onClick={sendText} disabled={!windowOpen||sending||!draft.trim()}>{sending?'...':'Send'}</Btn>
                  <Btn sm onClick={()=>setShowTpl(v=>!v)}>Template</Btn>
                </div>
              </div>
            </>)}
          </div>
        )}
      </div>
    </div>
  );
}

function WhatsAppModal({cand, profile, onClose}) {
  const [msg, setMsg] = useState(() => buildWhatsAppMsg(cand, profile?.whatsappTemplate));
  const [apiBusy, setApiBusy] = useState(false);
  const [apiNote, setApiNote] = useState('');
  const sendApi = async () => {
    setApiBusy(true); setApiNote('');
    const r = await waSend({to:cand.phone, text:msg});
    setApiBusy(false);
    setApiNote(r.ok ? '✓ Sent from your business number.' : (r.windowClosed ? 'No reply from them in the last 24 hours, so free text is blocked. Open the WhatsApp tab and send a template first.' : r.error));
  };
  const copy = () => { navigator.clipboard?.writeText(msg); };
  return (
    <Modal onClose={onClose} maxWidth={480}>
      <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>📱 WhatsApp message</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>For {cand.name} · {cand.phone}</div>
      <TA value={msg} onChange={e=>setMsg(e.target.value)} style={{minHeight:180,fontFamily:'inherit'}}/>
      <div style={{display:'flex',gap:8,marginTop:14}}>
        {profile?.waApiEnabled==='Yes' && <Btn grn onClick={sendApi} disabled={apiBusy}>{apiBusy?'Sending...':'⚡ Send via Business API'}</Btn>}
        <Btn grn={profile?.waApiEnabled!=='Yes'} onClick={()=>window.open(waLink(cand.phone, msg), '_blank')}>💬 Open in WhatsApp</Btn>
        <Btn onClick={copy}>📋 Copy text</Btn>
        <Btn onClick={onClose}>Close</Btn>
      </div>
      {apiNote && <div style={{fontSize:12,marginTop:10,color:apiNote.startsWith('✓')?gr:'#A32D2D'}}>{apiNote}</div>}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DUPLICATE SCANNER MODAL
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY-SPECIFIC FEE RULES EDITOR (e.g. $100 for Motel referrals, $50 for Gas Station)
// ═══════════════════════════════════════════════════════════════════════════════
function FeeRulesEditor({rules, onChange, refType}) {
  const [industry, setIndustry] = useState(INDUSTRIES[0]);
  const [feeType, setFeeType] = useState('flat');
  const [amount, setAmount] = useState('100');
  const [percent, setPercent] = useState('10');
  const usedIndustries = new Set((rules||[]).map(r=>r.industry));
  const available = INDUSTRIES.filter(i=>!usedIndustries.has(i));

  const addRule = () => {
    if (!industry) return;
    onChange([...(rules||[]), {industry, feeType, feeAmount:amount, feePercent:percent}]);
  };
  const removeRule = (ind) => onChange((rules||[]).filter(r=>r.industry!==ind));

  return (
    <div style={{marginTop:10}}>
      <SecLabel>Category-specific {refType==='employee'?'incentives':'fees'} (optional)</SecLabel>
      <div style={{fontSize:11,color:'#999',marginBottom:8}}>Override the default {refType==='employee'?'incentive':'fee'} for specific client industries — e.g. $100 for Motel referrals but $50 for Gas Station. Any industry without a rule uses the default above.</div>
      {(rules||[]).length>0 && (
        <div style={{marginBottom:10}}>
          {rules.map(r => (
            <div key={r.industry} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#f8f8f4',borderRadius:7,marginBottom:5,fontSize:12.5}}>
              <span style={{fontWeight:600,flex:1}}>{r.industry}</span>
              <span>{r.feeType==='flat' ? fmtUSD(r.feeAmount||0) : `${r.feePercent||0}%`}</span>
              <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>removeRule(r.industry)}>✕</Btn>
            </div>
          ))}
        </div>
      )}
      {available.length>0 && (
        <div style={{display:'flex',gap:6,alignItems:'flex-end',flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:110}}>
            <label style={S.lbl}>Industry</label>
            <Sel value={industry} onChange={e=>setIndustry(e.target.value)}>{available.map(i=><option key={i}>{i}</option>)}</Sel>
          </div>
          <div style={{minWidth:100}}>
            <label style={S.lbl}>Type</label>
            <Sel value={feeType} onChange={e=>setFeeType(e.target.value)}><option value="flat">Flat $</option><option value="percent">%</option></Sel>
          </div>
          <div style={{minWidth:90}}>
            <label style={S.lbl}>{feeType==='flat'?'Amount ($)':'Percent'}</label>
            <Inp type="number" min={0} value={feeType==='flat'?amount:percent} onChange={e=>feeType==='flat'?setAmount(e.target.value):setPercent(e.target.value)}/>
          </div>
          <Btn sm onClick={addRule}>+ Add rule</Btn>
        </div>
      )}
    </div>
  );
}

function DuplicateModal({list, labelKey, onClose}) {
  const groups = findDuplicateGroups(list);
  return (
    <Modal onClose={onClose} maxWidth={560}>
      <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>🔍 Duplicate scan</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>Matches found by phone number</div>
      {groups.length===0 && <div style={{textAlign:'center',padding:30,color:'#aaa'}}>✓ No duplicates found.</div>}
      {groups.map((g,i) => (
        <div key={i} style={{background:'#FCEBEB',border:'0.5px solid #f3c9c9',borderRadius:8,padding:'10px 12px',marginBottom:8}}>
          <div style={{fontSize:11,fontWeight:700,color:'#A32D2D',marginBottom:4}}>⚠ Same phone number ({g[0].phone})</div>
          {g.map(r => <div key={r.id} style={{fontSize:13}}>{r[labelKey]} <span style={{color:'#888',fontSize:11}}>· added {r.addedAt}</span></div>)}
        </div>
      ))}
      <div style={{marginTop:10}}><Btn onClick={onClose}>Close</Btn></div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BULK CSV/EXCEL IMPORT MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function BulkImportModal({fields, sample, onImport, onClose}) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [err, setErr] = useState('');

  const handleFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setFileName(file.name); setErr('');
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const parsed = parseCSV(ev.target.result);
        if (parsed.length===0) { setErr('No rows found in file.'); return; }
        setRows(parsed);
      } catch { setErr('Could not parse this file. Please use a CSV export.'); }
    };
    reader.readAsText(file);
  };

  return (
    <Modal onClose={onClose} maxWidth={700}>
      <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>📥 Bulk import (CSV)</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>Expected columns: <strong>{fields.join(', ')}</strong>. Export your spreadsheet as CSV first.</div>
      <label style={{display:'inline-block',padding:'8px 14px',borderRadius:8,border:'1.5px dashed #ccc',cursor:'pointer',fontSize:13,color:'#666',marginBottom:10}}>
        {fileName || 'Choose CSV file...'}
        <input type="file" accept=".csv,text/csv" style={{display:'none'}} onChange={handleFile}/>
      </label>
      {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'7px 11px',fontSize:12,color:'#A32D2D',marginBottom:10}}>⚠ {err}</div>}
      {rows.length>0 && (
        <div>
          <div style={{fontSize:12,color:gr,fontWeight:600,marginBottom:8}}>✓ {rows.length} row(s) ready to import</div>
          <div style={{maxHeight:220,overflow:'auto',border:`0.5px solid ${br}`,borderRadius:8}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11.5}}>
              <thead><tr style={{background:'#f8f8f4'}}>{Object.keys(rows[0]).map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',borderBottom:`0.5px solid ${br}`}}>{h}</th>)}</tr></thead>
              <tbody>{rows.slice(0,8).map((r,i)=>(<tr key={i}>{Object.keys(rows[0]).map(h=><td key={h} style={{padding:'6px 8px',borderBottom:'0.5px solid #f0efe9'}}>{r[h]}</td>)}</tr>))}</tbody>
            </table>
          </div>
          {rows.length>8 && <div style={{fontSize:11,color:'#aaa',marginTop:4}}>+ {rows.length-8} more row(s) not shown</div>}
        </div>
      )}
      <div style={{display:'flex',gap:8,marginTop:14}}>
        <Btn prim disabled={rows.length===0} onClick={()=>onImport(rows)}>Import {rows.length||''} record(s)</Btn>
        <Btn onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTRACT GENERATOR MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function ContractModal({pl, client, profile, onClose, existingContract, onSigned, addAct}) {
  const p = {...DEFAULT_PROFILE, ...profile};
  const taxLines = [
    p.gst?`GST: ${p.gst}`:'', p.eic?`EIC: ${p.eic}`:'',
    p.ein?`EIN: ${p.ein}`:'', p.pan?`PAN: ${p.pan}`:'',
  ].filter(Boolean);
  const [text, setText] = useState(() => existingContract?.text || (p.contractTemplate||DEFAULT_PROFILE.contractTemplate)
    .replace('{companyName}', p.companyName||'')
    .replace('{clientName}', client?.name||pl.clientName||'')
    .replace('{clientLocation}', client?.city&&client?.state?`${client.city}, ${client.state}`:(client?.state||''))
    .replace('{candidateName}', pl.candidateName||'')
    .replace('{fee}', fmtUSD(pl.total||0))
    .replace('{paymentTerms}', p.paymentTerms||'')
    .replace('{paymentTermsNote}', p.paymentTerms||'')
    .replace('{date}', today())
  );
  const [agencySig, setAgencySig] = useState(existingContract?.agencySig || '');
  const [clientSig, setClientSig] = useState(existingContract?.clientSig || '');
  const [signed, setSigned] = useState(!!existingContract?.signedAt);
  const [signedAt, setSignedAt] = useState(existingContract?.signedAt || '');

  const doESign = async () => {
    if (!agencySig.trim() || !clientSig.trim()) { window.alert('Both signatory names are required to e-sign.'); return; }
    const ts = new Date().toISOString();
    const rec = {id: existingContract?.id || uid(), placementId: pl.id, candidateName: pl.candidateName,
      clientName: client?.name||pl.clientName, text, agencySig, clientSig, signedAt: ts};
    await DB.save('contracts', rec.id, rec);
    setSigned(true); setSignedAt(ts);
    if (onSigned) onSigned(rec);
    if (addAct) addAct(`✅ Contract e-signed for "${pl.candidateName}" → "${client?.name||pl.clientName}"`);
  };

  const letterheadHTML = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;">
      <div>
        ${p.logo?`<img src="${p.logo}" style="max-height:55px;max-width:170px;object-fit:contain;"/>`:`<div style="font-size:20px;font-weight:900;color:${bl};">${(p.companyName||'').replace(/</g,'&lt;')}</div>`}
        <div style="font-size:11px;color:#555;margin-top:6px;line-height:1.7;">
          <div>${(p.address||'').replace(/</g,'&lt;')}</div>
          <div>${p.city||''}${p.country?', '+p.country:''}</div>
          <div>${p.email||''}${p.phone?' · '+p.phone:''}${p.website?' · '+p.website:''}</div>
        </div>
      </div>
      <div style="text-align:right;font-size:24px;font-weight:900;color:${bl};letter-spacing:1px;">PLACEMENT<br/>AGREEMENT</div>
    </div>
    ${taxLines.length?`<div style="display:flex;gap:6px;margin:8px 0 14px;flex-wrap:wrap;">${taxLines.map(t=>`<div style="border:1px solid #c0c8d0;border-radius:4px;padding:2px 8px;font-size:10px;font-weight:600;color:#444;">${t}</div>`).join('')}</div>`:''}
    <div style="height:0.5px;background:#ccc;margin:4px 0 16px;"></div>
  `;
  const sigBlockHTML = signed
    ? `<div style="margin-top:60px;display:flex;gap:60px;font-size:12px;"><div style="flex:1;"><div style="font-family:'Brush Script MT',cursive;font-size:22px;border-bottom:1px solid #999;padding-bottom:4px;">${agencySig.replace(/</g,'&lt;')}</div><div style="padding-top:4px;color:#666;">Authorized signature — ${(p.companyName||'').replace(/</g,'&lt;')}<br/>E-signed ${new Date(signedAt).toLocaleString()}</div></div><div style="flex:1;"><div style="font-family:'Brush Script MT',cursive;font-size:22px;border-bottom:1px solid #999;padding-bottom:4px;">${clientSig.replace(/</g,'&lt;')}</div><div style="padding-top:4px;color:#666;">Authorized signature — ${(client?.name||pl.clientName||'').replace(/</g,'&lt;')}<br/>E-signed ${new Date(signedAt).toLocaleString()}</div></div></div>`
    : `<div style="margin-top:60px;display:flex;gap:60px;font-size:12px;"><div style="flex:1;"><div style="border-top:1px solid #999;padding-top:6px;">Authorized signature — ${(p.companyName||'').replace(/</g,'&lt;')}</div></div><div style="flex:1;"><div style="border-top:1px solid #999;padding-top:6px;">Authorized signature — ${(client?.name||pl.clientName||'').replace(/</g,'&lt;')}</div></div></div>`;
  const doPrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Placement Agreement — ${pl.invNum||''}</title></head><body style="font-family:'Helvetica Neue',Arial,sans-serif;padding:40px;font-size:14px;color:#222;">${letterheadHTML}<div style="font-family:Georgia,serif;white-space:pre-wrap;line-height:1.7;">${text.replace(/</g,'&lt;')}</div>${sigBlockHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <Modal onClose={onClose} maxWidth={680}>
      <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>📜 Placement contract</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>{pl.candidateName} → {client?.name||pl.clientName}. Edit the body below before printing — the letterhead pulls from My Business automatically.</div>
      <div style={{fontFamily:"'Helvetica Neue',Arial,sans-serif",fontSize:12,border:`0.5px solid ${br}`,borderRadius:8,padding:'14px 16px',marginBottom:12}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            {p.logo?<img src={p.logo} alt="logo" style={{maxHeight:44,maxWidth:140,objectFit:'contain'}}/>:<div style={{fontSize:16,fontWeight:900,color:bl}}>{p.companyName}</div>}
            <div style={{fontSize:10.5,color:'#555',marginTop:5,lineHeight:1.7}}>
              {p.address&&<div>{p.address}</div>}
              <div>{p.city}{p.country?', '+p.country:''}</div>
              <div>{p.email}{p.phone?' · '+p.phone:''}</div>
            </div>
          </div>
          <div style={{textAlign:'right',fontSize:16,fontWeight:900,color:bl,letterSpacing:1}}>PLACEMENT<br/>AGREEMENT</div>
        </div>
        {taxLines.length>0 && <div style={{display:'flex',gap:6,margin:'10px 0 0',flexWrap:'wrap'}}>{taxLines.map((t,i)=><div key={i} style={{border:'1px solid #c0c8d0',borderRadius:4,padding:'2px 8px',fontSize:10,fontWeight:600,color:'#444'}}>{t}</div>)}</div>}
      </div>
      <TA value={text} onChange={e=>{setText(e.target.value); setSigned(false);}} style={{minHeight:230,fontFamily:'Georgia,serif',fontSize:13}} disabled={signed}/>
      <SecLabel>✍️ E-signature</SecLabel>
      {signed ? (
        <div style={{background:'#EAF3DE',border:'0.5px solid #cde3b0',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#3B6D11'}}>
          ✓ Signed by <strong>{agencySig}</strong> and <strong>{clientSig}</strong> on {new Date(signedAt).toLocaleString()}
        </div>
      ) : (
        <div style={S.fg2}>
          <FG label="Agency signatory (typed name)"><Inp value={agencySig} onChange={e=>setAgencySig(e.target.value)} placeholder={p.companyName}/></FG>
          <FG label="Client signatory (typed name)"><Inp value={clientSig} onChange={e=>setClientSig(e.target.value)} placeholder={client?.contact||client?.name||''}/></FG>
        </div>
      )}
      <div style={{display:'flex',gap:8,marginTop:14,flexWrap:'wrap'}}>
        {!signed && <Btn prim onClick={doESign}>✍️ E-Sign & Save</Btn>}
        <Btn onClick={doPrint}>🖨 Print / Save as PDF</Btn>
        <Btn onClick={()=>navigator.clipboard?.writeText(text)}>📋 Copy text</Btn>
        <Btn onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PARTNER / EMPLOYEE REFERRAL AGREEMENT MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function PartnerAgreementModal({partner, profile, onClose, existingAgreement, onSigned, addAct, allowSign}) {
  const p = {...DEFAULT_PROFILE, ...profile};
  const taxLines = [
    p.gst?`GST: ${p.gst}`:'', p.eic?`EIC: ${p.eic}`:'',
    p.ein?`EIN: ${p.ein}`:'', p.pan?`PAN: ${p.pan}`:'',
  ].filter(Boolean);
  const feeDescription = partner.feeType==='flat'
    ? `${fmtUSD(partner.feeAmount||0)} per successful placement`
    : `${partner.feePercent||0}% of the placement fee`;
  const [text, setText] = useState(() => existingAgreement?.text || (p.partnerContractTemplate||DEFAULT_PROFILE.partnerContractTemplate)
    .replace('{companyName}', p.companyName||'')
    .replace('{partnerName}', partner.name||'')
    .replace('{partnerAddress}', partner.address||'—')
    .replace('{feeDescription}', feeDescription)
    .replace('{paymentTerms}', '30 days')
    .replace('{date}', today())
  );
  const [agencySig, setAgencySig] = useState(existingAgreement?.agencySig || '');
  const [partnerSig, setPartnerSig] = useState(existingAgreement?.partnerSig || '');
  const [signed, setSigned] = useState(!!existingAgreement?.signedAt);
  const [signedAt, setSignedAt] = useState(existingAgreement?.signedAt || '');

  const doESign = async () => {
    if (!agencySig.trim() || !partnerSig.trim()) { window.alert('Both signatory names are required to e-sign.'); return; }
    const ts = new Date().toISOString();
    const rec = {id: existingAgreement?.id || uid(), partnerId: partner.id, partnerName: partner.name,
      text, agencySig, partnerSig, signedAt: ts};
    await DB.save('partnerAgreements', rec.id, rec);
    setSigned(true); setSignedAt(ts);
    if (onSigned) onSigned(rec);
    if (addAct) addAct(`✅ Referral agreement e-signed with "${partner.name}"`);
  };

  const letterheadHTML = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;">
      <div>
        ${p.logo?`<img src="${p.logo}" style="max-height:55px;max-width:170px;object-fit:contain;"/>`:`<div style="font-size:20px;font-weight:900;color:${bl};">${(p.companyName||'').replace(/</g,'&lt;')}</div>`}
        <div style="font-size:11px;color:#555;margin-top:6px;line-height:1.7;">
          <div>${(p.address||'').replace(/</g,'&lt;')}</div>
          <div>${p.city||''}${p.country?', '+p.country:''}</div>
          <div>${p.email||''}${p.phone?' · '+p.phone:''}${p.website?' · '+p.website:''}</div>
        </div>
      </div>
      <div style="text-align:right;font-size:24px;font-weight:900;color:${bl};letter-spacing:1px;">REFERRAL<br/>AGREEMENT</div>
    </div>
    ${taxLines.length?`<div style="display:flex;gap:6px;margin:8px 0 14px;flex-wrap:wrap;">${taxLines.map(t=>`<div style="border:1px solid #c0c8d0;border-radius:4px;padding:2px 8px;font-size:10px;font-weight:600;color:#444;">${t}</div>`).join('')}</div>`:''}
    <div style="height:0.5px;background:#ccc;margin:4px 0 16px;"></div>
  `;
  const sigBlockHTML = signed
    ? `<div style="margin-top:60px;display:flex;gap:60px;font-size:12px;"><div style="flex:1;"><div style="font-family:'Brush Script MT',cursive;font-size:22px;border-bottom:1px solid #999;padding-bottom:4px;">${agencySig.replace(/</g,'&lt;')}</div><div style="padding-top:4px;color:#666;">Authorized signature — ${(p.companyName||'').replace(/</g,'&lt;')}<br/>E-signed ${new Date(signedAt).toLocaleString()}</div></div><div style="flex:1;"><div style="font-family:'Brush Script MT',cursive;font-size:22px;border-bottom:1px solid #999;padding-bottom:4px;">${partnerSig.replace(/</g,'&lt;')}</div><div style="padding-top:4px;color:#666;">Authorized signature — ${(partner.name||'').replace(/</g,'&lt;')}<br/>E-signed ${new Date(signedAt).toLocaleString()}</div></div></div>`
    : `<div style="margin-top:60px;display:flex;gap:60px;font-size:12px;"><div style="flex:1;"><div style="border-top:1px solid #999;padding-top:6px;">Authorized signature — ${(p.companyName||'').replace(/</g,'&lt;')}</div></div><div style="flex:1;"><div style="border-top:1px solid #999;padding-top:6px;">Authorized signature — ${(partner.name||'').replace(/</g,'&lt;')}</div></div></div>`;
  const doPrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Referral Agreement — ${partner.name||''}</title></head><body style="font-family:'Helvetica Neue',Arial,sans-serif;padding:40px;font-size:14px;color:#222;">${letterheadHTML}<div style="font-family:Georgia,serif;white-space:pre-wrap;line-height:1.7;">${text.replace(/</g,'&lt;')}</div>${sigBlockHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <Modal onClose={onClose} maxWidth={680}>
      <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>📜 Referral agreement</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>With {partner.name} ({partner.refType==='employee'?'employee incentive':'external partner'}). Edit the body below before printing.</div>
      <div style={{fontFamily:"'Helvetica Neue',Arial,sans-serif",fontSize:12,border:`0.5px solid ${br}`,borderRadius:8,padding:'14px 16px',marginBottom:12}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            {p.logo?<img src={p.logo} alt="logo" style={{maxHeight:44,maxWidth:140,objectFit:'contain'}}/>:<div style={{fontSize:16,fontWeight:900,color:bl}}>{p.companyName}</div>}
            <div style={{fontSize:10.5,color:'#555',marginTop:5,lineHeight:1.7}}>
              {p.address&&<div>{p.address}</div>}
              <div>{p.city}{p.country?', '+p.country:''}</div>
              <div>{p.email}{p.phone?' · '+p.phone:''}</div>
            </div>
          </div>
          <div style={{textAlign:'right',fontSize:16,fontWeight:900,color:bl,letterSpacing:1}}>REFERRAL<br/>AGREEMENT</div>
        </div>
        {taxLines.length>0 && <div style={{display:'flex',gap:6,margin:'10px 0 0',flexWrap:'wrap'}}>{taxLines.map((t,i)=><div key={i} style={{border:'1px solid #c0c8d0',borderRadius:4,padding:'2px 8px',fontSize:10,fontWeight:600,color:'#444'}}>{t}</div>)}</div>}
      </div>
      <TA value={text} onChange={e=>{setText(e.target.value); setSigned(false);}} style={{minHeight:200,fontFamily:'Georgia,serif',fontSize:13}} disabled={signed}/>
      <SecLabel>✍️ E-signature</SecLabel>
      {signed ? (
        <div style={{background:'#EAF3DE',border:'0.5px solid #cde3b0',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#3B6D11'}}>
          ✓ Signed by <strong>{agencySig}</strong> and <strong>{partnerSig}</strong> on {new Date(signedAt).toLocaleString()}
        </div>
      ) : (
        allowSign===false ? (
          <div style={{fontSize:12,color:'#888'}}>Only an admin can e-sign this agreement on behalf of the company. You can still print or copy it to send.</div>
        ) : (
          <div style={S.fg2}>
            <FG label="Agency signatory (typed name)"><Inp value={agencySig} onChange={e=>setAgencySig(e.target.value)} placeholder={p.companyName}/></FG>
            <FG label={`${partner.refType==='employee'?'Employee':'Partner'} signatory (typed name)`}><Inp value={partnerSig} onChange={e=>setPartnerSig(e.target.value)} placeholder={partner.contact||partner.name||''}/></FG>
          </div>
        )
      )}
      <div style={{display:'flex',gap:8,marginTop:14,flexWrap:'wrap'}}>
        {!signed && allowSign!==false && <Btn prim onClick={doESign}>✍️ E-Sign & Save</Btn>}
        <Btn onClick={doPrint}>🖨 Print / Save as PDF</Btn>
        <Btn onClick={()=>navigator.clipboard?.writeText(text)}>📋 Copy text</Btn>
        <Btn onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLIENT SERVICE AGREEMENT MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function fillClientContract(profile, client) {
  const p = {...DEFAULT_PROFILE, ...profile};
  const feeDescription = `${fmtUSD(client.placementFee||0)} per worker placed${parseFloat(client.discPerUnit||0)>0?` (less ${fmtUSD(client.discPerUnit)}/unit discount)`:''}`;
  const map = { companyName:p.companyName||'', clientName:client.contact||client.name||'', clientBusiness:client.name||'',
    clientLocation: client.city&&client.state?`${client.city}, ${client.state}`:(client.state||client.city||''),
    feeDescription, paymentTerms:p.paymentTerms||'', date:today() };
  const tmpl = p.clientContractTemplate || DEFAULT_PROFILE.clientContractTemplate;
  return Object.keys(map).reduce((out,k)=>out.split('{'+k+'}').join(map[k]), tmpl);
}
function ClientAgreementModal({client, profile, onClose, existingAgreement, onSigned, addAct}) {
  const p = {...DEFAULT_PROFILE, ...profile};
  const taxLines = [
    p.gst?`GST: ${p.gst}`:'', p.eic?`EIC: ${p.eic}`:'',
    p.ein?`EIN: ${p.ein}`:'', p.pan?`PAN: ${p.pan}`:'',
  ].filter(Boolean);
  const feeDescription = `${fmtUSD(client.placementFee||0)} per worker placed${parseFloat(client.discPerUnit||0)>0?` (less ${fmtUSD(client.discPerUnit)}/unit discount)`:''}`;
  const [text, setText] = useState(() => (existingAgreement?.signedAt && existingAgreement?.text) ? existingAgreement.text : fillClientContract(profile, client));
  const [agencySig, setAgencySig] = useState(existingAgreement?.agencySig || '');
  const [clientSig, setClientSig] = useState(existingAgreement?.clientSig || '');
  const [signed, setSigned] = useState(!!existingAgreement?.signedAt);
  const [signedAt, setSignedAt] = useState(existingAgreement?.signedAt || '');

  const doESign = async () => {
    if (!agencySig.trim() || !clientSig.trim()) { window.alert('Both signatory names are required to e-sign.'); return; }
    const ts = new Date().toISOString();
    const rec = {id: client.id, clientId: client.id, clientName: client.name,
      text, agencySig, clientSig, signedAt: ts};
    await DB.save('clientAgreements', rec.id, rec);
    setSigned(true); setSignedAt(ts);
    if (onSigned) onSigned(rec);
    if (addAct) addAct(`✅ Service agreement e-signed with "${client.name}"`);
  };

  const copySignLink = async () => {
    const base = p.publicUrl ? p.publicUrl.replace(/\/$/,'') : ((typeof window!=='undefined' && window.location.origin && !window.location.origin.startsWith('file:')) ? window.location.origin : '');
    if (!base) { window.alert('Set your Public web app URL in My Business first.'); return; }
    if (!signed) {
      const rec = {id: client.id, clientId: client.id, clientName: client.name, text, agencySig: agencySig||p.companyName||'', clientSig:'', signedAt:'', sentAt:new Date().toISOString()};
      await DB.save('clientAgreements', rec.id, rec);
      if (onSigned) onSigned(rec);
    }
    const url = `${base}/sign-contract/${client.id}`;
    try { await navigator.clipboard.writeText(url); } catch (e) {}
    window.alert(signed ? 'Already signed. Link copied anyway:\n'+url : 'Link copied — send it to the client:\n'+url);
  };
  const sendToPortal = async () => {
    const rec = {id: client.id, clientId: client.id, clientName: client.name, text, agencySig: agencySig||p.companyName||'', clientSig:'', signedAt:'', sentAt:new Date().toISOString()};
    await DB.save('clientAgreements', rec.id, rec);
    setSigned(false); setSignedAt(''); setClientSig('');
    if (onSigned) onSigned(rec);
    if (addAct) addAct(`📤 Service agreement sent to "${client.name}" for e-signature`);
    window.alert('Sent. The client will see it in their portal and can sign it there.');
  };
  const letterheadHTML = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;">
      <div>
        ${p.logo?`<img src="${p.logo}" style="max-height:55px;max-width:170px;object-fit:contain;"/>`:`<div style="font-size:20px;font-weight:900;color:${bl};">${(p.companyName||'').replace(/</g,'&lt;')}</div>`}
        <div style="font-size:11px;color:#555;margin-top:6px;line-height:1.7;">
          <div>${(p.address||'').replace(/</g,'&lt;')}</div>
          <div>${p.city||''}${p.country?', '+p.country:''}</div>
          <div>${p.email||''}${p.phone?' · '+p.phone:''}${p.website?' · '+p.website:''}</div>
        </div>
      </div>
      <div style="text-align:right;font-size:24px;font-weight:900;color:${bl};letter-spacing:1px;">SERVICE<br/>AGREEMENT</div>
    </div>
    ${taxLines.length?`<div style="display:flex;gap:6px;margin:8px 0 14px;flex-wrap:wrap;">${taxLines.map(t=>`<div style="border:1px solid #c0c8d0;border-radius:4px;padding:2px 8px;font-size:10px;font-weight:600;color:#444;">${t}</div>`).join('')}</div>`:''}
    <div style="height:0.5px;background:#ccc;margin:4px 0 16px;"></div>
  `;
  const sigBlockHTML = signed
    ? `<div style="margin-top:60px;display:flex;gap:60px;font-size:12px;"><div style="flex:1;"><div style="font-family:'Brush Script MT',cursive;font-size:22px;border-bottom:1px solid #999;padding-bottom:4px;">${agencySig.replace(/</g,'&lt;')}</div><div style="padding-top:4px;color:#666;">Authorized signature — ${(p.companyName||'').replace(/</g,'&lt;')}<br/>E-signed ${new Date(signedAt).toLocaleString()}</div></div><div style="flex:1;"><div style="font-family:'Brush Script MT',cursive;font-size:22px;border-bottom:1px solid #999;padding-bottom:4px;">${clientSig.replace(/</g,'&lt;')}</div><div style="padding-top:4px;color:#666;">Authorized signature — ${(client.name||'').replace(/</g,'&lt;')}<br/>E-signed ${new Date(signedAt).toLocaleString()}</div></div></div>`
    : `<div style="margin-top:60px;display:flex;gap:60px;font-size:12px;"><div style="flex:1;"><div style="border-top:1px solid #999;padding-top:6px;">Authorized signature — ${(p.companyName||'').replace(/</g,'&lt;')}</div></div><div style="flex:1;"><div style="border-top:1px solid #999;padding-top:6px;">Authorized signature — ${(client.name||'').replace(/</g,'&lt;')}</div></div></div>`;
  const doPrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Service Agreement — ${client.name||''}</title></head><body style="font-family:'Helvetica Neue',Arial,sans-serif;padding:40px;font-size:14px;color:#222;">${letterheadHTML}<div style="font-family:Georgia,serif;white-space:pre-wrap;line-height:1.7;">${text.replace(/</g,'&lt;')}</div>${sigBlockHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <Modal onClose={onClose} maxWidth={680}>
      <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>📜 Client service agreement</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>With {client.name}. Edit the body below before printing — the letterhead pulls from My Business automatically.</div>
      <div style={{fontFamily:"'Helvetica Neue',Arial,sans-serif",fontSize:12,border:`0.5px solid ${br}`,borderRadius:8,padding:'14px 16px',marginBottom:12}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            {p.logo?<img src={p.logo} alt="logo" style={{maxHeight:44,maxWidth:140,objectFit:'contain'}}/>:<div style={{fontSize:16,fontWeight:900,color:bl}}>{p.companyName}</div>}
            <div style={{fontSize:10.5,color:'#555',marginTop:5,lineHeight:1.7}}>
              {p.address&&<div>{p.address}</div>}
              <div>{p.city}{p.country?', '+p.country:''}</div>
              <div>{p.email}{p.phone?' · '+p.phone:''}</div>
            </div>
          </div>
          <div style={{textAlign:'right',fontSize:16,fontWeight:900,color:bl,letterSpacing:1}}>SERVICE<br/>AGREEMENT</div>
        </div>
        {taxLines.length>0 && <div style={{display:'flex',gap:6,margin:'10px 0 0',flexWrap:'wrap'}}>{taxLines.map((t,i)=><div key={i} style={{border:'1px solid #c0c8d0',borderRadius:4,padding:'2px 8px',fontSize:10,fontWeight:600,color:'#444'}}>{t}</div>)}</div>}
      </div>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
        <Btn sm onClick={()=>{ if (signed && !window.confirm('This agreement is already signed. Re-issuing from the current template clears the signatures so the client must sign again. Continue?')) return;
          setText(fillClientContract(profile, client)); setSigned(false); setSignedAt(''); setClientSig(''); }}>↻ Reload from My Business template</Btn>
        <Btn sm onClick={sendToPortal}>📤 Send to client portal for e-sign</Btn>
        <Btn sm onClick={copySignLink}>🔗 Copy e-sign link (no login)</Btn>
      </div>
      <TA value={text} onChange={e=>{setText(e.target.value); setSigned(false);}} style={{minHeight:200,fontFamily:'Georgia,serif',fontSize:13}} disabled={signed}/>
      <SecLabel>✍️ E-signature</SecLabel>
      {signed ? (
        <div style={{background:'#EAF3DE',border:'0.5px solid #cde3b0',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#3B6D11'}}>
          ✓ Signed by <strong>{agencySig}</strong> and <strong>{clientSig}</strong> on {new Date(signedAt).toLocaleString()}
        </div>
      ) : (
        <div style={S.fg2}>
          <FG label="Agency signatory (typed name)"><Inp value={agencySig} onChange={e=>setAgencySig(e.target.value)} placeholder={p.companyName}/></FG>
          <FG label="Client signatory (typed name)"><Inp value={clientSig} onChange={e=>setClientSig(e.target.value)} placeholder={client.contact||client.name||''}/></FG>
        </div>
      )}
      <div style={{display:'flex',gap:8,marginTop:14,flexWrap:'wrap'}}>
        {!signed && <Btn prim onClick={doESign}>✍️ E-Sign & Save</Btn>}
        <Btn onClick={doPrint}>🖨 Print / Save as PDF</Btn>
        <Btn onClick={()=>navigator.clipboard?.writeText(text)}>📋 Copy text</Btn>
        <Btn onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// REFERRAL PAYOUT RECEIPT MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function PayoutReceiptModal({payable, profile, onClose}) {
  const p = {...DEFAULT_PROFILE, ...profile};
  const receiptNo = 'PAY-' + payable.id.slice(-8).toUpperCase();
  const letterheadHTML = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;">
      <div>
        ${p.logo?`<img src="${p.logo}" style="max-height:55px;max-width:170px;object-fit:contain;"/>`:`<div style="font-size:20px;font-weight:900;color:${bl};">${(p.companyName||'').replace(/</g,'&lt;')}</div>`}
        <div style="font-size:11px;color:#555;margin-top:6px;line-height:1.7;">
          <div>${(p.address||'').replace(/</g,'&lt;')}</div>
          <div>${p.city||''}${p.country?', '+p.country:''}</div>
          <div>${p.email||''}${p.phone?' · '+p.phone:''}</div>
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:22px;font-weight:900;color:${bl};letter-spacing:1px;">PAYOUT RECEIPT</div>
        <div style="font-size:12px;color:#666;margin-top:4px;">${receiptNo}</div>
      </div>
    </div>
    <div style="height:0.5px;background:#ccc;margin:4px 0 16px;"></div>
  `;
  const bodyHTML = `
    <div style="margin-bottom:16px;font-size:13px;">
      <div><strong>Paid to:</strong> ${(payable.partnerName||'').replace(/</g,'&lt;')}</div>
      <div><strong>Date:</strong> ${payable.status==='Paid' ? payable.paidDate : today()}</div>
      <div><strong>Reason:</strong> Referral ${payable.status==='Paid'?'payment':'amount owed'} for candidate placement</div>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px;">
      <thead><tr style="background:#f4f4f0;"><th style="text-align:left;padding:8px;border:1px solid #ddd;">Description</th><th style="text-align:right;padding:8px;border:1px solid #ddd;">Amount</th></tr></thead>
      <tbody><tr><td style="padding:8px;border:1px solid #ddd;">Referral fee — ${(payable.candidateName||'').replace(/</g,'&lt;')} placed at ${(payable.clientName||'').replace(/</g,'&lt;')} (placed ${payable.placedDate})</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${fmtUSD(payable.amount||0)}</td></tr></tbody>
      <tfoot><tr><td style="padding:8px;border:1px solid #ddd;font-weight:700;text-align:right;">Total</td><td style="text-align:right;padding:8px;border:1px solid #ddd;font-weight:700;">${fmtUSD(payable.amount||0)}</td></tr></tfoot>
    </table>
    <div style="font-size:12px;color:#888;">${payable.status==='Paid' ? 'This confirms the above amount has been paid.' : 'This confirms the above amount is confirmed owed, pending payment.'}</div>
  `;
  const doPrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Payout Receipt — ${receiptNo}</title></head><body style="font-family:'Helvetica Neue',Arial,sans-serif;padding:40px;font-size:14px;color:#222;">${letterheadHTML}${bodyHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <Modal onClose={onClose} maxWidth={560}>
      <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>🧾 Payout receipt</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>{receiptNo} · {payable.partnerName}</div>
      <div style={{fontFamily:"'Helvetica Neue',Arial,sans-serif",fontSize:12,border:`0.5px solid ${br}`,borderRadius:8,padding:'14px 16px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
          <div>
            {p.logo?<img src={p.logo} alt="logo" style={{maxHeight:40,maxWidth:130,objectFit:'contain'}}/>:<div style={{fontSize:15,fontWeight:900,color:bl}}>{p.companyName}</div>}
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:14,fontWeight:900,color:bl}}>PAYOUT RECEIPT</div>
            <div style={{fontSize:11,color:'#666'}}>{receiptNo}</div>
          </div>
        </div>
        <div style={{fontSize:13,marginBottom:8}}><strong>Paid to:</strong> {payable.partnerName}</div>
        <div style={{fontSize:13,marginBottom:8}}><strong>Date:</strong> {payable.status==='Paid'?payable.paidDate:today()}</div>
        <div style={{fontSize:13,borderTop:`0.5px solid ${br}`,paddingTop:8}}>Referral fee — {payable.candidateName} placed at {payable.clientName}</div>
        <div style={{fontSize:16,fontWeight:800,color:gr,marginTop:8,textAlign:'right'}}>{fmtUSD(payable.amount||0)}</div>
      </div>
      <div style={{display:'flex',gap:8,marginTop:14}}>
        <Btn prim onClick={doPrint}>🖨 Print / Save as PDF</Btn>
        <Btn onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const CBLANK={name:'',phone:'',email:'',age:'',visa:'',visaOther:'',industry:'',skills:[],experience:'',availability:'Immediate',hasCar:'No',originCountry:'',currentCity:'',currentState:'',yearsInUSA:'',members:'1',photo:'',notes:'',stage:'New',followUpDate:'',followUpNote:'',referralPartnerId:'',referralPartnerName:'',addedBy:'',addedByName:''};
const CLBLANK={name:'',contact:'',phone:'',email:'',industry:'',city:'',state:'',openings:'1',payType:'hourly',wage:'',fixedPay:'',fixedPeriod:'Monthly',skills:'',carNeeded:'No',legalOnly:'No',cashPay:'No',accommodation:'No',placementFee:'500',discPerUnit:'0',notes:'',followUpDate:'',followUpNote:'',placementIds:[],priority:'',hours:'',shift:'',jobDesc:''};
const RPBLANK={name:'',contact:'',phone:'',email:'',address:'',refType:'partner',feeType:'flat',feeAmount:'100',feePercent:'10',feeRules:[],notes:''};

function LoginPage({onLogin, profile}) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const isLocal = !FIREBASE_ENABLED;
  // `profile` from the parent is only populated after a successful login (it
  // comes from an authenticated listener), so on a fresh visit to this screen
  // it's still the bare default. Fetch it publicly too so the real logo shows
  // immediately, before anyone has signed in.
  const publicProfile = usePublicProfile();
  const displayProfile = (profile && profile.logo) ? profile : (publicProfile || profile);

  const doLogin = async (e) => {
    e.preventDefault();
    if (!email || !pass) { setErr('Enter email and password.'); return; }
    setLoading(true); setErr('');
    try {
      if (isLocal) {
        const users = DB.getLocalUsers();
        const found = users.find(u => u.email === email && u.password === pass);
        if (!found) {
          if (email === 'admin@three9.com' && pass === 'admin123') {
            onLogin({uid:'local-admin',email,role:'admin',name:'Admin'});
          } else { setErr('Incorrect email or password.'); }
          setLoading(false); return;
        }
        onLogin({uid:found.uid,email:found.email,role:found.role,name:found.name});
      } else {
        // Don't build the user object here — that duplicated the role/clientId
        // lookup the auth listener in App() already does correctly, and the two
        // could disagree (this copy was missing clientId, which broke client
        // logins on a fresh sign-in). Just sign in; the listener picks it up.
        await loginFirebase(email, pass);
      }
    } catch(e) {
      const code = e.code||'';
      setErr(code.includes('invalid-credential')||code.includes('wrong-password') ? 'Incorrect email or password.'
        : code.includes('user-not-found') ? 'No account with this email.'
        : code.includes('too-many') ? 'Too many attempts. Try later.'
        : 'Login failed: '+e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#0f2c4a 0%,#185FA5 60%,#1a7ab8 100%)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{width:'100%',maxWidth:400}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          {displayProfile?.logo
            ? <img src={displayProfile.logo} alt="logo" style={{height:60,objectFit:'contain',marginBottom:10}}/>
            : <div style={{width:70,height:70,background:'rgba(255,255,255,.15)',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 10px',fontSize:26,fontWeight:900,color:'#fff'}}>{(displayProfile?.companyName||'').split(' ').map(w=>w[0]).filter(Boolean).join('').toUpperCase().slice(0,3)||'CO'}</div>
          }
          <div style={{fontSize:22,fontWeight:800,color:'#fff',letterSpacing:'-0.5px'}}>{displayProfile?.companyName||'Three9 Services'}</div>
          <div style={{fontSize:13,color:'rgba(255,255,255,.6)',marginTop:3}}>Recruiting CRM</div>
        </div>
        <div style={{background:'#fff',borderRadius:16,padding:28,boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
          <div style={{fontSize:16,fontWeight:700,marginBottom:18}}>Sign in</div>
          {isLocal && (
            <div style={{background:'#EAF3DE',border:'0.5px solid #b7dca0',borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:12,color:'#27500A'}}>
              <strong>Local mode</strong> — Default: <strong>admin@three9.com</strong> / <strong>admin123</strong>
            </div>
          )}
          <form onSubmit={doLogin}>
            <FG label="Email" style={{marginBottom:12}}>
              <Inp type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" style={{fontSize:14,padding:'9px 11px'}} autoFocus/>
            </FG>
            <FG label="Password" style={{marginBottom:18}}>
              <Inp type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" style={{fontSize:14,padding:'9px 11px'}}/>
            </FG>
            {err && <div style={{background:'#FCEBEB',borderRadius:8,padding:'8px 12px',fontSize:13,color:'#A32D2D',marginBottom:14}}>⚠ {err}</div>}
            <button type="submit" disabled={loading} style={{width:'100%',padding:10,borderRadius:8,border:'none',background:loading?'#aaa':bl,color:'#fff',fontSize:14,fontWeight:600,cursor:loading?'not-allowed':'pointer'}}>
              {loading ? 'Signing in...' : 'Sign in →'}
            </button>
          </form>
          <div style={{marginTop:14,fontSize:11,color:'#bbb',textAlign:'center'}}>Contact your administrator for an account</div>
        </div>
        <div style={{marginTop:12,background:'rgba(255,255,255,.1)',borderRadius:10,padding:'8px 14px',fontSize:11,color:'rgba(255,255,255,.65)',textAlign:'center'}}>
          {isLocal ? '💾 Local mode — configure Firebase for cloud sync' : '🔒 Secured with Firebase'}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLIENT APP VIEW — what a client-role login sees after signing in.
// Deliberately does NOT use the staff realtime listeners (which need broad
// `list` access) — it only ever fetches its own client doc plus the specific
// candidates/placements that doc points to, matching the same safe, scoped
// pattern as the public client portal, just now with write actions enabled.
// ═══════════════════════════════════════════════════════════════════════════════
function ClientAppView({user, onLogout, profile}) {
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [interviewing, setInterviewing] = useState([]); // [{cand}]
  const [placementRows, setPlacementRows] = useState([]); // [{pl, cand}]
  const [agreement, setAgreement] = useState(null);
  const [toastMsg, setToastMsg] = useState('');
  const [busyId, setBusyId] = useState('');

  const toast = useCallback(msg => { setToastMsg(msg); setTimeout(()=>setToastMsg(''), 3000); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const cl = await DB.getOne('clients', user.clientId);
    setClient(cl);
    if (cl) {
      const ivIds = cl.interviewingCandidateIds || [];
      const ivRows = [];
      for (const id of ivIds) { const c = await DB.getOne('candidates', id); if (c) ivRows.push({cand:c}); }
      setInterviewing(ivRows);

      const plIds = cl.placementIds || [];
      const plRows = [];
      for (const pid of plIds) {
        const pl = await DB.getOne('placements', pid);
        if (!pl) continue;
        const cand = pl.candidateId ? await DB.getOne('candidates', pl.candidateId) : null;
        plRows.push({pl, cand});
      }
      plRows.sort((a,b)=>(b.pl.date||'').localeCompare(a.pl.date||''));
      setPlacementRows(plRows);

      const agr = await DB.getOne('clientAgreements', user.clientId);
      if (agr) setAgreement(agr);
    }
    setLoading(false);
  }, [user.clientId]);

  useEffect(() => { load(); }, [load]);

  const clientAddAct = useCallback((text) => { logActivity({text, user: `${client?.name||'Client'} (portal)`}).catch(()=>{}); }, [client]);

  const [showContract, setShowContract] = useState(false);
  const [signName, setSignName] = useState('');
  const [signAgree, setSignAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  // Signed agreements keep their signed text; unsigned ones always follow the current My Business template.
  const contractText = !client ? '' : ((agreement?.signedAt && agreement?.text) ? agreement.text : (agreement?.text && agreement?.sentAt ? agreement.text : fillClientContract(profile, client)));
  const signContract = async () => {
    if (!signName.trim()) { toast('Please type your full name.'); return; }
    if (!signAgree) { toast('Please tick the box to agree.'); return; }
    setSigning(true);
    try {
      const rec = {id: client.id, clientId: client.id, clientName: client.name, text: contractText,
        agencySig: agreement?.agencySig || profile?.companyName || DEFAULT_PROFILE.companyName,
        clientSig: signName.trim(), signedAt: new Date().toISOString(), signedVia: 'client-portal'};
      await DB.save('clientAgreements', rec.id, rec);
      setAgreement(rec);
      clientAddAct(`✅ ${client.name} e-signed the service agreement in their portal`);
      toast('Agreement signed. Thank you!');
    } catch (e) { toast('Could not save your signature. Please try again.'); }
    setSigning(false);
  };
  const printContract = () => {
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(`<html><head><title>Service Agreement</title></head><body style="font-family:Georgia,serif;padding:40px;line-height:1.7;font-size:14px;"><div style="white-space:pre-wrap;">${(contractText||'').replace(/</g,'&lt;')}</div>${agreement?.signedAt?`<p style="margin-top:40px;">Signed by <b>${(agreement.clientSig||'').replace(/</g,'&lt;')}</b> on ${new Date(agreement.signedAt).toLocaleString()}</p>`:''}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  const markPlaced = useCallback(async (cand) => {
    if (!client) return;
    setBusyId(cand.id);
    await createPlacement({cand, client, placements:[], setPlacements:null,
      setCandidates: () => {}, // no local candidates list held here — load() below refreshes from source of truth
      setClients: (fn) => { const updated = fn([client])[0]; setClient(updated); },
      referralPartners:[], setReferralPayables:null, addAct:clientAddAct, toast});
    await load();
    setBusyId('');
  }, [client, toast, load, clientAddAct]);

  const markRejected = useCallback(async (cand) => {
    if (!client) return;
    if (!window.confirm(`Mark ${cand.name} as not moving forward?`)) return;
    setBusyId(cand.id);
    await rejectCandidateInterview({cand, client, setCandidates:()=>{}, setClients:(fn)=>{ const updated = fn([client])[0]; setClient(updated); }, addAct:clientAddAct, toast, byClient:true});
    await load();
    setBusyId('');
  }, [client, toast, load, clientAddAct]);

  if (loading) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );

  if (!client) return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:30,textAlign:'center',maxWidth:360}}>
        <div style={{fontSize:32,marginBottom:10}}>🔍</div>
        <div style={{fontSize:14,color:'#666',marginBottom:16}}>We couldn't load your account. Please contact {profile?.companyName||DEFAULT_PROFILE.companyName}.</div>
        <Btn sm onClick={onLogout}>Sign out</Btn>
      </div>
    </div>
  );

  const totalBilled = placementRows.reduce((a,{pl})=>a+(pl.total||0),0);
  const totalPaid = placementRows.reduce((a,{pl})=>a+parseFloat(pl.alreadyPaid||0),0);
  const totalDue = totalBilled - totalPaid;

  return (
    <div style={{minHeight:'100vh',background:'#f0efe9'}}>
      <div style={{background:'#fff',borderBottom:`0.5px solid ${br}`,paddingTop:'env(safe-area-inset-top)'}}>
        <div style={{padding:'0 20px',height:52,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontWeight:700,fontSize:14}}>{client.name}</div>
          <Btn sm onClick={onLogout}>Sign out</Btn>
        </div>
      </div>
      <div style={{maxWidth:640,margin:'0 auto',padding:20}}>
        <div style={{fontSize:12,color:'#888',marginBottom:16}}>Your workers & billing with {profile?.companyName||DEFAULT_PROFILE.companyName}</div>

        {interviewing.length>0 && (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>🗓 Ongoing interviews ({interviewing.length})</div>
            {interviewing.map(({cand}) => (
              <div key={cand.id} style={{background:'#FAEEDA',border:'0.5px solid #f0dcb0',borderRadius:12,padding:'12px 16px',marginBottom:8,display:'flex',alignItems:'center',gap:12}}>
                {cand.photo ? <img src={cand.photo} alt="" style={{width:40,height:40,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/> : <div style={{...S.av,width:40,height:40}}>{initials(cand.name)}</div>}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:13.5}}>{cand.name}</div>
                  <div style={{fontSize:11.5,color:'#7a5a10',marginTop:1}}>{cand.industry||''}{cand.experience?` · ${cand.experience}yr exp`:''}</div>
                </div>
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  <Btn sm onClick={()=>markPlaced(cand)} disabled={busyId===cand.id}>{busyId===cand.id?'...':'✅ Place'}</Btn>
                  <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>markRejected(cand)} disabled={busyId===cand.id}>❌ Reject</Btn>
                </div>
              </div>
            ))}
          </div>
        )}
        {interviewing.length===0 && (
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,padding:20,textAlign:'center',color:'#aaa',fontSize:13,marginBottom:20}}>No ongoing interviews right now.</div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px',textAlign:'center'}}>
            <div style={{fontSize:20,fontWeight:800,color:tx}}>{placementRows.length}</div>
            <div style={{fontSize:11,color:'#888'}}>Workers placed</div>
          </div>
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px',textAlign:'center'}}>
            <div style={{fontSize:20,fontWeight:800,color:tx}}>{fmtUSD(totalBilled)}</div>
            <div style={{fontSize:11,color:'#888'}}>Total billed</div>
          </div>
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px',textAlign:'center'}}>
            <div style={{fontSize:20,fontWeight:800,color:totalDue>0.01?rd:gr}}>{fmtUSD(totalDue)}</div>
            <div style={{fontSize:11,color:'#888'}}>Balance due</div>
          </div>
        </div>

        {contractText && (
          <div style={{background:agreement?.signedAt?'#fff':'#FFF8E6',border:`0.5px solid ${agreement?.signedAt?br:'#f0dcb0'}`,borderRadius:12,padding:'14px 16px',marginBottom:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <div style={{fontSize:14,fontWeight:700}}>📜 Service Agreement {agreement?.signedAt ? <span style={{fontSize:11,color:'#3B6D11',fontWeight:600}}>✓ Signed {new Date(agreement.signedAt).toLocaleDateString()}</span> : <span style={{fontSize:11,color:'#b07a10',fontWeight:600}}>• Signature needed</span>}</div>
              <div style={{display:'flex',gap:6}}>
                <Btn sm onClick={()=>setShowContract(v=>!v)}>{showContract||!agreement?.signedAt?'Hide':'View'}</Btn>
                <Btn sm onClick={printContract}>🖨 Print</Btn>
              </div>
            </div>
            {(showContract || !agreement?.signedAt) && (
              <div style={{marginTop:10}}>
                <div style={{fontFamily:'Georgia,serif',fontSize:13,whiteSpace:'pre-wrap',lineHeight:1.7,background:'#fff',border:`0.5px solid ${br}`,borderRadius:8,padding:'12px 14px',maxHeight:320,overflowY:'auto'}}>{contractText}</div>
                {agreement?.signedAt ? (
                  <div style={{fontSize:12,color:'#3B6D11',marginTop:8}}>Signed by <strong>{agreement.clientSig}</strong> on {new Date(agreement.signedAt).toLocaleString()}{agreement.agencySig?` · countersigned by ${agreement.agencySig}`:''}</div>
                ) : (
                  <div style={{marginTop:10}}>
                    <div style={{fontSize:12,color:'#666',marginBottom:6}}>To accept this agreement, type your full legal name. This counts as your electronic signature.</div>
                    <Inp value={signName} onChange={e=>setSignName(e.target.value)} placeholder="Your full name"/>
                    <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:12,color:'#444',margin:'10px 0'}}>
                      <input type="checkbox" checked={signAgree} onChange={e=>setSignAgree(e.target.checked)} style={{marginTop:2}}/>
                      <span>I have read this agreement and agree to its terms on behalf of {client.name}.</span>
                    </label>
                    <Btn prim onClick={signContract} disabled={signing}>{signing?'Signing...':'✍️ Sign agreement'}</Btn>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>Workers placed with you ({placementRows.length})</div>
        {placementRows.length===0 && <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,padding:24,textAlign:'center',color:'#aaa',fontSize:13}}>No placements yet.</div>}
        {placementRows.map(({pl,cand}) => {
          const balance = (pl.total||0) - parseFloat(pl.alreadyPaid||0);
          return (
            <div key={pl.id} style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,padding:'12px 16px',marginBottom:8,display:'flex',alignItems:'center',gap:12}}>
              {cand?.photo ? <img src={cand.photo} alt="" style={{width:40,height:40,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/> : <div style={{...S.av,width:40,height:40}}>{initials(pl.candidateName)}</div>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:13.5}}>{pl.candidateName}</div>
                <div style={{fontSize:11.5,color:'#888',marginTop:1}}>{pl.invNum} · Placed {pl.date}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:13,fontWeight:700,color:tx}}>{fmtUSD(pl.total||0)}</div>
                <div style={{fontSize:11,color:balance>0.01?rd:gr}}>{balance>0.01?`Due ${fmtUSD(balance)}`:'Paid ✓'}</div>
              </div>
            </div>
          );
        })}
      </div>
      {toastMsg && <div style={{position:'fixed',bottom:24,right:24,background:'#1a1a18',color:'#fff',padding:'10px 16px',borderRadius:8,fontSize:13,zIndex:9999}}>{toastMsg}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC REFERRAL SUBMISSION PAGE (no login required)
// ═══════════════════════════════════════════════════════════════════════════════
function ReferralSubmitPage() {
  const params = new URLSearchParams(window.location.search);
  const pathParts = window.location.pathname.replace(/\/$/,'').split('/').filter(Boolean); // ['refer', 'abc-immigration']
  const partnerId = pathParts[1] || params.get('pid') || '';
  const partnerName = params.get('pn') ? decodeURIComponent(params.get('pn')) : '';
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({name:'',phone:'',email:'',industry:'',originCountry:'',notes:''});
  const [website, setWebsite] = useState(''); // honeypot — real users never see or fill this field
  const loadedAt = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => { if (FIREBASE_ENABLED) await initFirebase(); setReady(true); })();
  }, []);

  const setE = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));

  const submit = async () => {
    if (website) { setDone(true); return; } // honeypot tripped — silently pretend success, don't write
    if (Date.now() - loadedAt.current < 2500) { setErr('Please take a moment to review before submitting.'); return; }
    if (!form.name || !form.phone) { setErr('Name and phone are required.'); return; }
    setErr(''); setSubmitting(true);
    try {
      const rec = {id: uid(), partnerId, partnerName: partnerName||'Unknown partner',
        name: form.name, phone: form.phone, email: form.email, industry: form.industry,
        originCountry: form.originCountry, notes: form.notes,
        submittedAt: new Date().toISOString(), status: 'New'};
      await DB.save('referralSubmissions', rec.id, rec);
      setDone(true);
    } catch (e) {
      setErr('Something went wrong submitting this. Please try again or contact us directly.');
    }
    setSubmitting(false);
  };

  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}}>
      <div style={{width:'100%',maxWidth:480}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <BrandBadge profile={profile} size={52}/>
          <div style={{fontSize:19,fontWeight:800,color:tx}}>{profile?.companyName || DEFAULT_PROFILE.companyName}</div>
          <div style={{fontSize:13,color:'#888',marginTop:2}}>Refer a candidate for job placement</div>
        </div>
        <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:24,boxShadow:'0 2px 12px rgba(0,0,0,.04)'}}>
          {done ? (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>Referral submitted!</div>
              <div style={{fontSize:13,color:'#666',lineHeight:1.6}}>Thanks{partnerName?`, ${partnerName}`:''} — we've received the candidate's info and will follow up soon.</div>
              <Btn sm style={{marginTop:16}} onClick={()=>{setDone(false); setForm({name:'',phone:'',email:'',industry:'',originCountry:'',notes:''});}}>Submit another candidate</Btn>
            </div>
          ) : (
            <div>
              {partnerName && (
                <div style={{background:'#E6F1FB',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#0C447C',marginBottom:16}}>
                  Referring on behalf of: <strong>{partnerName}</strong>
                </div>
              )}
              <div style={{position:'absolute',left:'-9999px',width:1,height:1,overflow:'hidden'}} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e=>setWebsite(e.target.value)}/>
              </div>
              <FG label="Candidate full name *"><Inp value={form.name} onChange={setE('name')} placeholder="John Martinez"/></FG>
              <FG label="Phone number *" style={{marginTop:10}}><PhoneInput value={form.phone} onChange={setE('phone')} placeholder="(555) 000-0000"/></FG>
              <FG label="Email" style={{marginTop:10}}><Inp value={form.email} onChange={setE('email')} placeholder="john@email.com"/></FG>
              <div style={S.fg2}>
                <FG label="Industry" style={{marginTop:10}}><Sel value={form.industry} onChange={setE('industry')}><option value="">Select...</option>{INDUSTRIES.map(v=><option key={v}>{v}</option>)}</Sel></FG>
                <FG label="Originally from" style={{marginTop:10}}><Inp value={form.originCountry} onChange={setE('originCountry')} placeholder="India, Mexico..."/></FG>
              </div>
              <FG label="Notes" style={{marginTop:10}}><TA value={form.notes} onChange={setE('notes')} placeholder="Anything else we should know..."/></FG>
              {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginTop:12}}>⚠ {err}</div>}
              <Btn prim onClick={submit} disabled={submitting} style={{marginTop:16,width:'100%',textAlign:'center'}}>{submitting?'Submitting...':'Submit referral'}</Btn>
              {!FIREBASE_ENABLED && <div style={{fontSize:11,color:'#aaa',marginTop:10,textAlign:'center'}}>Note: cloud sync isn't configured on this deployment.</div>}
            </div>
          )}
        </div>
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC PARTNER SELF-REGISTRATION PAGE (no login required)
// ═══════════════════════════════════════════════════════════════════════════════
function PartnerSignupPage() {
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({name:'',contact:'',phone:'',email:'',address:'',notes:''});
  const [website, setWebsite] = useState(''); // honeypot — real users never see or fill this field
  const loadedAt = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => { if (FIREBASE_ENABLED) await initFirebase(); setReady(true); })();
  }, []);

  const setE = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));

  const submit = async () => {
    if (website) { setDone(true); return; } // honeypot tripped — silently pretend success, don't write
    if (Date.now() - loadedAt.current < 2500) { setErr('Please take a moment to review before submitting.'); return; }
    if (!form.name || !form.phone) { setErr('Organization/name and phone are required.'); return; }
    setErr(''); setSubmitting(true);
    try {
      const rec = {id: uid(), name: form.name, contact: form.contact, phone: form.phone,
        email: form.email, address: form.address, notes: form.notes,
        submittedAt: new Date().toISOString(), status: 'New'};
      await DB.save('partnerApplications', rec.id, rec);
      setDone(true);
    } catch (e) {
      setErr('Something went wrong submitting this. Please try again or contact us directly.');
    }
    setSubmitting(false);
  };

  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}}>
      <div style={{width:'100%',maxWidth:480}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <BrandBadge profile={profile} size={52}/>
          <div style={{fontSize:19,fontWeight:800,color:tx}}>{profile?.companyName || DEFAULT_PROFILE.companyName}</div>
          <div style={{fontSize:13,color:'#888',marginTop:2}}>Become a referral partner</div>
        </div>
        <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:24,boxShadow:'0 2px 12px rgba(0,0,0,.04)'}}>
          {done ? (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>Application submitted!</div>
              <div style={{fontSize:13,color:'#666',lineHeight:1.6}}>Thanks for your interest — our team will review your details and follow up soon with next steps.</div>
            </div>
          ) : (
            <div>
              <div style={{fontSize:12,color:'#666',marginBottom:16,lineHeight:1.6}}>Fill in your details below to register as a referral partner. Once approved, we'll send you a unique link for submitting candidates.</div>
              <div style={{position:'absolute',left:'-9999px',width:1,height:1,overflow:'hidden'}} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e=>setWebsite(e.target.value)}/>
              </div>
              <FG label="Organization / your name *"><Inp value={form.name} onChange={setE('name')} placeholder="ABC Immigration Services"/></FG>
              <FG label="Contact person" style={{marginTop:10}}><Inp value={form.contact} onChange={setE('contact')} placeholder="Full name"/></FG>
              <FG label="Phone number *" style={{marginTop:10}}><PhoneInput value={form.phone} onChange={setE('phone')} placeholder="(555) 000-0000"/></FG>
              <FG label="Email" style={{marginTop:10}}><Inp value={form.email} onChange={setE('email')} placeholder="contact@office.com"/></FG>
              <FG label="Address" style={{marginTop:10}}><Inp value={form.address} onChange={setE('address')} placeholder="Street, city, state"/></FG>
              <FG label="Notes" style={{marginTop:10}}><TA value={form.notes} onChange={setE('notes')} placeholder="Tell us about your organization, the region you serve, or any details that would help..."/></FG>
              {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginTop:12}}>⚠ {err}</div>}
              <Btn prim onClick={submit} disabled={submitting} style={{marginTop:16,width:'100%',textAlign:'center'}}>{submitting?'Submitting...':'Submit application'}</Btn>
              {!FIREBASE_ENABLED && <div style={{fontSize:11,color:'#aaa',marginTop:10,textAlign:'center'}}>Note: cloud sync isn't configured on this deployment.</div>}
            </div>
          )}
        </div>
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC CANDIDATE SELF-REGISTRATION PAGE (no login required)
// ═══════════════════════════════════════════════════════════════════════════════
function CandidateSignupPage() {
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({name:'',phone:'',email:'',age:'',visa:'',visaOther:'',industry:'',
    skills:[],experience:'',availability:'Immediate',hasCar:'No',originCountry:'',currentCity:'',
    currentState:'',yearsInUSA:'',photo:'',notes:''});
  const [website, setWebsite] = useState(''); // honeypot
  const loadedAt = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => { if (FIREBASE_ENABLED) await initFirebase(); setReady(true); })();
  }, []);

  const setE = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));
  const toggleSkill = (sk) => setForm(f => ({...f, skills: f.skills.includes(sk) ? f.skills.filter(s=>s!==sk) : [...f.skills, sk]}));

  const submit = async () => {
    if (website) { setDone(true); return; }
    if (Date.now() - loadedAt.current < 2500) { setErr('Please take a moment to review before submitting.'); return; }
    if (!form.name.trim() || !form.phone.trim()) { setErr('Name and phone are required.'); return; }
    if (!form.age) { setErr('Age is required.'); return; }
    if (!form.visa) { setErr('Please select your work authorization.'); return; }
    if (form.visa==='Other' && !form.visaOther.trim()) { setErr('Please specify your work authorization.'); return; }
    if (!form.industry) { setErr('Please select your industry.'); return; }
    if (form.experience==='') { setErr('Please enter your years of experience.'); return; }
    if (!form.currentCity.trim() || !form.currentState) { setErr('Please enter the city and state you are currently in.'); return; }
    setErr(''); setSubmitting(true);
    try {
      const rec = {id: uid(), ...form, submittedAt: new Date().toISOString(), status: 'New'};
      await DB.save('candidateSubmissions', rec.id, rec);
      setDone(true);
    } catch (e) {
      setErr('Something went wrong submitting this. Please try again or contact us directly.');
    }
    setSubmitting(false);
  };

  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}}>
      <div style={{width:'100%',maxWidth:480}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <BrandBadge profile={profile} size={52}/>
          <div style={{fontSize:19,fontWeight:800,color:tx}}>{profile?.companyName || DEFAULT_PROFILE.companyName}</div>
          <div style={{fontSize:13,color:'#888',marginTop:2}}>Register as a job candidate</div>
        </div>
        <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:24,boxShadow:'0 2px 12px rgba(0,0,0,.04)'}}>
          {done ? (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>Thanks for registering!</div>
              <div style={{fontSize:13,color:'#666',lineHeight:1.6}}>We've received your info and will be in touch soon about job opportunities.</div>
            </div>
          ) : (
            <div>
              <div style={{position:'absolute',left:'-9999px',width:1,height:1,overflow:'hidden'}} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e=>setWebsite(e.target.value)}/>
              </div>
              <div style={{marginBottom:14}}>
                <label style={S.lbl}>Photo (optional)</label>
                <div style={{marginTop:5}}><PhotoUpload value={form.photo} onChange={p=>setForm(f=>({...f,photo:p}))}/></div>
              </div>
              <FG label="Full name *"><Inp value={form.name} onChange={setE('name')} placeholder="John Martinez"/></FG>
              <FG label="Phone number *" style={{marginTop:10}}><PhoneInput value={form.phone} onChange={setE('phone')} placeholder="(555) 000-0000"/></FG>
              <div style={S.fg2}>
                <FG label="Age *" style={{marginTop:10}}><Inp type="number" min={0} max={120} value={form.age} onChange={setE('age')}/></FG>
                <FG label="Email" style={{marginTop:10}}><Inp value={form.email} onChange={setE('email')} placeholder="john@email.com"/></FG>
                <FG label="Work authorization *" style={{marginTop:10}}><Sel value={form.visa} onChange={setE('visa')}><option value="">Select...</option>{VISAS.map(v=><option key={v}>{v}</option>)}</Sel></FG>
                {form.visa==='Other' && <FG label="Specify *" style={{marginTop:10}}><Inp value={form.visaOther} onChange={setE('visaOther')}/></FG>}
                <FG label="Industry *" style={{marginTop:10}}><Sel value={form.industry} onChange={setE('industry')}><option value="">Select...</option>{INDUSTRIES.map(v=><option key={v}>{v}</option>)}</Sel></FG>
                <FG label="Experience (years) *" style={{marginTop:10}}><Inp type="number" min={0} value={form.experience} onChange={setE('experience')}/></FG>
                <FG label="Originally from" style={{marginTop:10}}><Inp value={form.originCountry} onChange={setE('originCountry')} placeholder="India, Mexico..."/></FG>
                <FG label="Currently in — city *" style={{marginTop:10}}><Inp value={form.currentCity} onChange={setE('currentCity')}/></FG>
                <FG label="Currently in — state *" style={{marginTop:10}}><Sel value={form.currentState} onChange={setE('currentState')}><option value="">Select...</option>{STATES.map(s=><option key={s}>{s}</option>)}</Sel></FG>
                <FG label="Years in USA" style={{marginTop:10}}><Inp type="number" min={0} value={form.yearsInUSA} onChange={setE('yearsInUSA')}/></FG>
                <FG label="Availability" style={{marginTop:10}}><Sel value={form.availability} onChange={setE('availability')}><option>Immediate</option><option>2 weeks</option><option>1 month</option></Sel></FG>
              </div>
              <FG label="Skills" style={{marginTop:10}}><SkillChips selected={form.skills} onToggle={toggleSkill}/></FG>
              <FG label="Notes" style={{marginTop:10}}><TA value={form.notes} onChange={setE('notes')} placeholder="Anything else we should know..."/></FG>
              {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginTop:12}}>⚠ {err}</div>}
              <Btn prim onClick={submit} disabled={submitting} style={{marginTop:16,width:'100%',textAlign:'center'}}>{submitting?'Submitting...':'Submit'}</Btn>
            </div>
          )}
        </div>
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC CANDIDATE PHOTO UPLOAD PAGE (no login — existing candidate adds just a photo)
// ═══════════════════════════════════════════════════════════════════════════════
function ClientSignupPage() {
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({contact:'',name:'',phone:'',email:'',industry:'',city:'',state:'',openings:'1',
    payType:'hourly',wage:'',fixedPay:'',fixedPeriod:'Monthly',hours:'',shift:'',skills:'',jobDesc:'',
    carNeeded:'No',legalOnly:'No',cashPay:'No',accommodation:'No',notes:''});
  const [website, setWebsite] = useState(''); // honeypot
  const loadedAt = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { (async () => { if (FIREBASE_ENABLED) await initFirebase(); setReady(true); })(); }, []);
  const setE = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));
  const setV = (k) => (v) => setForm(f => ({...f, [k]: v}));
  const submit = async () => {
    if (website) { setDone(true); return; }
    if (Date.now() - loadedAt.current < 2500) { setErr('Please take a moment to review before submitting.'); return; }
    if (!form.contact.trim()) { setErr('Your name is required.'); return; }
    if (!form.name.trim()) { setErr('Business name is required.'); return; }
    if (!form.phone.trim()) { setErr('Phone number is required.'); return; }
    if (!form.industry) { setErr('Please select your business type.'); return; }
    if (!form.city.trim() || !form.state) { setErr('Please enter your business city and state.'); return; }
    if (!(parseInt(form.openings,10) > 0)) { setErr('Please enter how many people you need.'); return; }
    setErr(''); setSubmitting(true);
    try {
      const rec = {id: uid(), ...form, submittedAt: new Date().toISOString(), status: 'New'};
      await DB.save('clientSubmissions', rec.id, rec);
      setDone(true);
    } catch (e) {
      setErr('Something went wrong submitting this. Please try again or contact us directly.');
    }
    setSubmitting(false);
  };
  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );
  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}}>
      <div style={{width:'100%',maxWidth:520}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <BrandBadge profile={profile} size={52}/>
          <div style={{fontSize:19,fontWeight:800,color:tx}}>{profile?.companyName || DEFAULT_PROFILE.companyName}</div>
          <div style={{fontSize:13,color:'#888',marginTop:2}}>Tell us who you need to hire</div>
        </div>
        <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:24,boxShadow:'0 2px 12px rgba(0,0,0,.04)'}}>
          {done ? (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>Thanks, we've got your request!</div>
              <div style={{fontSize:13,color:'#666',lineHeight:1.6}}>Our team will review it and contact you shortly about candidates.</div>
            </div>
          ) : (
            <div>
              <div style={{position:'absolute',left:'-9999px',width:1,height:1,overflow:'hidden'}} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e=>setWebsite(e.target.value)}/>
              </div>
              <div style={S.fg2}>
                <FG label="Your name *"><Inp value={form.contact} onChange={setE('contact')} placeholder="Sam Patel"/></FG>
                <FG label="Business name *"><Inp value={form.name} onChange={setE('name')} placeholder="Green Valley Motel"/></FG>
                <FG label="Phone *"><PhoneInput value={form.phone} onChange={setE('phone')} placeholder="(555) 000-0000"/></FG>
                <FG label="Email"><Inp value={form.email} onChange={setE('email')} placeholder="owner@business.com"/></FG>
                <FG label="Business type *"><Sel value={form.industry} onChange={setE('industry')}><option value="">Select...</option>{INDUSTRIES.map(v=><option key={v}>{v}</option>)}</Sel></FG>
                <FG label="People needed *"><Inp type="number" min={1} value={form.openings} onChange={setE('openings')}/></FG>
                <FG label="City *"><Inp value={form.city} onChange={setE('city')} placeholder="Birmingham"/></FG>
                <FG label="State *"><Sel value={form.state} onChange={setE('state')}><option value="">Select...</option>{STATES.map(s=><option key={s}>{s}</option>)}</Sel></FG>
              </div>
              <SecLabel>💵 Pay</SecLabel>
              <Toggle label="Pay type" value={form.payType==='fixed'?'Fixed':'Hourly'} onChange={v=>setV('payType')(v==='Fixed'?'fixed':'hourly')} options={['Hourly','Fixed']}/>
              <div style={S.fg2}>
                {form.payType==='fixed' ? (
                  <>
                    <FG label="Fixed pay amount ($)"><Inp type="number" min={0} value={form.fixedPay} onChange={setE('fixedPay')} placeholder="3000"/></FG>
                    <FG label="Pay period"><Sel value={form.fixedPeriod} onChange={setE('fixedPeriod')}><option>Weekly</option><option>Bi-weekly</option><option>Monthly</option><option>Per shift</option></Sel></FG>
                  </>
                ) : (
                  <FG label="Hourly wage ($)"><Inp value={form.wage} onChange={setE('wage')} placeholder="15.00"/></FG>
                )}
                <FG label="Hours (e.g. 40/week)"><Inp value={form.hours} onChange={setE('hours')} placeholder="40 hrs/week"/></FG>
                <FG label="Shift"><Inp value={form.shift} onChange={setE('shift')} placeholder="Day, 8am–4pm"/></FG>
              </div>
              <SecLabel>⚙️ Requirements</SecLabel>
              <div style={S.fg2}>
                <Toggle label="Car required" value={form.carNeeded} onChange={setV('carNeeded')}/>
                <Toggle label="Legal workers only" value={form.legalOnly} onChange={setV('legalOnly')}/>
                <Toggle label="Pays in cash" value={form.cashPay} onChange={setV('cashPay')}/>
                <Toggle label="Accommodation provided" value={form.accommodation} onChange={setV('accommodation')}/>
              </div>
              <FG label="Skills needed" style={{marginTop:10}}><Inp value={form.skills} onChange={setE('skills')} placeholder="Housekeeping, Customer Service"/></FG>
              <FG label="Job description" style={{marginTop:10}}><TA value={form.jobDesc} onChange={setE('jobDesc')} placeholder="Duties, expectations, dress code..."/></FG>
              <FG label="Anything else" style={{marginTop:10}}><TA value={form.notes} onChange={setE('notes')} placeholder="Start date, housing details..."/></FG>
              {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginTop:12}}>⚠ {err}</div>}
              <Btn prim onClick={submit} disabled={submitting} style={{marginTop:16,width:'100%',textAlign:'center'}}>{submitting?'Submitting...':'Submit'}</Btn>
            </div>
          )}
        </div>
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

function CandidatePhotoPage() {
  const pathParts = window.location.pathname.replace(/\/$/,'').split('/').filter(Boolean); // ['candidate-photo', '<id>']
  const candidateId = pathParts[1] || '';
  const params = new URLSearchParams(window.location.search);
  const name = params.get('n') ? decodeURIComponent(params.get('n')) : '';
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [photo, setPhoto] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => { if (FIREBASE_ENABLED) await initFirebase(); setReady(true); })();
  }, []);

  const submit = async () => {
    if (!photo) { setErr('Please choose a photo first.'); return; }
    if (!candidateId) { setErr('This link looks incomplete — please ask for a new one.'); return; }
    setErr(''); setSubmitting(true);
    try {
      await DB.save('candidates', candidateId, {id: candidateId, photo});
      setDone(true);
    } catch (e) {
      setErr('Something went wrong. Please try again or contact us directly.');
    }
    setSubmitting(false);
  };

  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}}>
      <div style={{width:'100%',maxWidth:420}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <BrandBadge profile={profile} size={52}/>
          <div style={{fontSize:19,fontWeight:800,color:tx}}>{profile?.companyName || DEFAULT_PROFILE.companyName}</div>
        </div>
        <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:24,boxShadow:'0 2px 12px rgba(0,0,0,.04)',textAlign:'center'}}>
          {done ? (
            <div style={{padding:'20px 0'}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>Photo added!</div>
              <div style={{fontSize:13,color:'#666'}}>Thanks{name?`, ${name}`:''} — that's all we needed.</div>
            </div>
          ) : (
            <div>
              <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Hi{name?` ${name}`:''} 👋</div>
              <div style={{fontSize:13,color:'#666',marginBottom:18,lineHeight:1.6}}>Add a photo for your candidate profile. This is optional — you can submit without one if you'd rather not.</div>
              <div style={{display:'flex',justifyContent:'center'}}><PhotoUpload value={photo} onChange={setPhoto} size={110}/></div>
              {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginTop:16,textAlign:'left'}}>⚠ {err}</div>}
              <Btn prim onClick={submit} disabled={submitting||!photo} style={{marginTop:18,width:'100%',textAlign:'center'}}>{submitting?'Uploading...':'Submit photo'}</Btn>
            </div>
          )}
        </div>
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC CANDIDATE PROFILE PAGE (read-only — for sharing with a client)
// ═══════════════════════════════════════════════════════════════════════════════
function CandidateProfilePage() {
  const pathParts = window.location.pathname.replace(/\/$/,'').split('/').filter(Boolean); // ['candidate', '<id>']
  const candidateId = pathParts[1] || '';
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [cand, setCand] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        if (FIREBASE_ENABLED) await initFirebase();
        const rec = await DB.getOne('candidates', candidateId);
        if (rec) setCand(rec); else setErr("This candidate profile isn't available.");
      } catch (e) {
        setErr("This candidate profile isn't available.");
      }
      setReady(true);
    })();
  }, [candidateId]);

  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );

  if (err || !cand) return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:30,textAlign:'center',maxWidth:360}}>
        <div style={{fontSize:32,marginBottom:10}}>🔍</div>
        <div style={{fontSize:14,color:'#666'}}>{err||"This candidate profile isn't available."}</div>
      </div>
    </div>
  );

  const visaDisplay = cand.visa==='Other' ? (cand.visaOther||'Other') : (cand.visa||'');
  const location = [cand.currentCity, cand.currentState].filter(Boolean).join(', ');

  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}}>
      <div style={{width:'100%',maxWidth:480}}>
        <div style={{textAlign:'center',marginBottom:16}}>
          <BrandBadge profile={profile} size={36}/>
          <div style={{fontSize:12,color:'#888'}}>Candidate profile from {profile?.companyName || DEFAULT_PROFILE.companyName}</div>
        </div>
        <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:28,boxShadow:'0 2px 12px rgba(0,0,0,.04)'}}>
          <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:20}}>
            {cand.photo ? <img src={cand.photo} alt="" style={{width:84,height:84,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/> : <div style={{width:84,height:84,borderRadius:'50%',background:'#f0efe9',display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,color:'#ccc',flexShrink:0}}>👤</div>}
            <div>
              <div style={{fontSize:20,fontWeight:800,color:tx}}>{cand.name}</div>
              <div style={{fontSize:13,color:'#888',marginTop:2}}>{cand.age?`${cand.age} years old`:''}{cand.age&&location?' · ':''}{location}</div>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:18}}>
            {cand.industry && <div><div style={S.lbl}>Industry</div><div style={{fontSize:13,fontWeight:600}}>{cand.industry}</div></div>}
            {cand.experience && <div><div style={S.lbl}>Experience</div><div style={{fontSize:13,fontWeight:600}}>{cand.experience} years</div></div>}
            {visaDisplay && <div><div style={S.lbl}>Work authorization</div><div style={{fontSize:13,fontWeight:600}}>{visaDisplay}</div></div>}
            <div><div style={S.lbl}>Availability</div><div style={{fontSize:13,fontWeight:600}}>{cand.availability||'—'}</div></div>
            {cand.hasCar && <div><div style={S.lbl}>Has a car</div><div style={{fontSize:13,fontWeight:600}}>{cand.hasCar}</div></div>}
            {(parseInt(cand.members,10)||1)>1 && <div><div style={S.lbl}>Group size</div><div style={{fontSize:13,fontWeight:600}}>{cand.members} people</div></div>}
          </div>
          {cand.skills && cand.skills.length>0 && (
            <div style={{marginBottom:18}}>
              <div style={S.lbl}>Skills</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:5,marginTop:5}}>{cand.skills.map(s=><span key={s} style={S.tB}>{s}</span>)}</div>
            </div>
          )}
          {cand.notes && (
            <div style={{marginBottom:6}}>
              <div style={S.lbl}>Notes</div>
              <div style={{fontSize:13,color:'#555',marginTop:4,lineHeight:1.6}}>{cand.notes}</div>
            </div>
          )}
          <div style={{marginTop:20,paddingTop:16,borderTop:`0.5px solid ${br}`,fontSize:12,color:'#888',textAlign:'center'}}>
            Contact {profile?.companyName || DEFAULT_PROFILE.companyName} to move forward with this candidate.
          </div>
        </div>
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC CLIENT PORTAL PAGE (read-only — workers placed, invoices, contract)
// ═══════════════════════════════════════════════════════════════════════════════
function ContractSignPage() {
  const pathParts = window.location.pathname.replace(/\/$/,'').split('/').filter(Boolean); // ['sign-contract','<clientId>']
  const clientId = pathParts[1] || '';
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [client, setClient] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot
  useEffect(() => {
    (async () => {
      try {
        if (FIREBASE_ENABLED) await initFirebase();
        const cl = await DB.getOne('clients', clientId);
        if (!cl) { setErr("This link isn't available."); setReady(true); return; }
        setClient(cl);
        const agr = await DB.getOne('clientAgreements', clientId);
        if (agr) setAgreement(agr);
      } catch (e) { setErr("This link isn't available."); }
      setReady(true);
    })();
  }, [clientId]);
  const p = {...DEFAULT_PROFILE, ...profile};
  const text = !client ? '' : ((agreement?.text && (agreement.signedAt || agreement.sentAt)) ? agreement.text : fillClientContract(profile, client));
  const signed = !!agreement?.signedAt;
  const sign = async () => {
    if (website) return;
    if (!name.trim()) { setErr('Please type your full name.'); return; }
    if (!agree) { setErr('Please tick the box to agree.'); return; }
    setErr(''); setBusy(true);
    try {
      const rec = {id: client.id, clientId: client.id, clientName: client.name, text,
        agencySig: agreement?.agencySig || p.companyName || '', clientSig: name.trim(),
        signedAt: new Date().toISOString(), signedVia: 'public-link', sentAt: agreement?.sentAt || ''};
      await DB.save('clientAgreements', rec.id, rec);
      setAgreement(rec);
    } catch (e) { setErr('Could not save your signature. Please contact ' + (p.companyName||'us') + '.'); }
    setBusy(false);
  };
  if (!ready) return (<div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{color:'#fff',fontSize:13}}>Loading...</div></div>);
  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',justifyContent:'center',padding:'32px 16px'}}>
      <div style={{width:'100%',maxWidth:640}}>
        <div style={{textAlign:'center',marginBottom:18}}>
          <BrandBadge profile={profile} size={52}/>
          <div style={{fontSize:19,fontWeight:800,color:tx}}>{p.companyName}</div>
          <div style={{fontSize:13,color:'#888',marginTop:2}}>Service Agreement</div>
        </div>
        <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:22,boxShadow:'0 2px 12px rgba(0,0,0,.04)'}}>
          {!client ? <div style={{textAlign:'center',color:'#888',fontSize:13,padding:20}}>{err||"This link isn't available."}</div> : (
            <div>
              <div style={{fontSize:12,color:'#888',marginBottom:10}}>Prepared for <strong>{client.name}</strong></div>
              <div style={{fontFamily:'Georgia,serif',fontSize:13.5,whiteSpace:'pre-wrap',lineHeight:1.7,border:`0.5px solid ${br}`,borderRadius:8,padding:'14px 16px',maxHeight:420,overflowY:'auto'}}>{text}</div>
              {signed ? (
                <div style={{background:'#EAF3DE',border:'0.5px solid #cde3b0',borderRadius:8,padding:'12px 14px',fontSize:13,color:'#3B6D11',marginTop:14}}>
                  ✓ Signed by <strong>{agreement.clientSig}</strong> on {new Date(agreement.signedAt).toLocaleString()}. Thank you!
                </div>
              ) : (
                <div style={{marginTop:14}}>
                  <div style={{position:'absolute',left:'-9999px',width:1,height:1,overflow:'hidden'}} aria-hidden="true"><input tabIndex={-1} autoComplete="off" value={website} onChange={e=>setWebsite(e.target.value)}/></div>
                  <div style={{fontSize:12,color:'#666',marginBottom:6}}>To accept, type your full legal name. This counts as your electronic signature.</div>
                  <Inp value={name} onChange={e=>setName(e.target.value)} placeholder="Your full name"/>
                  <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:12,color:'#444',margin:'10px 0'}}>
                    <input type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)} style={{marginTop:2}}/>
                    <span>I have read this agreement and agree to its terms on behalf of {client.name}.</span>
                  </label>
                  {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginBottom:10}}>⚠ {err}</div>}
                  <Btn prim onClick={sign} disabled={busy} style={{width:'100%',textAlign:'center'}}>{busy?'Signing...':'✍️ Sign agreement'}</Btn>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

function InvoicePublicPage() {
  const pathParts = window.location.pathname.replace(/\/$/,'').split('/').filter(Boolean); // ['invoice','<placementId>']
  const plId = pathParts[1] || '';
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [pl, setPl] = useState(null);
  const [client, setClient] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        if (FIREBASE_ENABLED) await initFirebase();
        const p = await DB.getOne('placements', plId);
        if (p) { setPl(p); setClient(await DB.getOne('clients', p.clientId)); }
      } catch (e) { /* shows the unavailable message */ }
      setReady(true);
    })();
  }, [plId]);
  if (!ready || !profile) return (<div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{color:'#fff',fontSize:13}}>Loading...</div></div>);
  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',padding:'24px 12px'}}>
      {pl && client ? <InvModal pl={pl} client={client} profile={profile} readOnly onClose={()=>{}} onUpdate={()=>{}} onDelete={()=>{}}/>
        : <div style={{maxWidth:420,margin:'80px auto',textAlign:'center',color:'#888',fontSize:14}}>This invoice isn't available.</div>}
      <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
    </div>
  );
}

function ClientPortalPage() {
  const pathParts = window.location.pathname.replace(/\/$/,'').split('/').filter(Boolean); // ['client', '<id>']
  const clientId = pathParts[1] || '';
  const profile = usePublicProfile();
  const [ready, setReady] = useState(false);
  const [client, setClient] = useState(null);
  const [placementRows, setPlacementRows] = useState([]);
  const [agreement, setAgreement] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        if (FIREBASE_ENABLED) await initFirebase();
        const cl = await DB.getOne('clients', clientId);
        if (!cl) { setErr("This page isn't available."); setReady(true); return; }
        setClient(cl);
        const ids = cl.placementIds || [];
        const rows = [];
        for (const pid of ids) {
          const pl = await DB.getOne('placements', pid);
          if (!pl) continue;
          const cand = pl.candidateId ? await DB.getOne('candidates', pl.candidateId) : null;
          rows.push({pl, cand});
        }
        rows.sort((a,b) => (b.pl.date||'').localeCompare(a.pl.date||''));
        setPlacementRows(rows);
        const agr = await DB.getOne('clientAgreements', clientId);
        if (agr) setAgreement(agr);
      } catch (e) {
        setErr("This page isn't available.");
      }
      setReady(true);
    })();
  }, [clientId]);

  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:13}}>Loading...</div>
    </div>
  );

  if (err || !client) return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:30,textAlign:'center',maxWidth:360}}>
        <div style={{fontSize:32,marginBottom:10}}>🔍</div>
        <div style={{fontSize:14,color:'#666'}}>{err||"This page isn't available."}</div>
      </div>
    </div>
  );

  const totalBilled = placementRows.reduce((a,{pl})=>a+(pl.total||0),0);
  const totalPaid = placementRows.reduce((a,{pl})=>a+parseFloat(pl.alreadyPaid||0),0);
  const totalDue = totalBilled - totalPaid;
  const p = {...DEFAULT_PROFILE, ...(profile||{})};

  const printAgreement = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Service Agreement — ${client.name||''}</title></head><body style="font-family:Georgia,serif;padding:40px;white-space:pre-wrap;line-height:1.7;font-size:14px;">${(agreement.text||'').replace(/</g,'&lt;')}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  const printInvoice = (pl) => {
    const balance = (pl.total||0) - parseFloat(pl.alreadyPaid||0);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Invoice ${pl.invNum}</title></head><body style="font-family:'Helvetica Neue',Arial,sans-serif;padding:40px;font-size:13px;">
      <div style="font-size:22px;font-weight:900;color:${bl};margin-bottom:4px;">INVOICE ${pl.invNum}</div>
      <div style="color:#666;margin-bottom:16px;">Date: ${pl.date} · From: ${(p.companyName||'').replace(/</g,'&lt;')}</div>
      <table style="width:100%;border-collapse:collapse;"><thead><tr style="background:#f4f4f0;"><th style="text-align:left;padding:8px;border:1px solid #ddd;">Description</th><th style="text-align:right;padding:8px;border:1px solid #ddd;">Amount</th></tr></thead>
      <tbody>${(pl.items||[]).map(it=>`<tr><td style="padding:8px;border:1px solid #ddd;">${(it.desc||'').replace(/</g,'&lt;')}</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${fmtUSD((parseFloat(it.rate)||0)*(parseInt(it.qty)||1)-(parseFloat(it.discPerUnit)||0)*(parseInt(it.qty)||1))}</td></tr>`).join('')}</tbody>
      <tfoot><tr><td style="padding:8px;border:1px solid #ddd;font-weight:700;text-align:right;">Total</td><td style="text-align:right;padding:8px;border:1px solid #ddd;font-weight:700;">${fmtUSD(pl.total||0)}</td></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;text-align:right;">Paid</td><td style="text-align:right;padding:8px;border:1px solid #ddd;">${fmtUSD(pl.alreadyPaid||0)}</td></tr>
      <tr><td style="padding:8px;border:1px solid #ddd;font-weight:700;text-align:right;">Balance due</td><td style="text-align:right;padding:8px;border:1px solid #ddd;font-weight:700;color:${balance>0?rd:gr};">${fmtUSD(balance)}</td></tr></tfoot></table>
      </body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 16px'}}>
      <div style={{width:'100%',maxWidth:640}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <BrandBadge profile={profile} size={48}/>
          <div style={{fontSize:19,fontWeight:800,color:tx}}>{client.name}</div>
          <div style={{fontSize:13,color:'#888',marginTop:2}}>Your workers & billing with {p.companyName}</div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px',textAlign:'center'}}>
            <div style={{fontSize:20,fontWeight:800,color:tx}}>{placementRows.length}</div>
            <div style={{fontSize:11,color:'#888'}}>Workers placed</div>
          </div>
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px',textAlign:'center'}}>
            <div style={{fontSize:20,fontWeight:800,color:tx}}>{fmtUSD(totalBilled)}</div>
            <div style={{fontSize:11,color:'#888'}}>Total billed</div>
          </div>
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:10,padding:'12px 14px',textAlign:'center'}}>
            <div style={{fontSize:20,fontWeight:800,color:totalDue>0.01?rd:gr}}>{fmtUSD(totalDue)}</div>
            <div style={{fontSize:11,color:'#888'}}>Balance due</div>
          </div>
        </div>

        {agreement && (
          <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,padding:'16px 18px',marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:14,fontWeight:700}}>📜 Service Agreement</div>
              <Btn sm onClick={printAgreement}>🖨 View / Print</Btn>
            </div>
            {agreement.signedAt && <div style={{fontSize:11,color:'#3B6D11',marginTop:6}}>✓ Signed {new Date(agreement.signedAt).toLocaleDateString()}</div>}
          </div>
        )}

        <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>Workers placed with you ({placementRows.length})</div>
        {placementRows.length===0 && <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,padding:24,textAlign:'center',color:'#aaa',fontSize:13}}>No placements yet.</div>}
        {placementRows.map(({pl,cand}) => {
          const balance = (pl.total||0) - parseFloat(pl.alreadyPaid||0);
          return (
            <div key={pl.id} style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,padding:'12px 16px',marginBottom:8,display:'flex',alignItems:'center',gap:12}}>
              {cand?.photo ? <img src={cand.photo} alt="" style={{width:40,height:40,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/> : <div style={{...S.av,width:40,height:40}}>{initials(pl.candidateName)}</div>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:13.5}}>{pl.candidateName}</div>
                <div style={{fontSize:11.5,color:'#888',marginTop:1}}>{pl.invNum} · Placed {pl.date}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:13,fontWeight:700,color:tx}}>{fmtUSD(pl.total||0)}</div>
                <div style={{fontSize:11,color:balance>0.01?rd:gr}}>{balance>0.01?`Due ${fmtUSD(balance)}`:'Paid ✓'}</div>
              </div>
              <Btn sm onClick={()=>printInvoice(pl)}>🖨</Btn>
            </div>
          );
        })}
        <div style={{textAlign:'center',fontSize:11,color:'#aaa',marginTop:16}}>Powered by Three9 CRM</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CANDIDATES TAB
// ═══════════════════════════════════════════════════════════════════════════════
const CAND_IMPORT_FIELDS = ['name','phone','email','age','visa','industry','skills','experience','availability','originCountry','currentCity','currentState','yearsInUSA','hasCar','members','notes'];

function CandidatesTab({candidates, setCandidates, toast, profile, addAct, referralPartners, user, isAdmin, candidateSubmissions, setCandidateSubmissions}) {
  const [q, setQ] = useState('');
  const [form, setForm] = useState({...CBLANK});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [waFor, setWaFor] = useState(null);
  const [showDupes, setShowDupes] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [pendingDupe, setPendingDupe] = useState(null);
  const [linkCopied, setLinkCopied] = useState('');

  const getBaseUrl = useCallback(() => {
    if (profile?.publicUrl) return profile.publicUrl.replace(/\/$/,'');
    if (typeof window!=='undefined' && window.location.origin && !window.location.origin.startsWith('file:')) return window.location.origin;
    return '';
  }, [profile]);
  const copyPhotoLink = useCallback((cand) => {
    const base = getBaseUrl();
    if (!base) { toast('⚠ Set your Public web app URL in My Business first.'); return; }
    navigator.clipboard?.writeText(`${base}/candidate-photo/${cand.id}?n=${encodeURIComponent(cand.name)}`);
    setLinkCopied(cand.id+'-photo'); toast('Photo link copied ✓');
    setTimeout(()=>setLinkCopied(''), 2000);
  }, [getBaseUrl, toast]);
  const copyProfileLink = useCallback((cand) => {
    const base = getBaseUrl();
    if (!base) { toast('⚠ Set your Public web app URL in My Business first.'); return; }
    navigator.clipboard?.writeText(`${base}/candidate/${cand.id}`);
    setLinkCopied(cand.id+'-profile'); toast('Profile link copied ✓');
    setTimeout(()=>setLinkCopied(''), 2000);
  }, [getBaseUrl, toast]);
  const copySignupLink = useCallback(() => {
    const base = getBaseUrl();
    if (!base) { toast('⚠ Set your Public web app URL in My Business first.'); return; }
    navigator.clipboard?.writeText(`${base}/candidate-signup`);
    setLinkCopied('signup'); toast('Signup link copied ✓');
    setTimeout(()=>setLinkCopied(''), 2000);
  }, [getBaseUrl, toast]);

  // Employees only ever see candidates they personally added — admins see everyone's.
  const visibleCandidates = isAdmin===false ? candidates.filter(c=>c.addedBy===user?.uid) : candidates;

  const doSave = useCallback(async (rec) => {
    await DB.save('candidates', rec.id, rec);
    setCandidates(p => [rec, ...p]);
    setForm({...CBLANK});
    toast('Candidate saved ✓');
    addAct(`👤 Added candidate "${rec.name}"`);
    syncCandidateToSheets(rec, profile?.sheetsUrl);
  }, [setCandidates, toast, addAct, profile]);

  const save = useCallback(async () => {
    if (!form.name || !form.phone) { toast('Name and phone required.'); return; }
    const dupe = checkDuplicate(visibleCandidates, form.phone, null);
    const rec = {...form, id:uid(), addedAt:today(), createdAtTs:Date.now(), addedBy:user?.uid||'', addedByName:user?.name||''};
    if (dupe) { setPendingDupe({rec, existing:dupe}); return; }
    await doSave(rec);
  }, [form, toast, visibleCandidates, doSave, user]);

  const confirmDupeSave = useCallback(async () => {
    if (!pendingDupe) return;
    await doSave(pendingDupe.rec);
    setPendingDupe(null);
  }, [pendingDupe, doSave]);

  const del = useCallback(async (id) => {
    if (!window.confirm('Delete this candidate?')) return;
    const rec = candidates.find(c=>c.id===id);
    await DB.delete('candidates', id);
    setCandidates(p => p.filter(c => c.id !== id));
    toast('Deleted.');
    addAct(`🗑 Deleted candidate "${rec?.name||''}"`);
    deleteCandidateFromSheets(id, profile?.sheetsUrl);
  }, [toast, candidates, setCandidates, addAct, profile]);

  const startEdit = useCallback((c) => { setEditing(c.id); setEditForm({...CBLANK, ...c}); }, []);
  const cancelEdit = useCallback(() => setEditing(null), []);
  const saveEdit = useCallback(async () => {
    await DB.save('candidates', editForm.id, editForm);
    setCandidates(p => p.map(c => c.id === editing ? editForm : c));
    setEditing(null); toast('Updated ✓');
    addAct(`✏️ Updated candidate "${editForm.name}"`);
    syncCandidateToSheets(editForm, profile?.sheetsUrl);
  }, [editForm, editing, toast, setCandidates, addAct, profile]);

  const doImport = useCallback(async (rows) => {
    let count = 0;
    for (const row of rows) {
      if (!row.name || !row.phone) continue;
      const rec = {...CBLANK, ...row,
        skills: row.skills ? row.skills.split(/[;|]/).map(s=>s.trim()).filter(Boolean) : [],
        id: uid(), addedAt: today(), createdAtTs: Date.now(), addedBy:user?.uid||'', addedByName:user?.name||''};
      await DB.save('candidates', rec.id, rec);
      count++;
      setCandidates(p => [rec, ...p]);
      syncCandidateToSheets(rec, profile?.sheetsUrl);
    }
    setShowImport(false);
    toast(`Imported ${count} candidate(s) ✓`);
    addAct(`📥 Bulk imported ${count} candidate(s)`);
  }, [setCandidates, toast, addAct, profile, user]);

  const pendingCandSubs = (candidateSubmissions||[]).filter(s=>s.status==='New');
  const acceptCandSubmission = useCallback(async (sub) => {
    const dupe = checkDuplicate(visibleCandidates, sub.phone, null);
    if (dupe && !window.confirm(`A candidate named "${dupe.name}" already has this phone number. Add anyway?`)) return;
    const rec = {...CBLANK, ...sub, id:uid(), addedAt:today(), createdAtTs:Date.now(), addedBy:user?.uid||'', addedByName:user?.name||''};
    delete rec.status;
    await DB.save('candidates', rec.id, rec);
    setCandidates(p => [rec, ...p]);
    const updatedSub = {...sub, status:'accepted', candidateId:rec.id};
    await DB.save('candidateSubmissions', updatedSub.id, updatedSub);
    setCandidateSubmissions(p => p.map(s=>s.id===sub.id?updatedSub:s));
    toast(`Added "${sub.name}" ✓`);
    addAct(`📥 Accepted self-submitted candidate "${sub.name}"`);
  }, [visibleCandidates, setCandidates, setCandidateSubmissions, toast, addAct, user]);
  const dismissCandSubmission = useCallback(async (sub) => {
    if (!window.confirm(`Dismiss the submission from "${sub.name}"?`)) return;
    const updated = {...sub, status:'dismissed'};
    await DB.save('candidateSubmissions', updated.id, updated);
    setCandidateSubmissions(p => p.map(s=>s.id===sub.id?updated:s));
    toast('Dismissed.');
  }, [setCandidateSubmissions, toast]);

  const filtered = visibleCandidates.filter(c => {
    const s = q.toLowerCase();
    return !q || (c.name||'').toLowerCase().includes(s) || (c.phone||'').includes(s) ||
      (c.industry||'').toLowerCase().includes(s) || (c.visa||'').toLowerCase().includes(s) ||
      (c.originCountry||'').toLowerCase().includes(s);
  }).sort((a,b) => (b.createdAtTs||0) - (a.createdAtTs||0));
  const dupeCount = findDuplicateGroups(visibleCandidates).length;

  return (
    <div>
      <div style={{background:'#E6F1FB',border:'0.5px solid #c7dcf0',borderRadius:10,padding:'12px 14px',marginBottom:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:13,fontWeight:700,color:'#0C447C'}}>📋 Let candidates register themselves</div>
          <div style={{fontSize:11,color:'#0C447C',marginTop:2}}>Share this link so candidates can submit their own info (and photo) — submissions land below for your review.</div>
        </div>
        <Btn sm onClick={copySignupLink}>{linkCopied==='signup'?'✓ Copied':'🔗 Copy signup link'}</Btn>
      </div>
      {pendingCandSubs.length>0 && (
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>📥 New candidate submissions ({pendingCandSubs.length})</div>
          {[...pendingCandSubs].sort((a,b)=>(b.submittedAt||'').localeCompare(a.submittedAt||'')).map(sub => (
            <div key={sub.id} style={{...S.li, background:'#E6F1FB', border:'0.5px solid #c7dcf0'}}>
              {sub.photo ? <img src={sub.photo} alt="" style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/> : <div style={S.av}>{initials(sub.name)}</div>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{sub.name}{sub.age?` · ${sub.age}yo`:''}</div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{sub.phone}{sub.email?' · '+sub.email:''}{sub.industry?' · '+sub.industry:''}</div>
                <div style={{fontSize:11,color:'#0C447C',marginTop:3}}>Self-submitted · {fmtDateTime(sub.submittedAt)}</div>
              </div>
              <div style={{display:'flex',gap:5,flexShrink:0}}>
                <Btn sm onClick={()=>acceptCandSubmission(sub)}>✅ Add candidate</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>dismissCandSubmission(sub)}>Dismiss</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={visibleCandidates.reduce((a,c)=>a+personCount(c),0)} label="Total people"/>
        <StatCard n={visibleCandidates.filter(c=>c.availability==='Immediate').length} label="Available now"/>
        <StatCard n={visibleCandidates.filter(c=>c.hasCar==='Yes').length} label="Have a car"/>
        <StatCard n={visibleCandidates.filter(c=>(parseInt(c.members,10)||1)>1).length} label="Groups (2+)"/>
      </div>
      <div style={S.card}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:3}}>Add candidate</div>
        <div style={{fontSize:12,color:'#888',marginBottom:14}}>Data saves automatically to cloud</div>
        <CandidateForm value={form} onChange={setForm} onSave={save} saveLabel="Add candidate" referralPartners={referralPartners} existingCandidates={visibleCandidates}/>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <Btn sm onClick={()=>setShowImport(true)}>📥 Bulk import (CSV)</Btn>
        <Btn sm onClick={()=>setShowDupes(true)}>🔍 Scan duplicates{dupeCount>0?` (${dupeCount})`:''}</Btn>
      </div>
      <SBar value={q} onChange={setQ} placeholder="Search name, phone, industry, visa, origin..."/>
      {filtered.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>👤</div>{q?'No match.':'No candidates yet.'}</div>}
      {filtered.map(rec => (
        <div key={rec.id}>
          {editing === rec.id ? (
            <div style={S.card}>
              <div style={{fontSize:14,fontWeight:600,marginBottom:12}}>Editing: {rec.name}</div>
              <CandidateForm value={editForm} onChange={setEditForm} onSave={saveEdit} onCancel={cancelEdit} saveLabel="Save changes" referralPartners={referralPartners} existingCandidates={visibleCandidates}/>
            </div>
          ) : (
            <div style={S.li}>
              {rec.photo ? <img src={rec.photo} alt="" style={{width:44,height:44,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/> : <div style={S.av}>{initials(rec.name)}</div>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{rec.name}{rec.age?` · ${rec.age}yo`:''}</div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{rec.phone} · {rec.visa==='Other'?(rec.visaOther||'Other'):(rec.visa||'Visa?')} · {rec.industry||'No industry'}{rec.experience?` · ${rec.experience}yr exp`:''}{(rec.currentCity||rec.currentState)?` · Currently in: ${[rec.currentCity,rec.currentState].filter(Boolean).join(', ')}`:''}{rec.originCountry?` · From: ${rec.originCountry}`:''}{rec.yearsInUSA?` · ${rec.yearsInUSA}yr in USA`:''}</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
                  {(rec.skills||[]).slice(0,5).map(s=><span key={s} style={S.tB}>{s}</span>)}
                  <span style={rec.availability==='Immediate'?S.tG:S.tA}>{rec.availability}</span>
                  {rec.hasCar==='Yes'&&<span style={S.tT}>🚗 Car</span>}
                  {(parseInt(rec.members,10)||1)>1&&<span style={S.tGr}>👫 {rec.members} members</span>}
                  {rec.stage&&rec.stage!=='New'&&<span style={{...S.tGr,background:STAGE_BG[rec.stage],color:STAGE_FG[rec.stage]}}>{rec.stage==='Interviewing'&&rec.interviewClientName?`🗓 Interviewing at ${rec.interviewClientName}`:rec.stage}</span>}
                  {rec.followUpDate&&<span style={S.tA}>⏰ Follow-up: {rec.followUpDate}</span>}
                  {rec.referralPartnerName&&<span style={S.tB}>🏛 Ref: {rec.referralPartnerName}</span>}
                  {!rec.photo&&<span style={S.tA}>📷 No photo</span>}
                </div>
              </div>
              <div style={{fontSize:11,color:'#999',marginRight:8,textAlign:'right',minWidth:92}}>{(()=>{const a=addedInfo(rec);return <><div>{a.label}</div><div>{a.text}</div></>;})()}</div>
              <div style={{display:'flex',gap:5,flexShrink:0,flexWrap:'wrap',maxWidth:180,justifyContent:'flex-end'}}>
                <Btn sm onClick={()=>setWaFor(rec)}>📱 WhatsApp</Btn>
                <Btn sm onClick={()=>copyPhotoLink(rec)}>{linkCopied===rec.id+'-photo'?'✓ Copied':'📷 Photo link'}</Btn>
                <Btn sm onClick={()=>copyProfileLink(rec)}>{linkCopied===rec.id+'-profile'?'✓ Copied':'🔗 Profile link'}</Btn>
                <Btn sm onClick={()=>startEdit(rec)}>✏️ Edit</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>del(rec.id)}>🗑</Btn>
              </div>
            </div>
          )}
        </div>
      ))}
      {waFor && <WhatsAppModal cand={waFor} profile={profile} onClose={()=>setWaFor(null)}/>}
      {showDupes && <DuplicateModal list={candidates} labelKey="name" onClose={()=>setShowDupes(false)}/>}
      {showImport && <BulkImportModal fields={CAND_IMPORT_FIELDS} onImport={doImport} onClose={()=>setShowImport(false)}/>}
      {pendingDupe && (
        <Modal onClose={()=>setPendingDupe(null)} maxWidth={420}>
          <div style={{fontSize:15,fontWeight:700,marginBottom:8}}>⚠ Possible duplicate</div>
          <div style={{fontSize:13,color:'#555',marginBottom:14}}>A candidate named <strong>{pendingDupe.existing.name}</strong> already has phone number <strong>{pendingDupe.existing.phone}</strong>. Save anyway?</div>
          <div style={{display:'flex',gap:8}}>
            <Btn prim onClick={confirmDupeSave}>Save anyway</Btn>
            <Btn onClick={()=>setPendingDupe(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLIENTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ClientsTab({clients, setClients, toast, addAct, profile, clientAgreements, setClientAgreements, candidates, clientSubmissions=[], setClientSubmissions}) {
  const [q, setQ] = useState('');
  const [form, setForm] = useState({...CLBLANK});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [showDupes, setShowDupes] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [pendingDupe, setPendingDupe] = useState(null);
  const [contractFor, setContractFor] = useState(null);
  const [linkCopied, setLinkCopied] = useState('');
  const [loginFor, setLoginFor] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [clientLogins, setClientLogins] = useState([]); // users with role:'client'

  const loadClientLogins = useCallback(async () => {
    if (!FIREBASE_ENABLED) return;
    try { const all = await fbGetAll('users'); setClientLogins(all.filter(u=>u.role==='client')); }
    catch { /* leave as-is if this fails — login buttons just won't show status */ }
  }, []);
  useEffect(() => { loadClientLogins(); }, [loadClientLogins]);

  const openCreateLogin = useCallback((client) => {
    setLoginFor(client); setLoginEmail(client.email||''); setLoginPass(''); setLoginErr('');
  }, []);
  const createClientLogin = useCallback(async () => {
    if (!loginEmail || !loginPass) { setLoginErr('Email and password required.'); return; }
    if (loginPass.length < 6) { setLoginErr('Password must be 6+ characters.'); return; }
    if (!FIREBASE_ENABLED) { setLoginErr('Client logins need cloud sync (Firebase) to be set up.'); return; }
    setLoginBusy(true); setLoginErr('');
    try {
      const cred = await createFirebaseUser(loginEmail, loginPass);
      const userData = {uid:cred.user.uid, name:loginFor.contact||loginFor.name, email:loginEmail, role:'client', clientId:loginFor.id, createdAt:today()};
      await fbSetDoc('users', cred.user.uid, userData);
      toast(`Login created for ${loginFor.name} ✓`);
      addAct(`🔑 Created client login for "${loginFor.name}"`);
      setLoginFor(null);
      await loadClientLogins();
    } catch (e) {
      setLoginErr(e.code==='auth/email-already-in-use' ? 'That email already has an account.' : ('Error: '+e.message));
    }
    setLoginBusy(false);
  }, [loginEmail, loginPass, loginFor, toast, addAct, loadClientLogins]);

  const resetClientPassword = useCallback(async (login) => {
    try {
      await sendPasswordReset(login.email);
      toast(`Password reset email sent to ${login.email} ✓`);
    } catch (e) {
      toast('Could not send reset email: ' + e.message);
    }
  }, [toast]);

  const deleteClientLogin = useCallback(async (login, clientName) => {
    // The client SDK can only ever act on the CURRENTLY signed-in user, so an
    // admin can't directly delete someone else's Firebase Auth account from
    // here — that needs the Admin SDK (a Cloud Function), which this project
    // doesn't have set up. What this DOES do: remove their access record, so
    // the account can no longer get into the CRM even though it technically
    // still exists in Firebase Auth. See the confirm dialog for the same note.
    if (!window.confirm(`Remove ${clientName}'s login access? They won't be able to sign in anymore. (Note: this revokes their access immediately, but doesn't fully delete the underlying account — see CLIENT_PORTAL_SETUP.txt for why.)`)) return;
    try {
      await DB.delete('users', login.uid);
      setClientLogins(p => p.filter(u => u.uid !== login.uid));
      toast('Login access removed ✓');
      addAct(`🗑 Removed client login for "${clientName}"`);
    } catch (e) {
      toast('Something went wrong removing that login.');
    }
  }, [toast, addAct]);

  const getBaseUrl = useCallback(() => {
    if (profile?.publicUrl) return profile.publicUrl.replace(/\/$/,'');
    if (typeof window!=='undefined' && window.location.origin && !window.location.origin.startsWith('file:')) return window.location.origin;
    return '';
  }, [profile]);
  const copyPortalLink = useCallback((client) => {
    const base = getBaseUrl();
    if (!base) { toast('⚠ Set your Public web app URL in My Business first.'); return; }
    navigator.clipboard?.writeText(`${base}/client/${client.id}`);
    setLinkCopied(client.id); toast('Client portal link copied ✓');
    setTimeout(()=>setLinkCopied(''), 2000);
  }, [getBaseUrl, toast]);

  const [rebuilding, setRebuilding] = useState(false);
  const rebuildPortalData = useCallback(async () => {
    // Client portals AND client logins read via two lists stored on each client
    // record — `placementIds` and `interviewingCandidateIds`. Both only get
    // appended to going forward; anything from before those features existed
    // isn't on them yet. This scans existing placements/candidates once and
    // backfills both lists so older clients' portals and interview queues
    // aren't empty.
    setRebuilding(true);
    try {
      const allPlacements = await DB.getAll('placements');
      const placementsByClient = {};
      allPlacements.forEach(p => { if (!p.clientId) return; (placementsByClient[p.clientId] ||= []).push(p.id); });

      const allCandidates = candidates && candidates.length ? candidates : await DB.getAll('candidates');
      const interviewingByClient = {};
      allCandidates.forEach(c => {
        if (c.stage==='Interviewing' && c.interviewClientId) (interviewingByClient[c.interviewClientId] ||= []).push(c.id);
      });

      let updated = 0;
      for (const cl of clients) {
        const foundPlacements = placementsByClient[cl.id] || [];
        const foundInterviewing = interviewingByClient[cl.id] || [];
        const mergedPlacements = [...new Set([...(cl.placementIds||[]), ...foundPlacements])];
        const mergedInterviewing = [...new Set([...(cl.interviewingCandidateIds||[]), ...foundInterviewing])];
        const changed = mergedPlacements.length !== (cl.placementIds||[]).length
                      || mergedInterviewing.length !== (cl.interviewingCandidateIds||[]).length;
        if (changed) {
          const rec = {...cl, placementIds: mergedPlacements, interviewingCandidateIds: mergedInterviewing};
          await DB.save('clients', cl.id, rec);
          setClients(p => p.map(c => c.id===cl.id ? rec : c));
          updated++;
        }
      }
      toast(updated>0 ? `Rebuilt portal data for ${updated} client(s) ✓` : 'Everything was already up to date ✓');
      if (addAct) addAct(`🔄 Rebuilt client portal data (${updated} client(s) updated)`);
    } catch (e) {
      toast('Something went wrong rebuilding portal data.');
    }
    setRebuilding(false);
  }, [clients, setClients, candidates, toast, addAct]);

  const doSave = useCallback(async (rec) => {
    await DB.save('clients', rec.id, rec);
    setClients(p => [...p, rec]);
    setForm({...CLBLANK});
    toast('Client saved ✓');
    addAct(`🏢 Added client "${rec.name}"`);
    syncClientToSheets(rec, profile?.sheetsUrl);
  }, [setClients, toast, addAct, profile]);

  const save = useCallback(async () => {
    if (!form.contact || !form.state) { toast('Client name and state required.'); return; }
    const dupe = form.phone ? checkDuplicate(clients, form.phone, null) : null;
    const rec = {...form, name: form.name || form.contact, id:uid(), addedAt:today(), addedAtTs:Date.now()};
    if (dupe) { setPendingDupe({rec, existing:dupe}); return; }
    await doSave(rec);
  }, [form, toast, clients, doSave]);

  const confirmDupeSave = useCallback(async () => {
    if (!pendingDupe) return;
    await doSave(pendingDupe.rec);
    setPendingDupe(null);
  }, [pendingDupe, doSave]);

  const del = useCallback(async (id) => {
    if (!window.confirm('Delete this client?')) return;
    const rec = clients.find(c=>c.id===id);
    await DB.delete('clients', id);
    setClients(p => p.filter(c => c.id !== id));
    toast('Deleted.');
    addAct(`🗑 Deleted client "${rec?.name||''}"`);
    deleteClientFromSheets(id, profile?.sheetsUrl);
  }, [toast, clients, setClients, addAct, profile]);

  const pendingClientSubs = (clientSubmissions||[]).filter(x=>(x.status||'New')==='New');
  const acceptClientSubmission = useCallback(async (sub) => {
    const dupe = sub.phone ? clients.find(c => normPhone(c.phone) && normPhone(c.phone)===normPhone(sub.phone)) : null;
    if (dupe && !window.confirm(`A client "${dupe.name}" already has this phone number. Add anyway?`)) return;
    const rec = {...CLBLANK, ...sub, id:uid(), placementIds:[], addedAt:today(), addedAtTs:Date.now()};
    delete rec.status;
    await DB.save('clients', rec.id, rec);
    setClients(p => [rec, ...p]);
    const upd = {...sub, status:'accepted', clientId:rec.id};
    await DB.save('clientSubmissions', upd.id, upd);
    if (setClientSubmissions) setClientSubmissions(p => p.map(x=>x.id===sub.id?upd:x));
    toast(`Added "${sub.name}" ✓ — review the details and set your placement fee.`);
    addAct(`📥 Accepted self-submitted client "${sub.name}"`);
    syncClientToSheets(rec, profile?.sheetsUrl);
    setEditing(rec.id); setEditForm({...rec});
  }, [clients, setClients, setClientSubmissions, toast, addAct, profile]);
  const dismissClientSubmission = useCallback(async (sub) => {
    if (!window.confirm(`Dismiss the request from "${sub.name}"?`)) return;
    const upd = {...sub, status:'dismissed'};
    await DB.save('clientSubmissions', upd.id, upd);
    if (setClientSubmissions) setClientSubmissions(p => p.map(x=>x.id===sub.id?upd:x));
    toast('Dismissed.');
  }, [setClientSubmissions, toast]);
  const copySignupLink = useCallback(() => {
    const base = getBaseUrl();
    if (!base) { toast('⚠ Set your Public web app URL in My Business first.'); return; }
    navigator.clipboard?.writeText(`${base}/client-signup`);
    toast('Client signup link copied ✓');
  }, [getBaseUrl, toast]);

  const startEdit = useCallback((c) => { setEditing(c.id); setEditForm({...c}); }, []);
  const cancelEdit = useCallback(() => setEditing(null), []);
  const saveEdit = useCallback(async () => {
    const rec = {...editForm, name: editForm.name || editForm.contact};
    await DB.save('clients', rec.id, rec);
    setClients(p => p.map(c => c.id === editing ? rec : c));
    setEditing(null); toast('Updated ✓');
    addAct(`✏️ Updated client "${rec.name}"`);
    syncClientToSheets(rec, profile?.sheetsUrl);
  }, [editForm, editing, toast, setClients, addAct, profile]);

  const doImport = useCallback(async (rows) => {
    let count = 0;
    for (const row of rows) {
      if (!row.contact || !row.state) continue;
      const rec = {...CLBLANK, ...row, name: row.name || row.contact, id: uid(), addedAt: today(), addedAtTs: Date.now(), skipAutoWa: true};
      await DB.save('clients', rec.id, rec);
      count++;
      setClients(p => [...p, rec]);
      syncClientToSheets(rec, profile?.sheetsUrl);
    }
    setShowImport(false);
    toast(`Imported ${count} client(s) ✓`);
    addAct(`📥 Bulk imported ${count} client(s)`);
  }, [setClients, toast, addAct, profile]);

  const filtered = clients.filter(c => {
    const s = q.toLowerCase();
    return !q || (c.name||'').toLowerCase().includes(s) || (c.state||'').toLowerCase().includes(s) ||
      (c.city||'').toLowerCase().includes(s) || (c.industry||'').toLowerCase().includes(s);
  });
  const dupeCount = findDuplicateGroups(clients).length;

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={clients.length} label="Total clients"/>
        <StatCard n={clients.reduce((a,c)=>a+(parseInt(c.openings)||0),0)} label="Open positions"/>
        <StatCard n={clients.filter(c=>c.carNeeded==='Yes').length} label="Need car"/>
        <StatCard n={clients.filter(c=>c.legalOnly==='Yes').length} label="Legal only"/>
      </div>
      {pendingClientSubs.length>0 && (
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>📥 New client requests ({pendingClientSubs.length})</div>
          {[...pendingClientSubs].sort((a,b)=>(b.submittedAt||'').localeCompare(a.submittedAt||'')).map(sub => (
            <div key={sub.id} style={{...S.li, background:'#E6F1FB', border:'0.5px solid #c7dcf0', alignItems:'flex-start'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{sub.name} <span style={{fontWeight:400,color:'#666'}}>· {sub.contact}</span></div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{sub.phone}{sub.email?' · '+sub.email:''}{sub.industry?' · '+sub.industry:''}{sub.city||sub.state?' · '+[sub.city,sub.state].filter(Boolean).join(', '):''}</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
                  <span style={S.tG}>{sub.openings} needed</span>
                  {sub.payType==='fixed' ? (sub.fixedPay&&<span style={S.tGr}>${sub.fixedPay}/{(sub.fixedPeriod||'Monthly').toLowerCase()}</span>) : (sub.wage&&<span style={S.tGr}>${sub.wage}/hr</span>)}
                  {sub.hours&&<span style={S.tB}>🕒 {sub.hours}</span>}
                  {sub.shift&&<span style={S.tB}>🌗 {sub.shift}</span>}
                  {sub.carNeeded==='Yes'&&<span style={S.tA}>🚗 Car</span>}
                  {sub.legalOnly==='Yes'&&<span style={S.tB}>✅ Legal only</span>}
                  {sub.cashPay==='Yes'&&<span style={S.tG}>💵 Cash</span>}
                  {sub.accommodation==='Yes'&&<span style={S.tB}>🏠 Housing</span>}
                </div>
                {(sub.skills||sub.jobDesc||sub.notes) && <div style={{fontSize:11.5,color:'#555',marginTop:5,whiteSpace:'pre-wrap'}}>{[sub.skills&&`Skills: ${sub.skills}`, sub.jobDesc, sub.notes].filter(Boolean).join('\n')}</div>}
                <div style={{fontSize:11,color:'#0C447C',marginTop:3}}>Self-submitted · {fmtDateTime(sub.submittedAt)}</div>
              </div>
              <div style={{display:'flex',gap:5,flexShrink:0,flexWrap:'wrap'}}>
                <Btn sm onClick={()=>acceptClientSubmission(sub)}>✅ Add client</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>dismissClientSubmission(sub)}>Dismiss</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={S.card}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:3}}>Add client</div>
        <div style={{fontSize:12,color:'#888',marginBottom:14}}>🔒 Admin only — employees cannot see this tab</div>
        <ClientForm value={form} onChange={setForm} onSave={save} saveLabel="Save client"/>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <Btn sm onClick={copySignupLink}>🔗 Copy client signup link</Btn>
        <Btn sm onClick={()=>setShowImport(true)}>📥 Bulk import (CSV)</Btn>
        <Btn sm onClick={()=>setShowDupes(true)}>🔍 Scan duplicates{dupeCount>0?` (${dupeCount})`:''}</Btn>
        <Btn sm onClick={rebuildPortalData} disabled={rebuilding}>{rebuilding?'Rebuilding...':'🔄 Rebuild portal data'}</Btn>
      </div>
      <div style={{fontSize:11,color:'#aaa',marginBottom:14,marginTop:-8}}>If a client portal or client login shows no placements or ongoing interviews, click "Rebuild portal data" once — it backfills anything made before these features existed.</div>
      <SBar value={q} onChange={setQ} placeholder="Search clients..."/>
      {filtered.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>🏢</div>{q?'No match.':'No clients yet.'}</div>}
      {filtered.map(rec => (
        <div key={rec.id}>
          {editing === rec.id ? (
            <div style={S.card}>
              <div style={{fontSize:14,fontWeight:600,marginBottom:12}}>Editing: {rec.name}</div>
              <ClientForm value={editForm} onChange={setEditForm} onSave={saveEdit} onCancel={cancelEdit} saveLabel="Save changes"/>
            </div>
          ) : (
            <div style={S.li}>
              <div style={S.avC}>{initials(rec.contact||rec.name)}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{rec.contact||rec.name}{rec.contact&&rec.name&&rec.name!==rec.contact?<span style={{fontWeight:400,color:'#888',fontSize:12}}> · {rec.name}</span>:null}</div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{rec.city&&rec.state?`${rec.city}, ${rec.state}`:rec.state||'No location'} · {rec.industry||'No industry'} · {rec.openings} opening(s)</div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{rec.phone||'No phone'}{rec.email?' · '+rec.email:''}</div>
                <div style={{fontSize:11,color:'#999',marginTop:1}}>{(()=>{const a=addedInfo(rec);return a.text?`${a.label} ${a.text}`:'';})()}</div>
                {rec.skills && <div style={{fontSize:12,color:'#666',marginTop:1}}>Skills needed: {rec.skills}</div>}
                <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
                  {rec.carNeeded==='Yes'&&<span style={S.tA}>🚗 Car needed</span>}
                  {rec.legalOnly==='Yes'&&<span style={S.tB}>✅ Legal only</span>}
                  <span style={S.tG}>Fee: ${rec.placementFee||500}</span>
                  {parseFloat(rec.discPerUnit||0)>0&&<span style={S.tR}>-${rec.discPerUnit}/unit</span>}
                  {rec.payType==='fixed' ? (rec.fixedPay&&<span style={S.tGr}>${rec.fixedPay}/{(rec.fixedPeriod||'Monthly').toLowerCase()}</span>) : (rec.wage&&<span style={S.tGr}>${rec.wage}/hr</span>)}
                  {rec.cashPay==='Yes'&&<span style={S.tG}>💵 Cash pay</span>}
                  {rec.accommodation==='Yes'&&<span style={S.tB}>🏠 Accommodation</span>}
                  {rec.followUpDate&&<span style={S.tA}>⏰ Follow-up: {rec.followUpDate}</span>}
                  {clientLogins.some(l=>l.clientId===rec.id)&&<span style={S.tG}>🔑 Has login</span>}
                </div>
              </div>
              <div style={{display:'flex',gap:5,flexShrink:0,flexWrap:'wrap',maxWidth:220,justifyContent:'flex-end'}}>
                <Btn sm onClick={()=>setContractFor(rec)}>{clientAgreements.some(a=>a.clientId===rec.id)?'✅ Contract':'📜 Contract'}</Btn>
                <Btn sm onClick={()=>copyPortalLink(rec)}>{linkCopied===rec.id?'✓ Copied':'🔗 Portal link'}</Btn>
                {(() => {
                  const login = clientLogins.find(l=>l.clientId===rec.id);
                  return login ? (
                    <>
                      <Btn sm onClick={()=>resetClientPassword(login)}>📧 Reset password</Btn>
                      <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>deleteClientLogin(login, rec.name)}>🗑 Remove login</Btn>
                    </>
                  ) : (
                    <Btn sm onClick={()=>openCreateLogin(rec)}>🔑 Client login</Btn>
                  );
                })()}
                <Btn sm onClick={()=>startEdit(rec)}>✏️ Edit</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>del(rec.id)}>🗑</Btn>
              </div>
            </div>
          )}
        </div>
      ))}
      {showDupes && <DuplicateModal list={clients} labelKey="name" onClose={()=>setShowDupes(false)}/>}
      {showImport && <BulkImportModal fields={['name','contact','phone','email','industry','city','state','openings','payType','wage','fixedPay','fixedPeriod','skills','carNeeded','legalOnly','cashPay','accommodation','placementFee','notes']} onImport={doImport} onClose={()=>setShowImport(false)}/>}
      {pendingDupe && (
        <Modal onClose={()=>setPendingDupe(null)} maxWidth={420}>
          <div style={{fontSize:15,fontWeight:700,marginBottom:8}}>⚠ Possible duplicate</div>
          <div style={{fontSize:13,color:'#555',marginBottom:14}}>A client named <strong>{pendingDupe.existing.name}</strong> already has phone number <strong>{pendingDupe.existing.phone}</strong>. Save anyway?</div>
          <div style={{display:'flex',gap:8}}>
            <Btn prim onClick={confirmDupeSave}>Save anyway</Btn>
            <Btn onClick={()=>setPendingDupe(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
      {contractFor && (
        <ClientAgreementModal client={contractFor} profile={profile}
          existingAgreement={clientAgreements.find(a=>a.clientId===contractFor.id)}
          onClose={()=>setContractFor(null)} addAct={addAct}
          onSigned={(rec)=>setClientAgreements(cs => cs.some(a=>a.id===rec.id) ? cs.map(a=>a.id===rec.id?rec:a) : [...cs, rec])}/>
      )}
      {loginFor && (
        <Modal onClose={()=>setLoginFor(null)} maxWidth={420}>
          <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>🔑 Create login for {loginFor.name}</div>
          <div style={{fontSize:12,color:'#888',marginBottom:14}}>They'll be able to log in and see their ongoing interviews, mark candidates placed or rejected, and view their invoices — nothing belonging to any other client.</div>
          <FG label="Email"><Inp value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} placeholder="owner@business.com"/></FG>
          <FG label="Password" style={{marginTop:10}}><Inp type="text" value={loginPass} onChange={e=>setLoginPass(e.target.value)} placeholder="6+ characters"/></FG>
          {loginErr && <div style={{background:'#FCEBEB',borderRadius:7,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginTop:10}}>⚠ {loginErr}</div>}
          <div style={{display:'flex',gap:8,marginTop:14}}>
            <Btn prim onClick={createClientLogin} disabled={loginBusy}>{loginBusy?'Creating...':'Create login'}</Btn>
            <Btn onClick={()=>setLoginFor(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MATCH TAB
// ═══════════════════════════════════════════════════════════════════════════════
function MatchTab({candidates:allCandidates, clients, setClients, placements, setPlacements, toast, isAdmin, setCandidates, addAct, referralPartners, setReferralPayables, user}) {
  const candidates = isAdmin===false ? allCandidates.filter(c=>c.addedBy===user?.uid) : allCandidates;
  const [mode, setMode] = useState('auto');
  const [selClient, setSelClient] = useState('');
  const [qAuto, setQAuto] = useState('');
  const [manClient, setManClient] = useState('');
  const [manCand, setManCand] = useState('');
  const [qMC, setQMC] = useState('');
  const [qMK, setQMK] = useState('');

  const place = useCallback(async (candidateId, clientId) => {
    const client = clients.find(c=>c.id===clientId);
    const cand = candidates.find(c=>c.id===candidateId);
    const pl = await createPlacement({cand, client, placements, setPlacements, setCandidates, setClients, referralPartners, setReferralPayables, addAct, toast});
    if (pl) { setManClient(''); setManCand(''); }
  }, [candidates, clients, setClients, placements, toast, setCandidates, addAct, referralPartners, setReferralPayables, setPlacements]);

  const scheduleInterview = useCallback(async (candidateId, clientId) => {
    const client = clients.find(c=>c.id===clientId);
    const cand = candidates.find(c=>c.id===candidateId);
    if (!client||!cand||!setCandidates) return;
    await scheduleCandidateInterview({cand, client, setCandidates, setClients, addAct, toast});
  }, [candidates, clients, setClients, setCandidates, toast, addAct]);

  const cancelInterview = useCallback(async (candidateId) => {
    const cand = candidates.find(c=>c.id===candidateId);
    const client = cand ? clients.find(c=>c.id===cand.interviewClientId) : null;
    if (!cand||!setCandidates) return;
    await rejectCandidateInterview({cand, client, setCandidates, setClients, addAct, toast});
  }, [candidates, clients, setClients, setCandidates, toast, addAct]);

  const confirmFromInterview = useCallback(async (candidateId) => {
    const cand = candidates.find(c=>c.id===candidateId);
    const client = cand && clients.find(c=>c.id===cand.interviewClientId);
    const pl = await createPlacement({cand, client, placements, setPlacements, setCandidates, setClients, referralPartners, setReferralPayables, addAct, toast});
    return pl;
  }, [candidates, clients, setClients, placements, setPlacements, setCandidates, referralPartners, setReferralPayables, addAct, toast]);

  const activeInterviews = candidates.filter(c => c.stage === 'Interviewing');

  const autoClient = clients.find(c=>c.id===selClient);
  const autoClientSlots = autoClient ? clientOpenSlots(autoClient, candidates, placements) : 0;
  const autoMatches = autoClient
    ? [...candidates].filter(c => { if(!qAuto)return true; const s=qAuto.toLowerCase(); return (c.name||'').toLowerCase().includes(s)||(c.skills||[]).some(sk=>sk.toLowerCase().includes(s)); })
        .map(c => ({...c, score:matchScore(c,autoClient)})).sort((a,b)=>b.score-a.score)
    : [];
  const filtCl = clients.filter(c => { const s=qMC.toLowerCase(); return !qMC||(c.name||'').toLowerCase().includes(s)||(c.state||'').toLowerCase().includes(s); });
  const filtCa = candidates.filter(c => { const s=qMK.toLowerCase(); return !qMK||(c.name||'').toLowerCase().includes(s)||(c.industry||'').toLowerCase().includes(s); });
  const manCO = clients.find(c=>c.id===manClient);
  const manKO = candidates.find(c=>c.id===manCand);

  return (
    <div>
      {activeInterviews.length>0 && (
        <div style={{background:'#FAEEDA',border:'0.5px solid #f0dcb0',borderRadius:10,padding:'12px 14px',marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:'#7a5a10',marginBottom:8}}>🗓 Active interviews ({activeInterviews.length})</div>
          {activeInterviews.map(iv => (
            <div key={iv.id} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:'0.5px solid #f0dcb0'}}>
              <div style={{flex:1,minWidth:0}}>
                <span style={{fontWeight:600,fontSize:13}}>{iv.name}</span>
                <span style={{fontSize:12,color:'#7a5a10'}}> — interviewing at {iv.interviewClientName||'—'}</span>
              </div>
              <Btn sm onClick={()=>confirmFromInterview(iv.id)}>✅ Mark placed</Btn>
              <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>cancelInterview(iv.id)}>↩ Cancel</Btn>
            </div>
          ))}
        </div>
      )}
      <div style={{display:'flex',gap:8,marginBottom:16}}>
        <Btn prim={mode==='auto'} onClick={()=>setMode('auto')}>⚡ Auto match</Btn>
        <Btn prim={mode==='manual'} onClick={()=>setMode('manual')}>🖐 Manual match</Btn>
      </div>
      {mode==='auto' && (
        <div>
          <div style={{fontSize:12,color:'#888',marginBottom:12}}>Select a client — candidates ranked by AI score.</div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
            {clients.length===0 ? <div style={{fontSize:13,color:'#aaa'}}>No clients yet{!isAdmin?' — contact your admin':' — add one in Clients tab'}.</div>
            : clients.map(cl => {
              const slots = clientOpenSlots(cl, candidates, placements);
              return (
                <div key={cl.id} onClick={()=>{setSelClient(cl.id);setQAuto('');}} style={{padding:'6px 12px',borderRadius:8,cursor:'pointer',fontSize:13,border:`0.5px solid ${selClient===cl.id?bl:'#ccc'}`,background:selClient===cl.id?'#E6F1FB':'#fff',fontWeight:selClient===cl.id?600:400,color:selClient===cl.id?'#0C447C':tx,opacity:slots<=0?0.55:1}}>
                  {cl.name} <span style={{fontSize:10,opacity:.6}}>{cl.state}</span>{slots<=0&&<span style={{fontSize:10,color:rd,marginLeft:4}}>FULL</span>}
                </div>
              );
            })}
          </div>
          {autoClient && (
            <div>
              <div style={{background:'#f0f4f8',borderRadius:8,padding:'9px 13px',marginBottom:12,fontSize:12,color:'#444',display:'flex',flexWrap:'wrap',gap:8}}>
                <span><strong>{autoClient.name}</strong> · {autoClient.industry}</span>
                <span>Needs: <strong>{autoClient.skills||'Any'}</strong></span>
                {isAdmin && <span style={{color:gr,fontWeight:600}}>Fee: ${autoClient.placementFee||500}</span>}
                {autoClient.carNeeded==='Yes' && <span style={S.tA}>🚗 Car req.</span>}
                {autoClient.legalOnly==='Yes' && <span style={S.tB}>✅ Legal only</span>}
                <span style={{fontWeight:600,color:autoClientSlots<=0?rd:gr}}>{autoClientSlots<=0?'No open slots':`${autoClientSlots} open slot${autoClientSlots===1?'':'s'}`}</span>
              </div>
              {autoClientSlots<=0 && <div style={{background:'#FCEBEB',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#A32D2D',marginBottom:12}}>⚠ This client's openings are all filled by actual placements. New matches are blocked until a slot frees up — interviewing more candidates here won't affect this count.</div>}
              <SBar value={qAuto} onChange={setQAuto} placeholder="Filter candidates..."/>
              {autoMatches.length===0 && <div style={{textAlign:'center',padding:30,color:'#aaa'}}>No candidates yet.</div>}
              {autoMatches.map(rec => {
                const placed = placements.some(p=>p.candidateId===rec.id&&p.clientId===autoClient.id);
                const interviewingElsewhere = rec.stage==='Interviewing' && rec.interviewClientId && rec.interviewClientId!==autoClient.id;
                const interviewingHere = rec.stage==='Interviewing' && rec.interviewClientId===autoClient.id;
                const clientFull = autoClientSlots<=0 && !interviewingHere && !placed;
                const sc = rec.score>=65?{background:'#EAF3DE',color:'#27500A'}:rec.score>=35?{background:'#FAEEDA',color:'#633806'}:{background:'#FCEBEB',color:'#A32D2D'};
                return (
                  <div key={rec.id} style={S.li}>
                    <div style={{width:48,height:48,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,flexShrink:0,...sc}}>{rec.score}%</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:14}}>{rec.name}</div>
                      <div style={{fontSize:12,color:'#666',marginTop:1}}>{rec.visa||'Visa?'} · {rec.availability} · {rec.industry||'—'}{rec.experience?` · ${rec.experience}yr`:''}</div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
                        {(rec.skills||[]).slice(0,4).map(s=><span key={s} style={S.tB}>{s}</span>)}
                        {rec.hasCar==='Yes'&&<span style={S.tT}>🚗</span>}
                        {(parseInt(rec.members,10)||1)>1&&<span style={S.tGr}>{rec.members} members</span>}
                        {autoClient.legalOnly==='Yes'&&(rec.visa==='US Citizen'||rec.visa==='Green Card')&&<span style={S.tG}>✅ Legal</span>}
                        {autoClient.legalOnly==='Yes'&&rec.visa!=='US Citizen'&&rec.visa!=='Green Card'&&<span style={S.tR}>⚠ Not legal</span>}
                      </div>
                    </div>
                    {placed ? <span style={S.tG}>✓ Placed</span> : interviewingElsewhere ? (
                      <span style={S.tR}>🔒 Interviewing at {rec.interviewClientName}</span>
                    ) : clientFull ? (
                      <span style={S.tR}>🔒 Client full</span>
                    ) : (
                      <div style={{display:'flex',gap:6,flexShrink:0}}>
                        {interviewingHere && <span style={S.tA}>🗓 Interviewing here</span>}
                        {!interviewingHere && <Btn sm onClick={()=>scheduleInterview(rec.id,autoClient.id)}>🗓 Interview</Btn>}
                        <Btn grn style={{fontSize:12,padding:'5px 11px'}} onClick={()=>place(rec.id,autoClient.id)}>Place →</Btn>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {!autoClient && clients.length>0 && <div style={{textAlign:'center',padding:40,color:'#aaa'}}>↑ Select a client above</div>}
        </div>
      )}
      {mode==='manual' && (
        <div>
          <div style={{fontSize:12,color:'#888',marginBottom:14}}>Pick any client and candidate manually.</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            {[{title:'🏢 Select client',q:qMC,setQ:setQMC,items:filtCl,sel:manClient,setSel:setManClient,lbl:x=>`${x.name} ${x.state}`},
              {title:'👤 Select candidate',q:qMK,setQ:setQMK,items:filtCa,sel:manCand,setSel:setManCand,lbl:x=>`${x.stage==='Interviewing'&&x.interviewClientId?'🔒 ':''}${x.name} ${x.visa||''}`}
            ].map(({title,q:qq,setQ:sQ,items,sel,setSel,lbl}) => (
              <div key={title} style={{background:'#f8f8f4',border:`0.5px solid ${br}`,borderRadius:10,padding:12}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',color:'#666',marginBottom:8}}>{title}</div>
                <input value={qq} onChange={e=>sQ(e.target.value)} placeholder="Search..." style={{...S.inp,marginBottom:7,fontSize:12.5,padding:'5px 8px'}}/>
                <div style={{maxHeight:220,overflowY:'auto'}}>
                  {items.map(item => (
                    <div key={item.id} onClick={()=>setSel(item.id)} style={{padding:'7px 10px',border:`0.5px solid ${sel===item.id?bl:br}`,borderRadius:7,marginBottom:5,cursor:'pointer',background:sel===item.id?'#E6F1FB':'#fff',fontWeight:sel===item.id?600:400,fontSize:13,display:'flex',justifyContent:'space-between'}}>
                      <span>{lbl(item)}</span>
                      {sel===item.id && <span style={{color:bl}}>✓</span>}
                    </div>
                  ))}
                  {items.length===0 && <div style={{fontSize:12,color:'#aaa',padding:'8px 4px'}}>None found.</div>}
                </div>
              </div>
            ))}
          </div>
          <div style={{marginTop:14,background:'#f8f8f4',borderRadius:10,padding:14,border:`0.5px solid ${br}`}}>
            <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',color:'#666',marginBottom:10}}>Preview</div>
            <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              {manCO ? <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:8,padding:'8px 12px',fontSize:13}}><div style={{fontWeight:600}}>{manCO.name}</div><div style={{fontSize:11,color:'#888'}}>{manCO.state}</div>{isAdmin&&<div style={{fontSize:11,color:gr,fontWeight:600}}>Fee: ${manCO.placementFee||500}</div>}</div> : <div style={{fontSize:13,color:'#aaa',padding:'8px 12px'}}>← Pick a client</div>}
              <div style={{fontSize:22,color:'#ccc'}}>→</div>
              {manKO ? <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:8,padding:'8px 12px',fontSize:13}}><div style={{fontWeight:600}}>{manKO.name}</div><div style={{fontSize:11,color:'#888'}}>{manKO.visa||''} · {manKO.availability}</div></div> : <div style={{fontSize:13,color:'#aaa',padding:'8px 12px'}}>← Pick a candidate</div>}
            </div>
            <div style={{marginTop:12,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              {manClient&&manCand&&placements.some(p=>p.candidateId===manCand&&p.clientId===manClient)
                ? <span style={S.tG}>✓ Already placed</span>
                : manKO && manKO.stage==='Interviewing' && manKO.interviewClientId && manKO.interviewClientId!==manClient ? (
                  <span style={S.tR}>🔒 {manKO.name} is already interviewing at {manKO.interviewClientName} — can't be matched elsewhere until that's resolved.</span>
                ) : manCO && clientOpenSlots(manCO, candidates, placements)<=0 && !(manKO&&manKO.interviewClientId===manClient) ? (
                  <span style={S.tR}>🔒 {manCO.name} has no open slots left.</span>
                ) : (
                  <>
                    <Btn disabled={!manClient||!manCand} onClick={()=>scheduleInterview(manCand,manClient)}>🗓 Schedule interview</Btn>
                    <Btn grn disabled={!manClient||!manCand} onClick={()=>place(manCand,manClient)}>✅ Confirm placement</Btn>
                  </>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAP TAB
// ═══════════════════════════════════════════════════════════════════════════════
const PIN_COLORS = ['#185FA5','#E24B4A','#2d6a4f','#B8860B','#7B3FA0','#0891B2','#C2410C','#4338CA','#BE185D','#15803D'];
// One color per business TYPE (industry), not per individual business — so all gas
// stations share a color, all motels share another, etc.
const INDUSTRY_COLORS = Object.fromEntries(INDUSTRIES.map((ind,i) => [ind, PIN_COLORS[i % PIN_COLORS.length]]));
function pinColor(industry){ return INDUSTRY_COLORS[industry] || '#8a8a82'; }

function MapTab({clients, candidates, placements}) {
  const [tip, setTip] = useState(null);
  // Clients with no open slots left aren't actively hiring, so leave them off the
  // map. Ongoing interviews don't close a position — only an actual placement does.
  const openClients = (clients||[]).filter(cl => clientOpenSlots(cl, candidates||[], placements||[]) > 0);
  const byState = {};
  openClients.forEach(cl => { const k=(cl.state||'').toUpperCase().trim(); if(!byState[k])byState[k]=[]; byState[k].push(cl); });
  return (
    <div>
      <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Client map</div>
      <div style={{fontSize:12,color:'#888',marginBottom:12}}>{openClients.length} client(s) with open positions, across {Object.keys(byState).filter(k=>byState[k]?.length>0).length} state(s) — each business has its own color · hover a state for details</div>
      <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:12,overflow:'hidden',position:'relative',height:480}}>
        <svg viewBox="75 90 510 330" style={{width:'100%',height:'100%'}} onMouseLeave={()=>setTip(null)}>
          {Object.entries(STATE_PATHS).map(([abbr,{d,cx,cy}]) => {
            const list=byState[abbr]||[]; const has=list.length>0;
            return (
              <g key={abbr}>
                <path d={d} fill={has?'#7d97ad':'#D3D1C7'} stroke="#fff" strokeWidth={0.8} opacity={has?.88:.55} style={{cursor:has?'pointer':'default'}} onMouseEnter={()=>{if(has)setTip({cx,cy,abbr,list});}} onMouseLeave={()=>setTip(null)}/>
                <text x={cx} y={cy+4} textAnchor="middle" fontSize={6.5} fill={has?'#fff':'#777'} style={{pointerEvents:'none',userSelect:'none',fontWeight:has?700:400}}>{abbr}</text>
                {list.slice(0,6).map((cl,i) => {
                  const angle = (i / Math.max(1,Math.min(list.length,6))) * Math.PI * 2;
                  const px = cx + 8 + Math.cos(angle)*5;
                  const py = cy - 8 + Math.sin(angle)*5;
                  return <circle key={cl.id} cx={px} cy={py} r={3.2} fill={pinColor(cl.industry)} stroke="#fff" strokeWidth={0.7} style={{pointerEvents:'none'}}/>;
                })}
              </g>
            );
          })}
          {tip && (
            <foreignObject x={Math.min(tip.cx-70,505)} y={Math.max(tip.cy-110,90)} width={155} height={Math.min(130,28+tip.list.length*18)}>
              <div style={{background:'#1a1a18',color:'#fff',padding:'8px 11px',borderRadius:7,fontSize:11,lineHeight:1.7}}>
                <div style={{fontWeight:700,marginBottom:3}}>{stateName(tip.abbr)} — {tip.list.length} client(s)</div>
                {tip.list.slice(0,6).map(cl=>(
                  <div key={cl.id} style={{display:'flex',alignItems:'center',gap:5,opacity:.9}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:pinColor(cl.industry),flexShrink:0}}/>
                    <span>{cl.name}{cl.industry?` — ${cl.industry}`:''}</span>
                  </div>
                ))}
              </div>
            </foreignObject>
          )}
        </svg>
        <div style={{position:'absolute',bottom:10,left:10,background:'rgba(255,255,255,.95)',border:`0.5px solid ${br}`,borderRadius:7,padding:'7px 11px',fontSize:11,color:'#666'}}>
          <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:2}}><div style={{width:10,height:10,borderRadius:2,background:'#7d97ad'}}/> Has open clients</div>
          <div style={{display:'flex',alignItems:'center',gap:5}}><div style={{width:10,height:10,borderRadius:2,background:'#D3D1C7'}}/> None open</div>
        </div>
      </div>
      {openClients.length>0 && (() => {
        const industriesPresent = [...new Set(openClients.map(cl=>cl.industry).filter(Boolean))];
        const noIndustryCount = openClients.filter(cl=>!cl.industry).length;
        return (
          <div style={{marginTop:14}}>
            <div style={{fontSize:12,fontWeight:700,color:'#666',marginBottom:8}}>🎨 What each pin color means (by business type)</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:8}}>
              {industriesPresent.map(ind => {
                const count = openClients.filter(cl=>cl.industry===ind).length;
                return (
                  <div key={ind} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#fff',border:`0.5px solid ${br}`,borderRadius:7}}>
                    <span style={{width:11,height:11,borderRadius:'50%',background:pinColor(ind),flexShrink:0,border:'1px solid #fff',boxShadow:'0 0 0 1px rgba(0,0,0,.1)'}}/>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:12.5,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ind}</div>
                      <div style={{fontSize:10.5,color:'#888'}}>{count} business{count===1?'':'es'}</div>
                    </div>
                  </div>
                );
              })}
              {noIndustryCount>0 && (
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#fff',border:`0.5px solid ${br}`,borderRadius:7}}>
                  <span style={{width:11,height:11,borderRadius:'50%',background:'#8a8a82',flexShrink:0,border:'1px solid #fff',boxShadow:'0 0 0 1px rgba(0,0,0,.1)'}}/>
                  <div style={{fontSize:12.5,fontWeight:600}}>No industry set <span style={{fontWeight:400,color:'#888'}}>({noIndustryCount})</span></div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLACEMENTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function PlainShell({children}){ return <div style={{maxWidth:680,margin:'0 auto',background:'#fff',borderRadius:12,padding:'22px 24px',border:`0.5px solid ${br}`}}>{children}</div>; }
function InvModal({pl, client, profile, onClose, onUpdate, onDelete, readOnly}) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState(pl.items||[]);
  const [paid, setPaid] = useState(pl.alreadyPaid||'');
  const gross = items.reduce((a,i)=>(parseFloat(i.rate)||0)*(parseInt(i.qty)||1)+a,0);
  const discT = items.reduce((a,i)=>(parseFloat(i.discPerUnit)||0)*(parseInt(i.qty)||1)+a,0);
  const invTotal = gross-discT;
  const balance = invTotal-parseFloat(paid||0);
  const p = {...DEFAULT_PROFILE,...profile};
  const taxChips = [p.gst?`GST: ${p.gst}`:'',p.eic?`EIC: ${p.eic}`:'',p.ein?`EIN: ${p.ein}`:'',p.pan?`PAN: ${p.pan}`:''].filter(Boolean);
  const payStatus = balance<=0.01 ? 'PAID' : (parseFloat(paid||0)>0 ? 'PARTIALLY PAID' : 'UNPAID');
  const payColor = payStatus==='PAID' ? '#3B6D11' : (payStatus==='UNPAID' ? '#A32D2D' : '#b07a10');
  const clientAddr = [client.address, client.city, client.state].filter(Boolean).join(', ');
  const fromAddr = [p.address, p.city, p.country].filter(Boolean).join(', ');
  const candLine = [pl.candidateIndustry, pl.candidateVisa, pl.candidateExperience?`${pl.candidateExperience} yrs exp`:''].filter(Boolean).join(' · ');
  const saveEdit = useCallback(() => onUpdate({...pl,items,alreadyPaid:paid}), [pl,items,paid,onUpdate]);

  const invoiceHTML = () => `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;">
      <div>${p.logo?`<img src="${p.logo}" style="max-height:50px;max-width:150px;object-fit:contain;"/>`:`<div style="font-size:18px;font-weight:900;color:${bl};">${(p.companyName||'').replace(/</g,'&lt;')}</div>`}
        <div style="font-size:10.5px;color:#555;margin-top:5px;line-height:1.7;"><div>${p.city||''}${p.country?', '+p.country:''}</div><div>${p.email||''}${p.phone?' · '+p.phone:''}</div></div>
      </div>
      <div style="text-align:right;"><div style="font-size:28px;font-weight:900;color:${bl};letter-spacing:1px;">INVOICE</div><div style="font-size:11px;color:#555;margin-top:4px;line-height:1.8;"><div># ${pl.invNum}</div><div>Date: ${pl.date}</div><div>Due: ${pl.dueDate||pl.date}</div><div style="margin-top:6px;display:inline-block;border:1.5px solid ${payColor};color:${payColor};border-radius:4px;padding:2px 10px;font-size:11px;font-weight:800;letter-spacing:.6px;">${payStatus}</div></div></div>
    </div>
    ${taxChips.length>0?`<div style="display:flex;gap:6px;margin:8px 0 14px;">${taxChips.map(ch=>`<div style="border:1px solid #c0c8d0;border-radius:4px;padding:2px 8px;font-size:10px;font-weight:600;color:#444;">${ch}</div>`).join('')}</div>`:''}
    <div style="height:0.5px;background:#ccc;margin:4px 0 10px;"></div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin:10px 0;">
      <div><div style="font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:${bl};margin-bottom:3px;">FROM</div><div style="font-weight:700;">${(p.companyName||'').replace(/</g,'&lt;')}</div><div style="font-size:11px;color:#555;line-height:1.6;">${(fromAddr||'').replace(/</g,'&lt;')}</div><div style="font-size:11px;color:#555;">${p.email||''}${p.phone?' · '+p.phone:''}</div>${p.website?`<div style="font-size:11px;color:#555;">${p.website}</div>`:''}</div>
      <div><div style="font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:${bl};margin-bottom:3px;">BILL TO</div><div style="font-weight:700;">${(client.name||'').replace(/</g,'&lt;')}</div><div style="font-size:11px;color:#555;">${(client.contact||'').replace(/</g,'&lt;')}</div><div style="font-size:11px;color:#555;line-height:1.6;">${(clientAddr||'').replace(/</g,'&lt;')}</div>${client.phone?`<div style="font-size:11px;color:#555;">${client.phone}</div>`:''}${client.email?`<div style="font-size:11px;color:#555;">${client.email}</div>`:''}<div style="font-size:11px;color:#555;">Client #: CLT-39S-${(client.id||'0000').slice(-4).toUpperCase()}</div></div>
    </div>
    <div style="height:0.5px;background:#ccc;margin:4px 0 10px;"></div>
    ${pl.candidateName?`<div style="background:#f7f9fb;border:0.5px solid #dde3ea;border-radius:6px;padding:8px 12px;margin:0 0 10px;font-size:11px;display:flex;gap:10px;align-items:center;">${pl.candidatePhoto?`<img src="${pl.candidatePhoto}" style="width:34px;height:34px;border-radius:50%;object-fit:cover;"/>`:''}<div><div style="font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:${bl};">Candidate placed</div><div style="font-weight:700;font-size:12px;">${(pl.candidateName||'').replace(/</g,'&lt;')}${pl.headcount>1?` (+${pl.headcount-1} more)`:''}</div>${candLine?`<div style="color:#555;">${candLine.replace(/</g,'&lt;')}</div>`:''}<div style="color:#555;">Placement date: ${pl.date}</div></div></div>`:''}
    <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
      <thead><tr style="background:${bl};color:#fff;">${['Description','QTY','Rate','Disc/Unit','Amount'].map(h=>`<th style="padding:7px 10px;text-align:${h==='Description'?'left':'center'};font-size:9.5px;text-transform:uppercase;letter-spacing:.5px;">${h}</th>`).join('')}</tr></thead>
      <tbody>${items.map(it=>{const lg=(parseFloat(it.rate)||0)*(parseInt(it.qty)||1);const ld=(parseFloat(it.discPerUnit)||0)*(parseInt(it.qty)||1);return `<tr style="border-bottom:0.5px solid #eee;"><td style="padding:8px 10px;">${(it.desc||'—').replace(/</g,'&lt;')}</td><td style="padding:8px 10px;text-align:center;">${it.qty||1}</td><td style="padding:8px 10px;text-align:center;">${fmtUSD(it.rate)}</td><td style="padding:8px 10px;text-align:right;color:${parseFloat(it.discPerUnit||0)>0?rd:'#aaa'};">${parseFloat(it.discPerUnit||0)>0?`-${fmtUSD(it.discPerUnit)}/unit`:'—'}</td><td style="padding:8px 10px;text-align:right;font-weight:600;">${fmtUSD(lg-ld)}</td></tr>`;}).join('')}</tbody>
    </table>
    <div style="display:flex;justify-content:flex-end;margin:10px 0 14px;">
      <div style="min-width:240px;font-size:12px;">
        <div style="display:flex;justify-content:space-between;padding:3px 0;color:#555;border-bottom:0.5px solid #eee;"><span>Subtotal (gross)</span><span>${fmtUSD(gross)}</span></div>
        ${discT>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;color:${bl};border-bottom:0.5px solid #eee;"><span>Item discounts</span><span>-${fmtUSD(discT)}</span></div>`:''}
        <div style="display:flex;justify-content:space-between;padding:6px 0 3px;font-size:13px;font-weight:700;color:${bl};border-top:1.5px solid ${bl};margin-top:3px;"><span>Invoice total</span><span>${fmtUSD(invTotal)}</span></div>
        ${parseFloat(paid||0)>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;color:#555;"><span>Already paid</span><span>-${fmtUSD(paid)}</span></div>`:''}
        <div style="display:flex;justify-content:space-between;padding:6px 0 3px;font-size:13px;font-weight:700;color:${bl};border-top:1.5px solid ${bl};margin-top:3px;"><span>Balance due</span><span>${fmtUSD(balance)}</span></div>
      </div>
    </div>
    ${(p.bankName||p.zelle)?`<div style="background:#f0f4f8;border-left:4px solid ${bl};border-radius:4px;padding:10px 14px;font-size:11px;"><div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:${bl};margin-bottom:5px;">Payment Details</div>${p.bankName?`<div><strong>Bank:</strong> ${p.bankName}</div>`:''}${p.accountName?`<div><strong>Account name:</strong> ${p.accountName}</div>`:''}${p.accountNo?`<div><strong>Account no:</strong> ${p.accountNo}</div>`:''}${p.ifsc?`<div><strong>IFSC/SWIFT:</strong> ${p.ifsc}</div>`:''}${p.zelle?`<div>Zelle: ${p.zelle}</div>`:''}${p.paypal?`<div>PayPal: ${p.paypal}</div>`:''}</div>`:''}
    <div style="margin-top:12px;padding-top:10px;border-top:0.5px solid #e0e0e0;font-size:10.5px;color:#888;line-height:1.8;"><div>${p.invoiceNote||''}</div><div>${p.paymentTerms||''}</div></div>
    <div style="margin-top:22px;display:flex;justify-content:space-between;align-items:flex-end;font-size:11px;"><div style="color:#555;"><div style="font-weight:700;color:${bl};font-size:12px;">Thank you for your business!</div><div>Questions about this invoice? ${p.email||''}${p.phone?' · '+p.phone:''}</div></div><div style="text-align:center;min-width:170px;"><div style="border-top:1px solid #999;padding-top:4px;color:#666;">Authorized signature</div></div></div>
  `;

  const doDownload = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Invoice ${pl.invNum}</title></head><body style="font-family:'Helvetica Neue',Arial,sans-serif;padding:40px;font-size:12px;line-height:1.5;">${invoiceHTML()}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(()=>w.print(), 200); // gives the browser a beat to render before the print/"Save as PDF" dialog opens
  };

  const doWhatsApp = () => {
    if (!client.phone) { window.alert("This client doesn't have a phone number on file."); return; }
    const msg = `Hi ${client.contact||client.name}, here's invoice ${pl.invNum} for ${fmtUSD(invTotal)} (balance due: ${fmtUSD(balance)}). I've just downloaded the PDF — attaching it here now.`;
    doDownload();
    window.open(waLink(client.phone, msg), '_blank');
  };

  const Shell = readOnly ? PlainShell : Modal;
  return (
    <Shell onClose={onClose} maxWidth={680}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:8}}>
        <div style={{fontSize:15,fontWeight:700}}>{pl.invNum}</div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {!readOnly&&!editing&&<Btn sm onClick={()=>setEditing(true)}>✏️ Edit</Btn>}
          {editing&&<Btn sm style={{background:gr,color:'#fff',borderColor:gr}} onClick={saveEdit}>Save</Btn>}
          {editing&&<Btn sm onClick={()=>setEditing(false)}>Cancel</Btn>}
          {!editing&&<Btn sm onClick={doDownload}>⬇ Download PDF</Btn>}
          {!readOnly&&!editing&&<Btn sm onClick={doWhatsApp}>💬 Send to client</Btn>}
          {!readOnly&&<Btn sm style={{color:rd,borderColor:rd}} onClick={onDelete}>🗑</Btn>}
          {!readOnly&&<Btn sm onClick={onClose}>✕</Btn>}
        </div>
      </div>
      {editing ? (
        <div>
          <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.4px',color:'#666',marginBottom:8}}>Line items</div>
          <LineItems items={items} onChange={setItems}/>
          <Div/>
          <div style={{display:'flex',gap:12,alignItems:'center'}}>
            <FG label="Already paid ($)" style={{width:180}}><Inp type="number" min={0} value={paid} onChange={e=>setPaid(e.target.value)}/></FG>
            <div style={{fontSize:14,fontWeight:700,color:bl,paddingTop:20}}>Balance: {fmtUSD(balance)}</div>
          </div>
        </div>
      ) : (
        <div style={{fontFamily:"'Helvetica Neue',Arial,sans-serif",fontSize:12,lineHeight:1.5}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
            <div>{p.logo?<img src={p.logo} alt="logo" style={{maxHeight:50,maxWidth:150,objectFit:'contain'}}/>:<div style={{fontSize:18,fontWeight:900,color:bl}}>{p.companyName}</div>}
              <div style={{fontSize:10.5,color:'#555',marginTop:5,lineHeight:1.7}}><div>{p.city}{p.country?', '+p.country:''}</div><div>{p.email}{p.phone?' · '+p.phone:''}</div></div>
            </div>
            <div style={{textAlign:'right'}}><div style={{fontSize:28,fontWeight:900,color:bl,letterSpacing:1}}>INVOICE</div><div style={{fontSize:11,color:'#555',marginTop:4,lineHeight:1.8}}><div># {pl.invNum}</div><div>Date: {pl.date}</div><div>Due: {pl.dueDate||pl.date}</div><div style={{marginTop:6,display:'inline-block',border:`1.5px solid ${payColor}`,color:payColor,borderRadius:4,padding:'2px 10px',fontSize:11,fontWeight:800,letterSpacing:.6}}>{payStatus}</div></div></div>
          </div>
          {taxChips.length>0&&<div style={{display:'flex',gap:6,margin:'8px 0 14px'}}>{taxChips.map((ch,i)=><div key={i} style={{border:'1px solid #c0c8d0',borderRadius:4,padding:'2px 8px',fontSize:10,fontWeight:600,color:'#444'}}>{ch}</div>)}</div>}
          <div style={{height:'0.5px',background:'#ccc',margin:'4px 0 10px'}}/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,margin:'10px 0'}}>
            <div><div style={{fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.6px',color:bl,marginBottom:3}}>FROM</div><div style={{fontWeight:700}}>{p.companyName}</div><div style={{fontSize:11,color:'#555',lineHeight:1.6}}>{fromAddr}</div><div style={{fontSize:11,color:'#555'}}>{p.email}{p.phone?' · '+p.phone:''}</div>{p.website&&<div style={{fontSize:11,color:'#555'}}>{p.website}</div>}</div>
            <div><div style={{fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.6px',color:bl,marginBottom:3}}>BILL TO</div><div style={{fontWeight:700}}>{client.name}</div><div style={{fontSize:11,color:'#555'}}>{client.contact||''}</div><div style={{fontSize:11,color:'#555',lineHeight:1.6}}>{clientAddr}</div>{client.phone&&<div style={{fontSize:11,color:'#555'}}>{client.phone}</div>}{client.email&&<div style={{fontSize:11,color:'#555'}}>{client.email}</div>}<div style={{fontSize:11,color:'#555'}}>Client #: CLT-39S-{(client.id||'0000').slice(-4).toUpperCase()}</div></div>
          </div>
          <div style={{height:'0.5px',background:'#ccc',margin:'4px 0 10px'}}/>
          {pl.candidateName&&<div style={{background:'#f7f9fb',border:'0.5px solid #dde3ea',borderRadius:6,padding:'8px 12px',margin:'0 0 10px',fontSize:11,display:'flex',gap:10,alignItems:'center'}}>{pl.candidatePhoto&&<img src={pl.candidatePhoto} alt="" style={{width:34,height:34,borderRadius:'50%',objectFit:'cover'}}/>}<div><div style={{fontSize:9.5,fontWeight:700,textTransform:'uppercase',letterSpacing:'.6px',color:bl}}>Candidate placed</div><div style={{fontWeight:700,fontSize:12}}>{pl.candidateName}{pl.headcount>1?` (+${pl.headcount-1} more)`:''}</div>{candLine&&<div style={{color:'#555'}}>{candLine}</div>}<div style={{color:'#555'}}>Placement date: {pl.date}</div></div></div>}
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11.5}}>
            <thead><tr style={{background:bl,color:'#fff'}}>{['Description','QTY','Rate','Disc/Unit','Amount'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:h==='Description'?'left':'center',fontSize:9.5,textTransform:'uppercase',letterSpacing:'.5px'}}>{h}</th>)}</tr></thead>
            <tbody>{items.map((it,i)=>{const lg=(parseFloat(it.rate)||0)*(parseInt(it.qty)||1);const ld=(parseFloat(it.discPerUnit)||0)*(parseInt(it.qty)||1);return(<tr key={i} style={{borderBottom:'0.5px solid #eee'}}><td style={{padding:'8px 10px'}}>{it.desc||'—'}</td><td style={{padding:'8px 10px',textAlign:'center'}}>{it.qty||1}</td><td style={{padding:'8px 10px',textAlign:'center'}}>{fmtUSD(it.rate)}</td><td style={{padding:'8px 10px',textAlign:'right',color:parseFloat(it.discPerUnit||0)>0?rd:'#aaa'}}>{parseFloat(it.discPerUnit||0)>0?`-${fmtUSD(it.discPerUnit)}/unit`:'—'}</td><td style={{padding:'8px 10px',textAlign:'right',fontWeight:600}}>{fmtUSD(lg-ld)}</td></tr>);})}</tbody>
          </table>
          <div style={{display:'flex',justifyContent:'flex-end',margin:'10px 0 14px'}}>
            <div style={{minWidth:240,fontSize:12}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',color:'#555',borderBottom:'0.5px solid #eee'}}><span>Subtotal (gross)</span><span>{fmtUSD(gross)}</span></div>
              {discT>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',color:bl,borderBottom:'0.5px solid #eee'}}><span>Item discounts</span><span>-{fmtUSD(discT)}</span></div>}
              <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0 3px',fontSize:13,fontWeight:700,color:bl,borderTop:`1.5px solid ${bl}`,marginTop:3}}><span>Invoice total</span><span>{fmtUSD(invTotal)}</span></div>
              {parseFloat(paid||0)>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',color:'#555'}}><span>Already paid</span><span>-{fmtUSD(paid)}</span></div>}
              <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0 3px',fontSize:13,fontWeight:700,color:bl,borderTop:`1.5px solid ${bl}`,marginTop:3}}><span>Balance due</span><span>{fmtUSD(balance)}</span></div>
            </div>
          </div>
          {(p.bankName||p.zelle)&&<div style={{background:'#f0f4f8',borderLeft:`4px solid ${bl}`,borderRadius:4,padding:'10px 14px',fontSize:11}}><div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.5px',color:bl,marginBottom:5}}>Payment Details</div>{p.bankName&&<div><strong>Bank:</strong> {p.bankName}</div>}{p.accountName&&<div><strong>Account name:</strong> {p.accountName}</div>}{p.accountNo&&<div><strong>Account no:</strong> {p.accountNo}</div>}{p.ifsc&&<div><strong>IFSC/SWIFT:</strong> {p.ifsc}</div>}{p.zelle&&<div>Zelle: {p.zelle}</div>}{p.paypal&&<div>PayPal: {p.paypal}</div>}</div>}
          <div style={{marginTop:12,paddingTop:10,borderTop:'0.5px solid #e0e0e0',fontSize:10.5,color:'#888',lineHeight:1.8}}><div>{p.invoiceNote}</div><div>{p.paymentTerms}</div></div>
          <div style={{marginTop:22,display:'flex',justifyContent:'space-between',alignItems:'flex-end',fontSize:11}}><div style={{color:'#555'}}><div style={{fontWeight:700,color:bl,fontSize:12}}>Thank you for your business!</div><div>Questions about this invoice? {p.email}{p.phone?' · '+p.phone:''}</div></div><div style={{textAlign:'center',minWidth:170}}><div style={{borderTop:'1px solid #999',paddingTop:4,color:'#666'}}>Authorized signature</div></div></div>
        </div>
      )}
    </Shell>
  );
}

function PlacementsTab({placements, setPlacements, clients, profile, toast}) {
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState('');
  const filtered = placements.filter(p => { const s=q.toLowerCase(); return !q||(p.candidateName||'').toLowerCase().includes(s)||(p.clientName||'').toLowerCase().includes(s)||(p.invNum||'').toLowerCase().includes(s); });
  const total = placements.reduce((a,p)=>a+(p.total||0),0);
  const collected = placements.reduce((a,p)=>a+parseFloat(p.alreadyPaid||0),0);
  const updatePl = useCallback(async (updated) => {
    const newTotal = (updated.items||[]).reduce((a,i)=>(parseFloat(i.rate)||0)*(parseInt(i.qty)||1)-(parseFloat(i.discPerUnit)||0)*(parseInt(i.qty)||1)+a,0);
    const fin = {...updated,total:newTotal};
    await DB.save('placements', fin.id, fin);
    setPlacements(p => p.map(x=>x.id===fin.id?fin:x));
    setModal(m => m?{...m,pl:fin}:null);
    toast('Invoice updated ✓');
  }, [toast]);
  const delPl = useCallback(async (id) => {
    if (!window.confirm('Delete this placement?')) return;
    await DB.delete('placements', id);
    setPlacements(p => p.filter(x=>x.id!==id));
    setModal(null); toast('Deleted.');
  }, [toast]);

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={placements.reduce((a,p)=>a+(p.headcount||1),0)} label="People placed"/>
        <StatCard n={`$${total.toLocaleString(undefined,{minimumFractionDigits:0})}`} label="Gross fees"/>
        <StatCard n={placements.filter(p=>parseFloat(p.alreadyPaid||0)<(p.total||0)).length} label="Outstanding"/>
        <StatCard n={`$${collected.toLocaleString(undefined,{minimumFractionDigits:0})}`} label="Collected"/>
      </div>
      <div style={{fontSize:12,color:'#888',marginBottom:10}}>🔒 Admin only — click a placement to view/edit invoice</div>
      <SBar value={q} onChange={setQ} placeholder="Search placements..."/>
      {filtered.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>📄</div>{q?'No match.':'No placements yet. Use Match tab.'}</div>}
      {filtered.map(p => {
        const client = clients.find(cl=>cl.id===p.clientId)||{name:p.clientName,id:'0000',state:''};
        const balance = (p.total||0)-parseFloat(p.alreadyPaid||0);
        return (
          <div key={p.id} style={{...S.li,cursor:'pointer'}} onClick={()=>setModal({pl:p,client})}>
            <div style={{width:36,height:36,borderRadius:8,background:'#E6F1FB',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:18}}>📄</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:14}}>{p.candidateName} → {p.clientName}</div>
              <div style={{fontSize:12,color:'#666',marginTop:1}}>{p.invNum} · {p.date}</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
              <div style={{textAlign:'right'}}>
                <div style={{fontWeight:700,color:gr,fontSize:13}}>${(p.total||0).toFixed(2)}</div>
                {balance>0?<div style={{fontSize:11,color:rd}}>Due: ${balance.toFixed(2)}</div>:<div style={{fontSize:11,color:'#3B6D11'}}>Paid ✓</div>}
              </div>
              <Btn sm onClick={(e)=>{e.stopPropagation(); setModal({pl:p,client});}}>📄 View</Btn>
              <span style={{color:'#ccc',fontSize:16}}>›</span>
            </div>
          </div>
        );
      })}
      {modal && <InvModal pl={modal.pl} client={modal.client} profile={profile} onClose={()=>setModal(null)} onUpdate={updatePl} onDelete={()=>delPl(modal.pl.id)}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MY BUSINESS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ProfileTab({profile, setProfile, toast, candidates, clients, placements, activity, referralPartners, referralPayables, setCandidates, setClients, setPlacements, setActivity, setReferralPartners, setReferralPayables, addAct}) {
  const [form, setForm] = useState({...DEFAULT_PROFILE,...profile});
  const [restoreErr, setRestoreErr] = useState('');
  const save = useCallback(async () => {
    await DB.saveProfile(form);
    setProfile(form);
    toast('Profile saved ✓');
  }, [form, toast]);
  const handleLogo = useCallback((e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({...f, logo:ev.target.result}));
    reader.readAsDataURL(file);
  }, []);

  const handleIcon = useCallback(async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try { const dataUrl = await resizeImageFile(file, 192, 0.9, true); setForm(f => ({...f, icon:dataUrl})); }
    catch { /* leave icon unchanged if it can't be read */ }
  }, []);

  const doExport = useCallback(() => {
    const backup = {
      exportedAt: new Date().toISOString(), version: 1,
      candidates, clients, placements, activity, referralPartners, referralPayables, profile: form,
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], {type:'application/json'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `three9-backup-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    toast('Backup downloaded ✓');
  }, [candidates, clients, placements, activity, referralPartners, referralPayables, form, toast]);

  const doImport = useCallback((e) => {
    const file = e.target.files[0]; if (!file) return;
    setRestoreErr('');
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!window.confirm('This will add/overwrite records from the backup file into your current data (existing records not in the file are kept). Continue?')) return;
        let n = 0;
        for (const c of (data.candidates||[])) { await DB.save('candidates', c.id, c); n++; }
        for (const c of (data.clients||[])) { await DB.save('clients', c.id, c); n++; }
        for (const p of (data.placements||[])) { await DB.save('placements', p.id, p); n++; }
        for (const a of (data.activity||[])) { await DB.save('activity', a.id, a); n++; }
        for (const r of (data.referralPartners||[])) { await DB.save('referralPartners', r.id, r); n++; }
        for (const r of (data.referralPayables||[])) { await DB.save('referralPayables', r.id, r); n++; }
        // Reload merged state
        const merge = (existing, incoming) => { const map = new Map(existing.map(x=>[x.id,x])); (incoming||[]).forEach(x=>map.set(x.id,x)); return [...map.values()]; };
        if (data.candidates) setCandidates(p => merge(p, data.candidates));
        if (data.clients) setClients(p => merge(p, data.clients));
        if (data.placements) setPlacements(p => merge(p, data.placements));
        if (data.activity) setActivity(p => merge(p, data.activity));
        if (data.referralPartners) setReferralPartners(p => merge(p, data.referralPartners));
        if (data.referralPayables) setReferralPayables(p => merge(p, data.referralPayables));
        toast(`Restored ${n} record(s) ✓`);
        if (addAct) addAct(`♻️ Restored backup — ${n} record(s)`);
      } catch {
        setRestoreErr('Could not read this file. Make sure it\'s a Three9 backup JSON file.');
      }
    };
    reader.readAsText(file);
  }, [toast, addAct, setCandidates, setClients, setPlacements, setActivity, setReferralPartners, setReferralPayables]);

  return (
    <div>
      <div style={{background:'linear-gradient(135deg,#f0f4f8,#e8f0f8)',border:'0.5px solid #d0dcea',borderRadius:10,padding:14,display:'flex',alignItems:'center',gap:14,marginBottom:14}}>
        {form.logo?<img src={form.logo} alt="logo" style={{maxHeight:50,maxWidth:130,objectFit:'contain'}}/>:<div style={{width:80,height:44,background:bl,borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:11}}>{(form.companyName||'THREE9').slice(0,8).toUpperCase()}</div>}
        <div><div style={{fontWeight:700,fontSize:15,color:bl}}>{form.companyName}</div><div style={{fontSize:12,color:'#666'}}>{form.city}{form.country?' · '+form.country:''}{form.phone?' · '+form.phone:''}</div></div>
        <div style={{marginLeft:'auto'}}><span style={S.tA}>🔒 Admin only</span></div>
      </div>
      <div style={S.card}>
        <ProfileForm value={form} onChange={setForm}/>
        <SecLabel>🖼 Company logo</SecLabel>
        <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
          <label style={{flex:1,height:90,border:'1.5px dashed #ccc',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:5,color:'#999',fontSize:12,background:'#fafaf8',cursor:'pointer'}}>
            {form.logo?<img src={form.logo} alt="logo" style={{maxHeight:70,maxWidth:'100%',objectFit:'contain'}}/>:<><div style={{fontSize:24}}>🖼</div><div>Click to upload logo</div><div style={{fontSize:11,color:'#bbb'}}>PNG, JPG, SVG</div></>}
            <input type="file" accept="image/*" style={{display:'none'}} onChange={handleLogo}/>
          </label>
          {form.logo&&<Btn sm style={{color:rd,borderColor:rd,alignSelf:'center'}} onClick={()=>setForm(f=>({...f,logo:null}))}>Remove</Btn>}
        </div>
        <SecLabel>🔹 Small icon</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:8}}>A square icon (separate from your logo above) — used for the CRM's loading screen and the browser tab icon. A simple monogram or symbol works better than a wide logo here.</div>
        <div style={{display:'flex',gap:12,alignItems:'center'}}>
          <label style={{width:64,height:64,border:'1.5px dashed #ccc',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:'#999',fontSize:11,background:'#fafaf8',cursor:'pointer',overflow:'hidden'}}>
            {form.icon?<img src={form.icon} alt="icon" style={{width:'100%',height:'100%',objectFit:'contain'}}/>:<div style={{fontSize:20}}>🔹</div>}
            <input type="file" accept="image/*" style={{display:'none'}} onChange={handleIcon}/>
          </label>
          <div style={{fontSize:12,color:'#666'}}>{form.icon?'Click the square to replace it.':'Click the square to upload one. If left blank, your logo (or initials) is used instead.'}</div>
          {form.icon&&<Btn sm style={{color:rd,borderColor:rd}} onClick={()=>setForm(f=>({...f,icon:''}))}>Remove</Btn>}
        </div>
        <SecLabel>📱 WhatsApp message template</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Placeholders: {'{name} {age} {location} {visa} {origin} {usaYears} {availability} {members} {experience} {skills} {phone} {noteSection}'}</div>
        <TA value={form.whatsappTemplate||''} onChange={e=>setForm(f=>({...f,whatsappTemplate:e.target.value}))} style={{minHeight:110,fontSize:12}}/>
        <Btn sm style={{marginTop:6}} onClick={()=>setForm(f=>({...f,whatsappTemplate:DEFAULT_PROFILE.whatsappTemplate}))}>↺ Reset to default (adds any new fields)</Btn>
        <SecLabel>⚡ WhatsApp Business API</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Turn on after finishing WHATSAPP_API_SETUP.txt. Adds a "Send via Business API" button next to candidate messages and enables the WhatsApp inbox tab.</div>
        <Toggle label="API connected" value={form.waApiEnabled||'No'} onChange={v=>setForm(f=>({...f,waApiEnabled:v}))}/>
        <div style={{fontSize:11,color:'#999',margin:'10px 0 6px'}}>Approved templates, one per line: <code>name|language|number of variables|label</code></div>
        <TA value={form.waTemplates||''} onChange={e=>setForm(f=>({...f,waTemplates:e.target.value}))} placeholder={'hello_world|en_US|0|Test message\nnew_job|en_US|2|New job offer'} style={{minHeight:70,fontSize:12}}/>
        <div style={{fontSize:12,fontWeight:700,margin:'14px 0 4px'}}>🤖 Automatic messages to clients</div>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Sent from your business number. If the client hasn't messaged you in 24 hours, WhatsApp requires the approved template names below (see WHATSAPP_API_SETUP.txt).</div>
        <div style={S.fg2}>
          <Toggle label="New invoice → client" value={form.waAutoInvoice||'Yes'} onChange={v=>setForm(f=>({...f,waAutoInvoice:v}))}/>
          <Toggle label="Client added → contract link" value={form.waAutoContractLink||'Yes'} onChange={v=>setForm(f=>({...f,waAutoContractLink:v}))}/>
          <Toggle label="Contract signed → confirmation" value={form.waAutoContractSigned||'Yes'} onChange={v=>setForm(f=>({...f,waAutoContractSigned:v}))}/>
          <Toggle label="Interview match → candidate info" value={form.waAutoInterview||'Yes'} onChange={v=>setForm(f=>({...f,waAutoInterview:v}))}/>
        </div>
        <div style={S.fg2}>
          <FG label="Invoice template" style={{marginTop:8}}><Inp value={form.waTplInvoice||''} onChange={e=>setForm(f=>({...f,waTplInvoice:e.target.value}))} placeholder="invoice_ready"/></FG>
          <FG label="Contract-link template" style={{marginTop:8}}><Inp value={form.waTplContractLink||''} onChange={e=>setForm(f=>({...f,waTplContractLink:e.target.value}))} placeholder="contract_link"/></FG>
          <FG label="Signed-contract template" style={{marginTop:8}}><Inp value={form.waTplContractSigned||''} onChange={e=>setForm(f=>({...f,waTplContractSigned:e.target.value}))} placeholder="contract_signed"/></FG>
          <FG label="Interview-candidate template" style={{marginTop:8}}><Inp value={form.waTplInterview||''} onChange={e=>setForm(f=>({...f,waTplInterview:e.target.value}))} placeholder="interview_candidate"/></FG>
          <FG label="Interview button order (as created in Meta: hire, nofit, profile, call)" style={{marginTop:8}}><Inp value={form.waInterviewButtons||''} onChange={e=>setForm(f=>({...f,waInterviewButtons:e.target.value}))} placeholder="hire,nofit,profile,call"/></FG>
          <FG label="Template language" style={{marginTop:8}}><Inp value={form.waTplLang||''} onChange={e=>setForm(f=>({...f,waTplLang:e.target.value}))} placeholder="en_US"/></FG>
        </div>
        <div style={{fontSize:12,fontWeight:700,margin:'14px 0 4px'}}>🔔 Phone & PC notifications</div>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Firebase console → Project settings → Cloud Messaging → Web Push certificates → Generate key pair. Paste the public key here, save, then use "Enable notifications" on each device (WhatsApp tab).</div>
        <Inp value={form.fcmVapidKey||''} onChange={e=>setForm(f=>({...f,fcmVapidKey:e.target.value.trim()}))} placeholder="BNx...  (Web Push public key)"/>
        <SecLabel>📜 Contract template</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Placeholders: {'{companyName} {clientName} {clientLocation} {candidateName} {fee} {paymentTerms} {paymentTermsNote} {date}'}</div>
        <TA value={form.contractTemplate||''} onChange={e=>setForm(f=>({...f,contractTemplate:e.target.value}))} style={{minHeight:140,fontSize:12}}/>
        <SecLabel>📜 Client service agreement template</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Used for the contract in Clients and the client portal. Placeholders: {'{companyName} {clientName} {clientBusiness} {clientLocation} {feeDescription} {paymentTerms} {date}'}. Changes apply to every unsigned contract; for already-signed ones use "Reload from template" in the client's contract.</div>
        <TA value={form.clientContractTemplate||''} onChange={e=>setForm(f=>({...f,clientContractTemplate:e.target.value}))} style={{minHeight:160,fontSize:12}}/>
        <Btn sm style={{marginTop:6}} onClick={()=>setForm(f=>({...f,clientContractTemplate:DEFAULT_PROFILE.clientContractTemplate}))}>↺ Reset to default</Btn>
        <SecLabel>📜 Referral partner agreement template</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Placeholders: {'{companyName} {partnerName} {partnerAddress} {feeDescription} {paymentTerms} {date}'}</div>
        <TA value={form.partnerContractTemplate||''} onChange={e=>setForm(f=>({...f,partnerContractTemplate:e.target.value}))} style={{minHeight:140,fontSize:12}}/>
        <Btn sm style={{marginTop:6}} onClick={()=>setForm(f=>({...f,partnerContractTemplate:DEFAULT_PROFILE.partnerContractTemplate}))}>↺ Reset to default</Btn>
        <SecLabel>🔗 Public web app URL</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Your live deployed web address (e.g. after Firebase Hosting or a custom domain). Used to build referral links correctly — without this, links copied from the desktop app would be broken.</div>
        <Inp value={form.publicUrl||''} onChange={e=>setForm(f=>({...f,publicUrl:e.target.value.replace(/\/$/,'')}))} placeholder="https://crm.three9.in"/>
        {form.publicUrl ? <div style={{fontSize:11,color:gr,marginTop:5}}>✓ Referral links will use: {form.publicUrl}</div> : <div style={{fontSize:11,color:'#c99',marginTop:5}}>⚠ Not set — referral links copied from the desktop app will be broken until this is filled in.</div>}
        <SecLabel>📊 Google Sheets sync</SecLabel>
        <div style={{fontSize:11,color:'#999',marginBottom:6}}>Paste your Apps Script Web App URL to auto-sync every candidate/client add, edit, and delete to a live Google Sheet. See GOOGLE_SHEETS_SETUP.txt in your project folder for the 5-minute setup.</div>
        <Inp value={form.sheetsUrl||''} onChange={e=>setForm(f=>({...f,sheetsUrl:e.target.value}))} placeholder="https://script.google.com/macros/s/.../exec"/>
        {form.sheetsUrl && <div style={{fontSize:11,color:gr,marginTop:5}}>✓ Sync is active — new records will push to your sheet automatically.</div>}
        <div style={{display:'flex',gap:8,marginTop:18}}><Btn prim onClick={save}>💾 Save profile</Btn></div>
      </div>

      <div style={S.card}>
        <SecLabel>💾 Backup & restore</SecLabel>
        <div style={{fontSize:12,color:'#888',marginBottom:12}}>Download all your data (candidates, clients, placements, activity, referral partners & fees) as one file, or restore from a previous backup. Restoring adds/updates records without deleting anything currently in your CRM.</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <Btn onClick={doExport}>⬇ Download backup (.json)</Btn>
          <label style={{display:'inline-flex',alignItems:'center',padding:'8px 14px',borderRadius:8,border:`1.5px solid ${br}`,cursor:'pointer',fontSize:13,color:tx,fontWeight:600}}>
            ⬆ Restore from backup
            <input type="file" accept=".json,application/json" style={{display:'none'}} onChange={doImport}/>
          </label>
        </div>
        {restoreErr && <div style={{background:'#FCEBEB',borderRadius:7,padding:'7px 11px',fontSize:12,color:'#A32D2D',marginTop:10}}>⚠ {restoreErr}</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEAM TAB
// ═══════════════════════════════════════════════════════════════════════════════
function TeamTab({currentUser, toast, candidates, placements, referralPartners}) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({name:'',email:'',password:'',role:'employee'});
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState(null);
  const isLocal = !FIREBASE_ENABLED;

  useEffect(() => {
    // Team is for staff accounts only — client logins are created and managed
    // from the Clients tab instead, and shouldn't be mixed into this list.
    if (isLocal) { setUsers(DB.getLocalUsers().filter(u=>u.role!=='client')); }
    else { fbGetAll('users').then(docs => setUsers(docs.filter(u=>u.role!=='client'))); }
  }, []);

  const nextEmployeeId = useCallback(() => {
    const nums = users.map(u => parseInt((u.employeeId||'').replace(/\D/g,''),10)).filter(n=>!isNaN(n));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    return `EMP-${String(next).padStart(4,'0')}`;
  }, [users]);

  // Every employee — how many candidates they've personally added and how many of
  // those got placed, with a month-by-month breakdown. Independent of the referral
  // incentive system below (which only applies to employees also set up as referrers).
  const addedStats = useCallback((emp) => {
    const added = (candidates||[]).filter(c=>c.addedBy===emp.uid);
    const addedIds = new Set(added.map(c=>c.id));
    const placedRecs = (placements||[]).filter(p=>addedIds.has(p.candidateId));
    const addedCount = added.reduce((a,c)=>a+personCount(c),0);
    const placedCount = placedRecs.reduce((a,p)=>a+(p.headcount||1),0);
    const now = new Date();
    const months = [];
    for (let i=5;i>=0;i--){ const d = new Date(now.getFullYear(), now.getMonth()-i, 1); months.push({key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label:d.toLocaleDateString('en-US',{month:'short'})}); }
    const monthlyAdded = months.map(m => ({...m, n: added.filter(c=>{ const d=new Date(c.addedAt); return !isNaN(d) && `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`===m.key; }).reduce((a,c)=>a+personCount(c),0)}));
    const monthlyPlaced = months.map(m => ({...m, n: placedRecs.filter(p=>{ const d=new Date(p.date); return !isNaN(d) && `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`===m.key; }).reduce((a,p)=>a+(p.headcount||1),0)}));
    return {addedCount, placedCount, monthlyAdded, monthlyPlaced};
  }, [candidates, placements]);

  // Matches an employee login to their referral-incentive entry (by name, same
  // heuristic used on the employee's own Dashboard), then works out how many
  // candidates they've referred, how many got placed, and a month-by-month breakdown.
  const empStats = useCallback((emp) => {
    const partner = (referralPartners||[]).find(rp =>
      rp.refType==='employee' && (
        (emp.employeeId && rp.employeeId && rp.employeeId===emp.employeeId) ||
        (rp.name||'').trim().toLowerCase() === (emp.name||'').trim().toLowerCase()
      )
    );
    if (!partner) return null;
    const referred = (candidates||[]).filter(c=>c.referralPartnerId===partner.id);
    const referredIds = new Set(referred.map(c=>c.id));
    const placedRecs = (placements||[]).filter(p=>referredIds.has(p.candidateId));
    const referredCount = referred.reduce((a,c)=>a+personCount(c),0);
    const placedCount = placedRecs.reduce((a,p)=>a+(p.headcount||1),0);
    const now = new Date();
    const months = [];
    for (let i=5;i>=0;i--){ const d = new Date(now.getFullYear(), now.getMonth()-i, 1); months.push({key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label:d.toLocaleDateString('en-US',{month:'short'})}); }
    const monthly = months.map(m => {
      const n = placedRecs.filter(p => { const d = new Date(p.date); return !isNaN(d) && `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`===m.key; })
        .reduce((a,p)=>a+(p.headcount||1),0);
      return {...m, n};
    });
    return {referredCount, placedCount, monthly};
  }, [candidates, placements, referralPartners]);

  const create = useCallback(async () => {
    if (!form.name||!form.email||!form.password) { setErr('All fields required.'); return; }
    if (form.password.length<6) { setErr('Password must be 6+ characters.'); return; }
    setCreating(true); setErr('');
    const employeeId = nextEmployeeId();
    try {
      if (isLocal) {
        const existing = DB.getLocalUsers();
        if (existing.find(u=>u.email===form.email)) { setErr('Email already in use.'); setCreating(false); return; }
        const newUser = {uid:uid(),name:form.name,email:form.email,password:form.password,role:form.role,employeeId,createdAt:today()};
        DB.saveLocalUsers([...existing,newUser]);
        setUsers(p=>[...p,newUser]);
        setForm({name:'',email:'',password:'',role:'employee'});
        toast(`Account created for ${form.name} — ${employeeId}`);
      } else {
        const cred = await createFirebaseUser(form.email, form.password);
        const userData = {uid:cred.user.uid,name:form.name,email:form.email,role:form.role,employeeId,createdAt:today()};
        await fbSetDoc('users', cred.user.uid, userData);
        setUsers(p=>[...p,userData]);
        setForm({name:'',email:'',password:'',role:'employee'});
        toast(`Account created for ${form.name} — ${employeeId}`);
      }
    } catch(e) {
      setErr(e.code==='auth/email-already-in-use'?'Email already in use.':'Error: '+e.message);
    }
    setCreating(false);
  }, [form, isLocal, toast, nextEmployeeId]);

  const del = useCallback(async (emp) => {
    if (!window.confirm(`Delete account for ${emp.name}?`)) return;
    if (isLocal) { const updated=DB.getLocalUsers().filter(u=>u.uid!==emp.uid); DB.saveLocalUsers(updated); }
    else { await fbDelete('users', emp.uid); }
    setUsers(p=>p.filter(u=>u.uid!==emp.uid));
    toast(`${emp.name}'s account deleted.`);
  }, [isLocal, toast]);

  return (
    <div>
      <div style={{...S.card,borderLeft:`4px solid ${bl}`}}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:3}}>Create employee login</div>
        <div style={{fontSize:12,color:'#888',marginBottom:14}}>Employees get their own employee ID and a personal dashboard — no company-wide pricing, fees, or invoices</div>
        <EmployeeForm value={form} onChange={setForm}/>
        {err && <div style={{background:'#FCEBEB',borderRadius:7,padding:'7px 11px',fontSize:12,color:'#A32D2D',marginTop:8}}>⚠ {err}</div>}
        <div style={{marginTop:12}}><Btn prim onClick={create} disabled={creating}>{creating?'Creating...':'➕ Create account'}</Btn></div>
      </div>
      <div style={{fontSize:14,fontWeight:600,marginBottom:10}}>Employee accounts ({users.length})</div>
      {users.length===0 && <div style={{textAlign:'center',padding:'30px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:28,marginBottom:8,opacity:.4}}>👥</div>No accounts yet.</div>}
      {users.map(emp => {
        const stats = empStats(emp);
        const added = addedStats(emp);
        const maxMonthly = Math.max(1, ...added.monthlyAdded.map(m=>m.n), ...added.monthlyPlaced.map(m=>m.n));
        return (
          <div key={emp.uid}>
            <div style={S.li}>
              <div style={{...S.av,background:'#e8e4f8',color:'#5b3db5'}}>{initials(emp.name)}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:14}}>{emp.name} {emp.employeeId && <span style={{fontSize:11,color:'#888',fontFamily:'monospace',fontWeight:400}}>{emp.employeeId}</span>}</div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{emp.email} · Added: {emp.createdAt||'—'}</div>
                <div style={{marginTop:5,display:'flex',gap:5,flexWrap:'wrap'}}>
                  <span style={emp.role==='admin'?S.tA:S.tB}>{emp.role==='admin'?'👑 Admin':'👤 Employee'}</span>
                  <span style={S.tB}>{added.addedCount} candidates added</span>
                  <span style={S.tGr}>{added.placedCount} placed</span>
                  {stats && <span style={S.tG}>{stats.referredCount} referred (incentive)</span>}
                </div>
              </div>
              <Btn sm onClick={()=>setExpanded(expanded===emp.uid?null:emp.uid)}>{expanded===emp.uid?'▲ Hide monthly':'📊 Monthly'}</Btn>
              {emp.uid!==currentUser.uid && <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>del(emp)}>🗑 Delete</Btn>}
            </div>
            {expanded===emp.uid && (
              <div style={{...S.card,marginTop:-6,marginBottom:14,background:'#f8f8f4'}}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:10}}>Monthly performance — {emp.name}</div>
                <div style={{display:'flex',gap:16}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:'#666',marginBottom:6}}>Candidates added</div>
                    <div style={{display:'flex',alignItems:'flex-end',gap:8,height:80}}>
                      {added.monthlyAdded.map(m => (
                        <div key={m.key} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                          <div style={{fontSize:10,color:'#888'}}>{m.n>0?m.n:''}</div>
                          <div style={{width:'100%',maxWidth:24,height:Math.max(3,(m.n/maxMonthly)*55),background:'#7a90b0',borderRadius:'3px 3px 0 0'}}/>
                          <div style={{fontSize:10,color:'#666'}}>{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:'#666',marginBottom:6}}>Placed</div>
                    <div style={{display:'flex',alignItems:'flex-end',gap:8,height:80}}>
                      {added.monthlyPlaced.map(m => (
                        <div key={m.key} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                          <div style={{fontSize:10,color:'#888'}}>{m.n>0?m.n:''}</div>
                          <div style={{width:'100%',maxWidth:24,height:Math.max(3,(m.n/maxMonthly)*55),background:bl,borderRadius:'3px 3px 0 0'}}/>
                          <div style={{fontSize:10,color:'#666'}}>{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div style={{...S.card,background:'#f8f8f4',marginTop:8}}>
        <div style={{fontSize:12,fontWeight:600,color:'#666',marginBottom:6}}>🔐 Access levels</div>
        <div style={{fontSize:12,color:'#555',lineHeight:2}}>
          <div><strong>Admin:</strong> full access — Candidates · Pipeline · Clients · Match · Placed · Map · Reminders · Referrals (full) · Placements · Reports · Activity · My Business · Team</div>
          <div><strong>Employee:</strong> Candidates · Pipeline · Match · Placed · Map · Reminders · Referrals (view only, no fees) · their own Dashboard</div>
        </div>
        <div style={{fontSize:11,color:'#999',marginTop:8}}>"Referred" / "Placed" / "Monthly" counts only show for employees also set up as a referral incentive earner in the Referrals tab, matched by name.</div>
        <div style={{fontSize:11,color:'#999',marginTop:4}}>Client logins are separate from staff — create and manage those from the Clients tab, not here.</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PIPELINE BOARD (KANBAN)
// ═══════════════════════════════════════════════════════════════════════════════
function PipelineTab({candidates:allCandidates, setCandidates, clients, setClients, placements, setPlacements, referralPartners, setReferralPayables, toast, addAct, user, isAdmin}) {
  const candidates = isAdmin===false ? allCandidates.filter(c=>c.addedBy===user?.uid) : allCandidates;
  const [dragId, setDragId] = useState(null);
  const [pickClientFor, setPickClientFor] = useState(null); // candidate needing a client picked before placing
  const [pickClientQ, setPickClientQ] = useState('');

  const moveStage = useCallback(async (id, stage) => {
    const cand = candidates.find(c=>c.id===id);
    if (!cand || cand.stage===stage) return;
    if (stage === 'Placed') {
      // Selecting "Placed" should actually place them — generate the invoice, not just flip a label.
      if (cand.interviewClientId) {
        const client = clients.find(c=>c.id===cand.interviewClientId);
        if (client) { await createPlacement({cand, client, placements, setPlacements, setCandidates, setClients, referralPartners, setReferralPayables, addAct, toast}); return; }
      }
      // No client on file for this candidate yet — ask which client they were placed with.
      setPickClientFor(cand);
      return;
    }
    const updated = {...cand, stage};
    await DB.save('candidates', id, updated);
    setCandidates(p => p.map(c => c.id===id ? updated : c));
    addAct(`📊 Moved "${cand.name}" to ${stage}`);
  }, [candidates, clients, setClients, placements, setPlacements, setCandidates, referralPartners, setReferralPayables, addAct, toast]);

  const confirmPickClient = useCallback(async (client) => {
    if (!pickClientFor) return;
    await createPlacement({cand:pickClientFor, client, placements, setPlacements, setCandidates, setClients, referralPartners, setReferralPayables, addAct, toast});
    setPickClientFor(null); setPickClientQ('');
  }, [pickClientFor, placements, setPlacements, setCandidates, setClients, referralPartners, setReferralPayables, addAct, toast]);

  const filteredClients = (clients||[]).filter(c => !pickClientQ || (c.name||'').toLowerCase().includes(pickClientQ.toLowerCase()));

  return (
    <div>
      <div style={{fontSize:12,color:'#888',marginBottom:14}}>Drag a card between columns, or use the dropdown on mobile. Moving someone to "Placed" generates their invoice automatically.</div>
      <div style={{display:'grid',gridTemplateColumns:`repeat(${PIPELINE_STAGES.length}, minmax(0,1fr))`,gap:8}}>
        {PIPELINE_STAGES.map(stage => {
          const cards = candidates.filter(c => (c.stage||'New')===stage);
          const peopleCount = cards.reduce((a,c)=>a+personCount(c),0);
          return (
            <div key={stage}
              onDragOver={e=>e.preventDefault()}
              onDrop={e=>{e.preventDefault(); if(dragId) moveStage(dragId, stage);}}
              style={{minWidth:0,background:'#f8f8f4',borderRadius:10,padding:'8px 6px',minHeight:200}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,gap:4}}>
                <span style={{fontSize:10.5,fontWeight:700,padding:'3px 6px',borderRadius:6,background:STAGE_BG[stage],color:STAGE_FG[stage],whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{stage}</span>
                <span style={{fontSize:10.5,color:'#999',flexShrink:0}}>{peopleCount}</span>
              </div>
              {cards.map(c => (
                <div key={c.id} draggable onDragStart={()=>setDragId(c.id)}
                  style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:8,padding:'7px 8px',marginBottom:6,cursor:'grab',boxShadow:'0 1px 2px rgba(0,0,0,.04)'}}>
                  <div style={{fontWeight:600,fontSize:11.5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}{(parseInt(c.members,10)||1)>1?` 👫${c.members}`:''}</div>
                  <div style={{fontSize:10,color:'#888',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.stage==='Interviewing'&&c.interviewClientName?`🗓 ${c.interviewClientName}`:(c.industry||'—')}</div>
                  <select value={stage} onChange={e=>moveStage(c.id, e.target.value)} style={{marginTop:5,width:'100%',fontSize:10,padding:'3px 2px',borderRadius:5,border:`0.5px solid ${br}`}}>
                    {PIPELINE_STAGES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              {cards.length===0 && <div style={{fontSize:10,color:'#ccc',textAlign:'center',padding:'12px 0'}}>None</div>}
            </div>
          );
        })}
      </div>
      {pickClientFor && (
        <Modal onClose={()=>{setPickClientFor(null);setPickClientQ('');}} maxWidth={420}>
          <div style={{fontSize:15,fontWeight:700,marginBottom:3}}>Which client were they placed with?</div>
          <div style={{fontSize:12,color:'#888',marginBottom:12}}>{pickClientFor.name} doesn't have a client on file yet — pick one to generate their invoice.</div>
          <input value={pickClientQ} onChange={e=>setPickClientQ(e.target.value)} placeholder="Search clients..." style={{...S.inp,marginBottom:8}}/>
          <div style={{maxHeight:260,overflowY:'auto'}}>
            {filteredClients.map(cl => (
              <div key={cl.id} onClick={()=>confirmPickClient(cl)} style={{padding:'8px 10px',border:`0.5px solid ${br}`,borderRadius:7,marginBottom:5,cursor:'pointer',fontSize:13}}>
                <div style={{fontWeight:600}}>{cl.name}</div>
                <div style={{fontSize:11,color:'#888'}}>{cl.city}{cl.city&&cl.state?', ':''}{cl.state}</div>
              </div>
            ))}
            {filteredClients.length===0 && <div style={{fontSize:12,color:'#aaa',textAlign:'center',padding:10}}>No clients found.</div>}
          </div>
          <div style={{marginTop:10}}><Btn onClick={()=>{setPickClientFor(null);setPickClientQ('');}}>Cancel</Btn></div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FOLLOW-UP REMINDERS
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// PLACED CANDIDATES ROSTER
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// OPEN POSITIONS (no business/client names — see Map tab for who's who)
// ═══════════════════════════════════════════════════════════════════════════════
function OpenPositionsTab({clients, candidates, placements}) {
  const [industryFilter, setIndustryFilter] = useState('');
  const openRows = (clients||[])
    .map(cl => ({client:cl, slots:clientOpenSlots(cl, candidates, placements)}))
    .filter(r => r.slots > 0 && (!industryFilter || r.client.industry===industryFilter))
    .sort((a,b)=>b.slots-a.slots);
  const totalSlots = openRows.reduce((a,r)=>a+r.slots,0);
  const industries = [...new Set((clients||[]).map(c=>c.industry).filter(Boolean))];

  return (
    <div>
      <div style={{fontSize:12,color:'#888',marginBottom:14}}>Open positions across all clients — business names are kept private here; see the Map tab (or Clients, if you're an admin) for details on who's who. A position stays open to more candidates until someone is actually placed there.</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={openRows.length} label="Locations hiring"/>
        <StatCard n={totalSlots} label="Total open slots"/>
      </div>
      {industries.length>0 && (
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
          <div onClick={()=>setIndustryFilter('')} style={{padding:'5px 11px',borderRadius:7,cursor:'pointer',fontSize:12,border:`0.5px solid ${!industryFilter?bl:'#ccc'}`,background:!industryFilter?'#E6F1FB':'#fff',color:!industryFilter?'#0C447C':tx,fontWeight:!industryFilter?600:400}}>All</div>
          {industries.map(i => (
            <div key={i} onClick={()=>setIndustryFilter(i)} style={{padding:'5px 11px',borderRadius:7,cursor:'pointer',fontSize:12,border:`0.5px solid ${industryFilter===i?bl:'#ccc'}`,background:industryFilter===i?'#E6F1FB':'#fff',color:industryFilter===i?'#0C447C':tx,fontWeight:industryFilter===i?600:400}}>{i}</div>
          ))}
        </div>
      )}
      {openRows.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>📋</div>No open positions right now.</div>}
      {openRows.map(({client,slots}) => (
        <div key={client.id} style={S.li}>
          <div style={{...S.avC,background:pinColor(client.industry)+'22',color:pinColor(client.industry)}}>{client.industry?client.industry[0]:'?'}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:600,fontSize:14}}>{client.industry||'General'}{client.city||client.state?` — ${[client.city,stateName(client.state)].filter(Boolean).join(', ')}`:''}</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
              <span style={S.tG}>{slots} open slot{slots===1?'':'s'}</span>
              {client.carNeeded==='Yes'&&<span style={S.tA}>🚗 Car needed</span>}
              {client.legalOnly==='Yes'&&<span style={S.tB}>✅ Legal only</span>}
              {client.cashPay==='Yes'&&<span style={S.tG}>💵 Cash pay</span>}
              {client.accommodation==='Yes'&&<span style={S.tB}>🏠 Accommodation</span>}
              {client.payType==='fixed' ? (client.fixedPay&&<span style={S.tGr}>${client.fixedPay}/{(client.fixedPeriod||'Monthly').toLowerCase()}</span>) : (client.wage&&<span style={S.tGr}>${client.wage}/hr</span>)}
            </div>
            {client.skills && <div style={{fontSize:12,color:'#666',marginTop:4}}>Needs: {client.skills}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function PriorityTab({clients, setClients, candidates, placements, isAdmin, toast, addAct}) {
  const rank = {Urgent:0, High:1};
  const rows = (clients||[])
    .filter(cl => (cl.priority==='High'||cl.priority==='Urgent'))
    .map(cl => ({client:cl, slots:clientOpenSlots(cl, candidates, placements)}))
    .filter(r => r.slots > 0)   // filled jobs drop off automatically
    .sort((a,b)=> (rank[a.client.priority]-rank[b.client.priority]) || (b.slots-a.slots));
  const clearPriority = async (cl) => {
    const upd = {...cl, priority:''};
    await DB.save('clients', cl.id, upd);
    setClients(p => p.map(c => c.id===cl.id ? upd : c));
    if (addAct) addAct(`Removed priority from "${cl.name}"`);
    if (toast) toast('Priority removed');
  };
  return (
    <div>
      <div style={{fontSize:12,color:'#888',marginBottom:14}}>Jobs to fill first. They leave this list automatically once every position is placed.</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={rows.length} label="Priority jobs"/>
        <StatCard n={rows.reduce((a,r)=>a+r.slots,0)} label="Open slots"/>
      </div>
      {rows.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>⭐</div>No priority jobs right now.{isAdmin?' Set a client\'s Priority to High or Urgent in the Clients tab.':''}</div>}
      {rows.map(({client:cl,slots}) => {
        const urgent = cl.priority==='Urgent';
        return (
          <div key={cl.id} style={{...S.li, alignItems:'flex-start', borderLeft:`4px solid ${urgent?rd:'#EF9F27'}`}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <span style={{fontWeight:700,fontSize:14.5}}>{cl.name||cl.contact}</span>
                <span style={urgent?S.tR:S.tA}>{urgent?'🔥 Urgent':'⭐ High priority'}</span>
              </div>
              <div style={{fontSize:12,color:'#666',marginTop:2}}>{cl.contact&&cl.name?`Contact: ${cl.contact} · `:''}{cl.industry||'General'}{cl.city||cl.state?` · ${[cl.city,stateName(cl.state)].filter(Boolean).join(', ')}`:''}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:6}}>
                <span style={S.tG}>{slots} open slot{slots===1?'':'s'}</span>
                {cl.payType==='fixed' ? (cl.fixedPay&&<span style={S.tGr}>${cl.fixedPay}/{(cl.fixedPeriod||'Monthly').toLowerCase()}</span>) : (cl.wage&&<span style={S.tGr}>${cl.wage}/hr</span>)}
                {cl.hours&&<span style={S.tB}>🕒 {cl.hours}</span>}
                {cl.shift&&<span style={S.tB}>🌗 {cl.shift}</span>}
                {cl.carNeeded==='Yes'&&<span style={S.tA}>🚗 Car needed</span>}
                {cl.legalOnly==='Yes'&&<span style={S.tB}>✅ Legal only</span>}
                {cl.cashPay==='Yes'&&<span style={S.tG}>💵 Cash pay</span>}
                {cl.accommodation==='Yes'&&<span style={S.tB}>🏠 Accommodation</span>}
              </div>
              {cl.skills && <div style={{fontSize:12,color:'#555',marginTop:6}}><strong>Skills:</strong> {cl.skills}</div>}
              {cl.jobDesc && <div style={{fontSize:12,color:'#555',marginTop:4,whiteSpace:'pre-wrap'}}><strong>Job:</strong> {cl.jobDesc}</div>}
              {cl.notes && <div style={{fontSize:12,color:'#555',marginTop:4,whiteSpace:'pre-wrap'}}><strong>Notes:</strong> {cl.notes}</div>}
            </div>
            {isAdmin && <Btn sm onClick={()=>clearPriority(cl)}>Remove</Btn>}
          </div>
        );
      })}
    </div>
  );
}

function PlacedTab({candidates:allCandidates, placements, clients, isAdmin, user}) {
  const candidates = isAdmin===false ? allCandidates.filter(c=>c.addedBy===user?.uid) : allCandidates;
  const [q, setQ] = useState('');
  const placedCands = candidates.filter(c => c.stage==='Placed');
  const rows = placedCands.map(c => {
    const pl = [...placements].sort((a,b)=>(b.date||'').localeCompare(a.date||'')).find(p=>p.candidateId===c.id);
    const client = pl ? clients.find(cl=>cl.id===pl.clientId) : null;
    return {cand:c, pl, client};
  }).filter(r => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.cand.name||'').toLowerCase().includes(s) || (r.client?.name||'').toLowerCase().includes(s) || (r.client?.city||'').toLowerCase().includes(s);
  }).sort((a,b)=>(b.pl?.date||'').localeCompare(a.pl?.date||''));

  const totalPeople = placedCands.reduce((a,c)=>a+personCount(c),0);

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={placedCands.length} label="Placed candidates"/>
        <StatCard n={totalPeople} label="Total people (incl. couples)"/>
      </div>
      <SBar value={q} onChange={setQ} placeholder="Search by candidate or client..."/>
      {rows.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>📌</div>{q?'No match.':'No one is currently placed yet.'}</div>}
      {rows.map(({cand,pl,client}) => (
        <div key={cand.id} style={S.li}>
          <div style={S.av}>{initials(cand.name)}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:600,fontSize:14}}>{cand.name}{(parseInt(cand.members,10)||1)>1?` 👫${cand.members}`:''}</div>
            <div style={{fontSize:12,color:'#666',marginTop:1}}>{client ? `${client.name}${client.city?' · '+client.city:''}${client.state?', '+client.state:''}` : 'Client details unavailable'}{pl?.date?` · Placed ${pl.date}`:''}</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
              {client?.industry && <span style={S.tB}>{client.industry}</span>}
              {isAdmin && pl && <span style={S.tG}>Fee: {fmtUSD(pl.total||0)}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FOLLOW-UP REMINDERS
// ═══════════════════════════════════════════════════════════════════════════════
function RemindersTab({candidates:allCandidates, clients, setCandidates, setClients, toast, addAct, user, isAdmin}) {
  const candidates = isAdmin===false ? allCandidates.filter(c=>c.addedBy===user?.uid) : allCandidates;
  const visibleClients = isAdmin===false ? [] : clients; // employees don't have Clients access, so no client follow-ups either
  const clear = useCallback(async (type, rec) => {
    const updated = {...rec, followUpDate:'', followUpNote:''};
    await DB.save(type, rec.id, updated);
    if (type==='candidates') setCandidates(p=>p.map(c=>c.id===rec.id?updated:c));
    else setClients(p=>p.map(c=>c.id===rec.id?updated:c));
    toast('Reminder cleared ✓');
    addAct(`✅ Cleared follow-up for "${rec.name}"`);
  }, [setCandidates, setClients, toast, addAct]);

  const items = [
    ...candidates.filter(c=>c.followUpDate).map(c=>({...c, _type:'candidates', _label:'👤 Candidate'})),
    ...visibleClients.filter(c=>c.followUpDate).map(c=>({...c, _type:'clients', _label:'🏢 Client'})),
  ].sort((a,b)=> (a.followUpDate||'').localeCompare(b.followUpDate||''));

  const todayStr = new Date().toISOString().slice(0,10);
  const overdue = items.filter(i=>i.followUpDate < todayStr);
  const upcoming = items.filter(i=>i.followUpDate >= todayStr);

  const Row = ({i}) => (
    <div key={i.id+i._type} style={S.li}>
      <div style={{width:36,height:36,borderRadius:8,background:i.followUpDate<todayStr?'#FCEBEB':'#EAF3DE',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:16}}>⏰</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:600,fontSize:14}}>{i.name} <span style={{fontWeight:400,fontSize:11,color:'#999'}}>{i._label}</span></div>
        <div style={{fontSize:12,color:'#666',marginTop:1}}>{i.followUpDate} — {i.followUpNote||'No note'}</div>
      </div>
      <Btn sm onClick={()=>clear(i._type, i)}>✓ Done</Btn>
    </div>
  );

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={overdue.length} label="Overdue"/>
        <StatCard n={upcoming.length} label="Upcoming"/>
      </div>
      {items.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>⏰</div>No follow-ups set. Add one from a candidate or client's Edit form.</div>}
      {overdue.length>0 && <><div style={{fontSize:12,fontWeight:700,color:'#A32D2D',margin:'10px 0 6px'}}>OVERDUE</div>{overdue.map(i=><Row key={i.id+i._type} i={i}/>)}</>}
      {upcoming.length>0 && <><div style={{fontSize:12,fontWeight:700,color:'#666',margin:'14px 0 6px'}}>UPCOMING</div>{upcoming.map(i=><Row key={i.id+i._type} i={i}/>)}</>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVITY LOG
// ═══════════════════════════════════════════════════════════════════════════════
function ActivityTab({activity}) {
  const sorted = [...activity].sort((a,b)=> (b.ts||'').localeCompare(a.ts||''));
  return (
    <div>
      <div style={{fontSize:12,color:'#888',marginBottom:14}}>🔒 Admin only — a running feed of everything happening in your CRM</div>
      {sorted.length===0 && <div style={{textAlign:'center',padding:'40px 20px',color:'#aaa',fontSize:13}}><div style={{fontSize:32,marginBottom:8,opacity:.4}}>📋</div>No activity yet.</div>}
      {sorted.map(a => (
        <div key={a.id} style={{display:'flex',gap:10,padding:'9px 4px',borderBottom:'0.5px solid #f0efe9'}}>
          <div style={{fontSize:11,color:'#aaa',width:130,flexShrink:0}}>{new Date(a.ts).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
          <div style={{fontSize:13,color:'#333'}}>{a.text}{a.user?<span style={{color:'#999'}}> — {a.user}</span>:null}</div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MONTHLY REPORT
// ═══════════════════════════════════════════════════════════════════════════════
function ReportsTab({placements, candidates, clients}) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);

  const inMonth = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d)) return false;
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` === month;
  };
  const plsThisMonth = placements.filter(p => inMonth(p.date));
  const candsThisMonth = candidates.filter(c => inMonth(c.addedAt));
  const clientsThisMonth = clients.filter(c => inMonth(c.addedAt));
  const revenue = plsThisMonth.reduce((a,p)=>a+(p.total||0),0);
  const collected = plsThisMonth.reduce((a,p)=>a+parseFloat(p.alreadyPaid||0),0);
  const byClient = {};
  plsThisMonth.forEach(p => { byClient[p.clientName] = (byClient[p.clientName]||0) + (p.total||0); });
  const topClients = Object.entries(byClient).sort((a,b)=>b[1]-a[1]).slice(0,5);

  const exportCSV = () => {
    const rows = [['Candidate','Client','Invoice #','Date','Fee','Paid','Balance']];
    plsThisMonth.forEach(p => rows.push([p.candidateName,p.clientName,p.invNum,p.date,(p.total||0).toFixed(2),parseFloat(p.alreadyPaid||0).toFixed(2),((p.total||0)-parseFloat(p.alreadyPaid||0)).toFixed(2)]));
    const csv = rows.map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `three9-report-${month}.csv`; a.click();
  };
  const printReport = () => window.print();

  return (
    <div>
      <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16}}>
        <label style={{fontSize:12,color:'#888'}}>Month:</label>
        <input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={S.inp}/>
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <Btn sm onClick={exportCSV}>⬇ Export CSV</Btn>
          <Btn sm onClick={printReport}>🖨 Print</Btn>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={plsThisMonth.reduce((a,p)=>a+(p.headcount||1),0)} label="People placed"/>
        <StatCard n={fmtUSD(revenue)} label="Revenue billed"/>
        <StatCard n={fmtUSD(collected)} label="Collected"/>
        <StatCard n={candsThisMonth.length} label="New candidates"/>
      </div>
      <div style={S.card}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>Top clients this month</div>
        {topClients.length===0 && <div style={{fontSize:12,color:'#aaa'}}>No placements this month.</div>}
        {topClients.map(([name,amt]) => (
          <div key={name} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'0.5px solid #f0efe9',fontSize:13}}>
            <span>{name}</span><span style={{fontWeight:700,color:gr}}>{fmtUSD(amt)}</span>
          </div>
        ))}
      </div>
      <div style={S.card}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>Placements this month ({plsThisMonth.length})</div>
        {plsThisMonth.length===0 && <div style={{fontSize:12,color:'#aaa'}}>None yet.</div>}
        {plsThisMonth.map(p => (
          <div key={p.id} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'0.5px solid #f0efe9',fontSize:13}}>
            <span>{p.candidateName} → {p.clientName}</span><span style={{fontWeight:600}}>{fmtUSD(p.total||0)}</span>
          </div>
        ))}
      </div>
      <div style={{fontSize:11,color:'#aaa',marginTop:6}}>New clients added this month: {clientsThisMonth.length}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// REFERRAL PARTNERS (immigration offices etc.)
// ═══════════════════════════════════════════════════════════════════════════════
function ReferralsTab({referralPartners, setReferralPartners, referralPayables, setReferralPayables, referralSubmissions, setReferralSubmissions, candidates, setCandidates, toast, addAct, profile, placements, partnerApplications, setPartnerApplications, partnerAgreements, setPartnerAgreements, isAdmin}) {
  const [form, setForm] = useState({...RPBLANK});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [showDupes, setShowDupes] = useState(false);
  const [pendingDupe, setPendingDupe] = useState(null);
  const [reviewApp, setReviewApp] = useState(null); // application being reviewed/edited before accept
  const todayStr = new Date().toISOString().slice(0,10);

  const doSave = useCallback(async (rec) => {
    await DB.save('referralPartners', rec.id, rec);
    setReferralPartners(p => [...p, rec]);
    setForm({...RPBLANK});
    toast('Referral partner saved ✓');
    addAct(`🤝 Added referral partner "${rec.name}"`);
    if (reviewApp) {
      const updatedApp = {...reviewApp, status:'Accepted', partnerId:rec.id};
      await DB.save('partnerApplications', updatedApp.id, updatedApp);
      setPartnerApplications(p => p.map(a => a.id===reviewApp.id ? updatedApp : a));
      setReviewApp(null);
    }
  }, [setReferralPartners, toast, addAct, reviewApp, setPartnerApplications]);

  const save = useCallback(async () => {
    if (!form.name) { toast('Partner name required.'); return; }
    const dupe = form.phone ? checkDuplicate(referralPartners, form.phone, null) : null;
    const rec = {...form, id:slugify(form.name, referralPartners), addedAt:today()};
    if (dupe) { setPendingDupe({rec, existing:dupe}); return; }
    await doSave(rec);
  }, [form, toast, referralPartners, doSave]);

  const confirmDupeSave = useCallback(async () => { if(!pendingDupe) return; await doSave(pendingDupe.rec); setPendingDupe(null); }, [pendingDupe, doSave]);

  const del = useCallback(async (id) => {
    if (!window.confirm('Delete this referral partner? Existing referral fee records will remain.')) return;
    const rec = referralPartners.find(r=>r.id===id);
    await DB.delete('referralPartners', id);
    setReferralPartners(p => p.filter(r => r.id !== id));
    toast('Deleted.');
    addAct(`🗑 Deleted referral partner "${rec?.name||''}"`);
  }, [referralPartners, setReferralPartners, toast, addAct]);

  const startEdit = useCallback((r) => { setEditing(r.id); setEditForm({...r}); }, []);
  const cancelEdit = useCallback(() => setEditing(null), []);
  const saveEdit = useCallback(async () => {
    await DB.save('referralPartners', editForm.id, editForm);
    setReferralPartners(p => p.map(r => r.id === editing ? editForm : r));
    setEditing(null); toast('Updated ✓');
    addAct(`✏️ Updated referral partner "${editForm.name}"`);
  }, [editForm, editing, setReferralPartners, toast, addAct]);

  const [copiedId, setCopiedId] = useState('');
  const getBaseUrl = useCallback(() => {
    if (profile?.publicUrl) return profile.publicUrl.replace(/\/$/,'');
    if (window.location.origin && !window.location.origin.startsWith('file:')) return window.location.origin;
    return '';
  }, [profile]);
  const copyReferralLink = useCallback((rp) => {
    const base = getBaseUrl();
    if (!base) { toast('⚠ Set your Public web app URL in My Business first — see the note there.'); return; }
    const link = `${base}/refer/${rp.id}?pn=${encodeURIComponent(rp.name)}`;
    navigator.clipboard?.writeText(link);
    setCopiedId(rp.id);
    toast('Referral link copied ✓');
    setTimeout(() => setCopiedId(''), 2000);
  }, [toast, getBaseUrl]);

  const [signupCopied, setSignupCopied] = useState(false);
  const copySignupLink = useCallback(() => {
    const base = getBaseUrl();
    if (!base) { toast('⚠ Set your Public web app URL in My Business first — see the note there.'); return; }
    navigator.clipboard?.writeText(`${base}/partner-signup`);
    setSignupCopied(true);
    toast('Partner signup link copied ✓');
    setTimeout(() => setSignupCopied(false), 2000);
  }, [toast, getBaseUrl]);

  const startReviewApp = useCallback((app) => {
    setReviewApp({...app});
    setForm({...RPBLANK, name:app.name, contact:app.contact, phone:app.phone, email:app.email, address:app.address, notes:app.notes});
  }, []);
  const rejectApp = useCallback(async (app) => {
    if (!window.confirm(`Reject the application from "${app.name}"?`)) return;
    const updated = {...app, status:'Rejected'};
    await DB.save('partnerApplications', updated.id, updated);
    setPartnerApplications(p => p.map(a => a.id===app.id ? updated : a));
    toast('Application rejected.');
  }, [setPartnerApplications, toast]);

  const [agreementFor, setAgreementFor] = useState(null);
  const [receiptFor, setReceiptFor] = useState(null);

  const performance = referralPartners.map(rp => {
    const subs = referralSubmissions.filter(s=>s.partnerId===rp.id);
    const accepted = subs.filter(s=>s.status==='accepted').length;
    const refCands = candidates.filter(c=>c.referralPartnerId===rp.id);
    const refCandIds = new Set(refCands.map(c=>c.id));
    const placed = placements.filter(p=>refCandIds.has(p.candidateId));
    const revenue = placed.reduce((a,p)=>a+(p.total||0),0);
    const paid = referralPayables.filter(pb=>pb.partnerId===rp.id && pb.status==='Paid').reduce((a,pb)=>a+(parseFloat(pb.amount)||0),0);
    const owed = referralPayables.filter(pb=>pb.partnerId===rp.id && pb.status==='Pending').reduce((a,pb)=>a+(parseFloat(pb.amount)||0),0);
    return {...rp, submissions:subs.length, accepted, placed:placed.reduce((a,p)=>a+(p.headcount||1),0), revenue, paid, owed};
  }).sort((a,b)=>b.revenue-a.revenue);

  const acceptSubmission = useCallback(async (sub) => {
    const dupe = checkDuplicate(candidates, sub.phone, null);
    if (dupe && !window.confirm(`A candidate named "${dupe.name}" already has this phone number. Add anyway?`)) return;
    const rec = {...CBLANK, name:sub.name, phone:sub.phone, email:sub.email||'', industry:sub.industry||'',
      originCountry:sub.originCountry||'', notes:sub.notes||'', referralPartnerId:sub.partnerId||'',
      referralPartnerName:sub.partnerName||'', id:uid(), addedAt:today()};
    await DB.save('candidates', rec.id, rec);
    setCandidates(p => [...p, rec]);
    const updatedSub = {...sub, status:'accepted', candidateId:rec.id};
    await DB.save('referralSubmissions', updatedSub.id, updatedSub);
    setReferralSubmissions(p => p.map(s => s.id===sub.id ? updatedSub : s));
    toast(`Added "${sub.name}" as a candidate ✓`);
    addAct(`📥 Accepted referral submission — "${sub.name}" from "${sub.partnerName}"`);
  }, [candidates, setCandidates, setReferralSubmissions, toast, addAct]);

  const dismissSubmission = useCallback(async (sub) => {
    if (!window.confirm(`Dismiss this referral submission for "${sub.name}"?`)) return;
    const updated = {...sub, status:'dismissed'};
    await DB.save('referralSubmissions', updated.id, updated);
    setReferralSubmissions(p => p.map(s => s.id===sub.id ? updated : s));
    toast('Dismissed.');
  }, [setReferralSubmissions, toast]);

  const confirmOwed = useCallback(async (payable) => {
    const updated = {...payable, status:'Pending', confirmedDate:today()};
    await DB.save('referralPayables', updated.id, updated);
    setReferralPayables(p => p.map(x => x.id===payable.id ? updated : x));
    toast('Confirmed — fee now owed ✓');
    addAct(`✅ Confirmed 30-day placement — ${fmtUSD(payable.amount)} now owed to "${payable.partnerName}"`);
  }, [setReferralPayables, toast, addAct]);

  const markFellThrough = useCallback(async (payable) => {
    if (!window.confirm(`Mark this placement as fell through? No fee will be owed to ${payable.partnerName}.`)) return;
    const updated = {...payable, status:'Cancelled', cancelledDate:today()};
    await DB.save('referralPayables', updated.id, updated);
    setReferralPayables(p => p.map(x => x.id===payable.id ? updated : x));
    toast('Marked as fell through.');
    addAct(`❌ Placement fell through — referral fee to "${payable.partnerName}" cancelled`);
  }, [setReferralPayables, toast, addAct]);

  const markPaid = useCallback(async (payable) => {
    const updated = {...payable, status:'Paid', paidDate:today()};
    await DB.save('referralPayables', updated.id, updated);
    setReferralPayables(p => p.map(x => x.id===payable.id ? updated : x));
    toast('Marked as paid ✓');
    addAct(`💵 Paid referral fee ${fmtUSD(payable.amount)} to "${payable.partnerName}"`);
  }, [setReferralPayables, toast, addAct]);

  const scheduled = referralPayables.filter(p=>p.status==='Scheduled');
  const awaitingConfirm = scheduled.filter(p=>p.dueDate<=todayStr);
  const owed = referralPayables.filter(p=>p.status==='Pending');
  const overdue = owed.filter(p=>p.dueDate < todayStr);
  const totalOwed = owed.reduce((a,p)=>a+(parseFloat(p.amount)||0),0);
  const dupeCount = findDuplicateGroups(referralPartners).length;
  const pendingApps = partnerApplications.filter(a=>a.status==='New');
  const pendingSubs = referralSubmissions.filter(s=>s.status==='New');
  const statusMeta = {
    Scheduled: {bg:'#FAEEDA', icon:'⏳', label: p=>`Awaiting 30-day confirmation · due ${p.dueDate}`},
    Pending:   {bg:'#E6F1FB', icon:'🤝', label: p=>`Confirmed — owed · due ${p.dueDate}${p.dueDate<todayStr?' — OVERDUE':''}`},
    Paid:      {bg:'#EAF3DE', icon:'✓',  label: p=>`Paid ${p.paidDate}`},
    Cancelled: {bg:'#f0efe9', icon:'✕',  label: p=>`Placement fell through`},
  };

  if (isAdmin===false) {
    return (
      <div>
        <div style={{fontSize:12,color:'#888',marginBottom:14}}>View partners, share referral links, review new submissions, and send agreements. Fee amounts and payment details are visible to admins only.</div>
        <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>Partners & employees ({referralPartners.length})</div>
        {referralPartners.length===0 && <div style={{textAlign:'center',padding:'30px 20px',color:'#aaa',fontSize:13}}>No referral partners yet.</div>}
        {referralPartners.map(rp => (
          <div key={rp.id} style={S.li}>
            <div style={S.avC}>{initials(rp.name)}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:14}}>{rp.name} <span style={rp.refType==='employee'?S.tGr:S.tB}>{rp.refType==='employee'?'👤 Employee':'🏛 External partner'}</span></div>
              <div style={{fontSize:12,color:'#666',marginTop:1}}>{rp.contact||'—'}{rp.phone?' · '+rp.phone:''}</div>
            </div>
            <div style={{display:'flex',gap:5,flexShrink:0}}>
              {rp.refType!=='employee' && <Btn sm onClick={()=>copyReferralLink(rp)}>{copiedId===rp.id?'✓ Copied':'🔗 Copy link'}</Btn>}
              <Btn sm onClick={()=>setAgreementFor(rp)}>{partnerAgreements.some(a=>a.partnerId===rp.id)?'✅ Agreement':'📜 Agreement'}</Btn>
            </div>
          </div>
        ))}
        {pendingSubs.length>0 && (
          <div>
            <div style={{fontSize:13,fontWeight:700,margin:'20px 0 8px'}}>📥 New referral submissions ({pendingSubs.length})</div>
            {[...pendingSubs].sort((a,b)=>(b.submittedAt||'').localeCompare(a.submittedAt||'')).map(sub => (
              <div key={sub.id} style={{...S.li, background:'#E6F1FB', border:'0.5px solid #c7dcf0'}}>
                <div style={S.av}>{initials(sub.name)}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:14}}>{sub.name}</div>
                  <div style={{fontSize:12,color:'#666',marginTop:1}}>{sub.phone}{sub.email?' · '+sub.email:''}{sub.industry?' · '+sub.industry:''}</div>
                  <div style={{fontSize:11,color:'#0C447C',marginTop:3}}>Via {sub.partnerName} · {fmtDateTime(sub.submittedAt)}</div>
                  {sub.notes && <div style={{fontSize:12,color:'#666',marginTop:3,fontStyle:'italic'}}>"{sub.notes}"</div>}
                </div>
                <div style={{display:'flex',gap:5,flexShrink:0}}>
                  <Btn sm onClick={()=>acceptSubmission(sub)}>✅ Add as candidate</Btn>
                  <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>dismissSubmission(sub)}>Dismiss</Btn>
                </div>
              </div>
            ))}
          </div>
        )}
        {agreementFor && (
          <PartnerAgreementModal partner={agreementFor} profile={profile}
            existingAgreement={partnerAgreements.find(a=>a.partnerId===agreementFor.id)}
            onClose={()=>setAgreementFor(null)} addAct={addAct} allowSign={false}
            onSigned={(rec)=>setPartnerAgreements(ps => ps.some(a=>a.id===rec.id) ? ps.map(a=>a.id===rec.id?rec:a) : [...ps, rec])}/>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{fontSize:12,color:'#888',marginBottom:14}}>🔒 Admin only. Add external partners (immigration offices etc.) or employees earning an incentive for candidates they bring in. Share a partner's referral link so they can submit candidates directly — employees just get selected under "Referred by" when you add the candidate. When a referred candidate is placed, a fee/incentive is scheduled here; confirm it 30 days after placement to make it officially owed.</div>

      <div style={{background:'#E6F1FB',border:'0.5px solid #c7dcf0',borderRadius:10,padding:'12px 14px',marginBottom:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:13,fontWeight:700,color:'#0C447C'}}>📋 Let new partners register themselves</div>
          <div style={{fontSize:11,color:'#0C447C',marginTop:2}}>Share this link so new immigration offices can apply — applications land below for your review.</div>
        </div>
        <Btn sm onClick={copySignupLink}>{signupCopied?'✓ Copied':'🔗 Copy signup link'}</Btn>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={referralPartners.length} label="Partners"/>
        <StatCard n={pendingApps.length} label="New applications"/>
        <StatCard n={pendingSubs.length} label="New submissions"/>
        <StatCard n={overdue.length} label="Overdue payments"/>
        <StatCard n={fmtUSD(totalOwed)} label="Confirmed & owed"/>
      </div>

      {pendingApps.length>0 && (
        <div>
          <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>📋 New partner applications ({pendingApps.length})</div>
          {[...pendingApps].sort((a,b)=>(b.submittedAt||'').localeCompare(a.submittedAt||'')).map(app => (
            <div key={app.id} style={{...S.li, background:'#FAEEDA', border:'0.5px solid #f0dcb0'}}>
              <div style={S.avC}>{initials(app.name)}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{app.name}</div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{app.contact?app.contact+' · ':''}{app.phone}{app.email?' · '+app.email:''}</div>
                {app.address && <div style={{fontSize:12,color:'#666',marginTop:1}}>{app.address}</div>}
                <div style={{fontSize:11,color:'#7a5a10',marginTop:3}}>Applied {fmtDateTime(app.submittedAt)}</div>
                {app.notes && <div style={{fontSize:12,color:'#666',marginTop:3,fontStyle:'italic'}}>"{app.notes}"</div>}
              </div>
              <div style={{display:'flex',gap:5,flexShrink:0}}>
                <Btn sm onClick={()=>startReviewApp(app)}>✅ Review & accept</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>rejectApp(app)}>Reject</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={S.card}>
        <div style={{fontSize:15,fontWeight:600,marginBottom:3}}>{reviewApp ? `Reviewing application: ${reviewApp.name}` : 'Add referrer'}</div>
        <div style={{fontSize:12,color:'#888',marginBottom:14}}>{reviewApp ? 'Confirm details and set the fee terms below, then accept to add them as a partner.' : 'An external partner (e.g. immigration office) or an internal employee earning an incentive for candidates they bring in'}</div>
        <Toggle label="Type" value={form.refType==='employee'?'Employee':'External partner'} onChange={v=>setForm(f=>({...f,refType: v==='Employee'?'employee':'partner'}))} options={['External partner','Employee']}/>
        <div style={S.fg2}>
          <FG label={form.refType==='employee'?'Employee name *':'Partner name *'}><Inp value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder={form.refType==='employee'?'Priya Shah':'ABC Immigration Services'}/></FG>
          <FG label="Contact person"><Inp value={form.contact} onChange={e=>setForm(f=>({...f,contact:e.target.value}))}/></FG>
          <FG label="Phone"><PhoneInput value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="(555) 000-0000"/></FG>
          <FG label="Email"><Inp value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/></FG>
          <FG label="Address"><Inp value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} placeholder="Street, city, state"/></FG>
          <FG label="Fee type"><Sel value={form.feeType} onChange={e=>setForm(f=>({...f,feeType:e.target.value}))}><option value="flat">Flat fee per placement</option><option value="percent">% of placement fee</option></Sel></FG>
          {form.feeType==='flat'
            ? <FG label={form.refType==='employee'?'Incentive ($)':'Flat fee ($)'}><Inp type="number" min={0} value={form.feeAmount} onChange={e=>setForm(f=>({...f,feeAmount:e.target.value}))}/></FG>
            : <FG label={form.refType==='employee'?'Incentive (% of placement fee)':'Fee (% of placement fee)'}><Inp type="number" min={0} max={100} value={form.feePercent} onChange={e=>setForm(f=>({...f,feePercent:e.target.value}))}/></FG>}
        </div>
        <FeeRulesEditor rules={form.feeRules} onChange={rules=>setForm(f=>({...f,feeRules:rules}))} refType={form.refType}/>
        <FG label="Notes" style={{marginTop:10}}><TA value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Agreement details, region covered..."/></FG>
        <div style={{display:'flex',gap:8,marginTop:14}}>
          <Btn prim onClick={save}>{reviewApp ? '✅ Accept & add partner' : `Add ${form.refType==='employee'?'employee':'partner'}`}</Btn>
          {reviewApp && <Btn onClick={()=>{setReviewApp(null); setForm({...RPBLANK});}}>Cancel review</Btn>}
        </div>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:14}}>
        <Btn sm onClick={()=>setShowDupes(true)}>🔍 Scan duplicates{dupeCount>0?` (${dupeCount})`:''}</Btn>
      </div>
      {pendingSubs.length>0 && (
        <div>
          <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>📥 New referral submissions ({pendingSubs.length})</div>
          {[...pendingSubs].sort((a,b)=>(b.submittedAt||'').localeCompare(a.submittedAt||'')).map(sub => (
            <div key={sub.id} style={{...S.li, background:'#E6F1FB', border:'0.5px solid #c7dcf0'}}>
              <div style={S.av}>{initials(sub.name)}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{sub.name}</div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{sub.phone}{sub.email?' · '+sub.email:''}{sub.industry?' · '+sub.industry:''}{sub.originCountry?' · From: '+sub.originCountry:''}</div>
                <div style={{fontSize:11,color:'#0C447C',marginTop:3}}>Via {sub.partnerName} · {fmtDateTime(sub.submittedAt)}</div>
                {sub.notes && <div style={{fontSize:12,color:'#666',marginTop:3,fontStyle:'italic'}}>"{sub.notes}"</div>}
              </div>
              <div style={{display:'flex',gap:5,flexShrink:0}}>
                <Btn sm onClick={()=>acceptSubmission(sub)}>✅ Add as candidate</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>dismissSubmission(sub)}>Dismiss</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{fontSize:13,fontWeight:700,margin:'6px 0 8px'}}>Partners ({referralPartners.length})</div>
      {referralPartners.length===0 && <div style={{textAlign:'center',padding:'30px 20px',color:'#aaa',fontSize:13}}>No referral partners yet.</div>}
      {referralPartners.map(rp => (
        <div key={rp.id}>
          {editing===rp.id ? (
            <div style={S.card}>
              <Toggle label="Type" value={editForm.refType==='employee'?'Employee':'External partner'} onChange={v=>setEditForm(f=>({...f,refType: v==='Employee'?'employee':'partner'}))} options={['External partner','Employee']}/>
              <div style={S.fg2}>
                <FG label="Name"><Inp value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))}/></FG>
                <FG label="Contact person"><Inp value={editForm.contact} onChange={e=>setEditForm(f=>({...f,contact:e.target.value}))}/></FG>
                <FG label="Phone"><PhoneInput value={editForm.phone} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))} placeholder="(555) 000-0000"/></FG>
                <FG label="Email"><Inp value={editForm.email} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))}/></FG>
                <FG label="Address"><Inp value={editForm.address||''} onChange={e=>setEditForm(f=>({...f,address:e.target.value}))}/></FG>
                <FG label="Fee type"><Sel value={editForm.feeType} onChange={e=>setEditForm(f=>({...f,feeType:e.target.value}))}><option value="flat">Flat fee per placement</option><option value="percent">% of placement fee</option></Sel></FG>
                {editForm.feeType==='flat'
                  ? <FG label="Amount ($)"><Inp type="number" min={0} value={editForm.feeAmount} onChange={e=>setEditForm(f=>({...f,feeAmount:e.target.value}))}/></FG>
                  : <FG label="Amount (%)"><Inp type="number" min={0} max={100} value={editForm.feePercent} onChange={e=>setEditForm(f=>({...f,feePercent:e.target.value}))}/></FG>}
              </div>
              <FeeRulesEditor rules={editForm.feeRules} onChange={rules=>setEditForm(f=>({...f,feeRules:rules}))} refType={editForm.refType}/>
              <div style={{display:'flex',gap:8,marginTop:14}}><Btn prim onClick={saveEdit}>Save changes</Btn><Btn onClick={cancelEdit}>Cancel</Btn></div>
            </div>
          ) : (
            <div style={S.li}>
              <div style={S.avC}>{initials(rp.name)}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{rp.name} <span style={rp.refType==='employee'?S.tGr:S.tB}>{rp.refType==='employee'?'👤 Employee':'🏛 External partner'}</span></div>
                <div style={{fontSize:12,color:'#666',marginTop:1}}>{rp.contact||'—'}{rp.phone?' · '+rp.phone:''} · {rp.feeType==='flat'?`Flat ${fmtUSD(rp.feeAmount||0)}`:`${rp.feePercent||0}% of placement fee`}{rp.feeRules&&rp.feeRules.length>0?` · ${rp.feeRules.length} category override${rp.feeRules.length>1?'s':''}`:''}</div>
                {rp.refType==='employee'
                  ? <div style={{fontSize:11,color:'#888',marginTop:3}}>Internal — select "{rp.name}" under "Referred by" when adding a candidate</div>
                  : <div style={{fontSize:11,color:getBaseUrl()?bl:'#c99',marginTop:3,fontFamily:'monospace'}}>{getBaseUrl() ? `${getBaseUrl()}/refer/${rp.id}` : `⚠ Set Public web app URL in My Business to activate this link`}</div>}
              </div>
              <div style={{display:'flex',gap:5,flexShrink:0}}>
                {rp.refType!=='employee' && <Btn sm onClick={()=>copyReferralLink(rp)}>{copiedId===rp.id?'✓ Copied':'🔗 Copy link'}</Btn>}
                <Btn sm onClick={()=>setAgreementFor(rp)}>{partnerAgreements.some(a=>a.partnerId===rp.id)?'✅ Agreement':'📜 Agreement'}</Btn>
                <Btn sm onClick={()=>startEdit(rp)}>✏️ Edit</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>del(rp.id)}>🗑</Btn>
              </div>
            </div>
          )}
        </div>
      ))}

      <div style={{fontSize:13,fontWeight:700,margin:'20px 0 8px'}}>📊 Partner & employee performance</div>
      {performance.length===0 && <div style={{textAlign:'center',padding:'20px',color:'#aaa',fontSize:13}}>Add a partner or employee above to start tracking performance.</div>}
      {performance.length>0 && (
        <div style={{overflowX:'auto',border:`0.5px solid ${br}`,borderRadius:10,marginBottom:20}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
            <thead><tr style={{background:'#f8f8f4'}}>
              <th style={{padding:'8px 10px',textAlign:'left',borderBottom:`0.5px solid ${br}`}}>Name</th>
              <th style={{padding:'8px 10px',textAlign:'center',borderBottom:`0.5px solid ${br}`}}>Submissions</th>
              <th style={{padding:'8px 10px',textAlign:'center',borderBottom:`0.5px solid ${br}`}}>Accepted</th>
              <th style={{padding:'8px 10px',textAlign:'center',borderBottom:`0.5px solid ${br}`}}>Placed</th>
              <th style={{padding:'8px 10px',textAlign:'right',borderBottom:`0.5px solid ${br}`}}>Revenue generated</th>
              <th style={{padding:'8px 10px',textAlign:'right',borderBottom:`0.5px solid ${br}`}}>Paid out</th>
              <th style={{padding:'8px 10px',textAlign:'right',borderBottom:`0.5px solid ${br}`}}>Owed now</th>
            </tr></thead>
            <tbody>
              {performance.map(p => (
                <tr key={p.id}>
                  <td style={{padding:'8px 10px',borderBottom:'0.5px solid #f0efe9'}}>{p.name} <span style={{fontSize:10,color:'#999'}}>{p.refType==='employee'?'👤':'🏛'}</span></td>
                  <td style={{padding:'8px 10px',textAlign:'center',borderBottom:'0.5px solid #f0efe9'}}>{p.submissions}</td>
                  <td style={{padding:'8px 10px',textAlign:'center',borderBottom:'0.5px solid #f0efe9'}}>{p.accepted}</td>
                  <td style={{padding:'8px 10px',textAlign:'center',borderBottom:'0.5px solid #f0efe9'}}>{p.placed}</td>
                  <td style={{padding:'8px 10px',textAlign:'right',borderBottom:'0.5px solid #f0efe9',fontWeight:600,color:gr}}>{fmtUSD(p.revenue)}</td>
                  <td style={{padding:'8px 10px',textAlign:'right',borderBottom:'0.5px solid #f0efe9'}}>{fmtUSD(p.paid)}</td>
                  <td style={{padding:'8px 10px',textAlign:'right',borderBottom:'0.5px solid #f0efe9',color:p.owed>0?rd:'#888'}}>{fmtUSD(p.owed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{fontSize:13,fontWeight:700,margin:'20px 0 8px'}}>Referral fees ({owed.length} owed, {awaitingConfirm.length} awaiting confirmation)</div>
      {referralPayables.length===0 && <div style={{textAlign:'center',padding:'20px',color:'#aaa',fontSize:13}}>No referral fees yet — these appear automatically when a referred candidate gets placed.</div>}
      {[...referralPayables].sort((a,b)=>(a.dueDate||'').localeCompare(b.dueDate||'')).map(pb => {
        const meta = statusMeta[pb.status] || statusMeta.Scheduled;
        return (
          <div key={pb.id} style={S.li}>
            <div style={{width:36,height:36,borderRadius:8,background:meta.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:16}}>{meta.icon}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:14}}>{pb.partnerName} — {fmtUSD(pb.amount)}</div>
              <div style={{fontSize:12,color:'#666',marginTop:1}}>For {pb.candidateName} → {pb.clientName} · Placed {pb.placedDate}</div>
              <div style={{fontSize:11,marginTop:3,color:pb.status==='Paid'?'#3B6D11':(pb.status==='Cancelled'?'#999':(pb.dueDate<todayStr?rd:'#888'))}}>{meta.label(pb)}</div>
            </div>
            {pb.status==='Scheduled' && pb.dueDate<=todayStr && (
              <div style={{display:'flex',gap:5,flexShrink:0}}>
                <Btn sm onClick={()=>confirmOwed(pb)}>✅ Confirm owed</Btn>
                <Btn sm style={{color:rd,borderColor:rd}} onClick={()=>markFellThrough(pb)}>Fell through</Btn>
              </div>
            )}
            {pb.status==='Scheduled' && pb.dueDate>todayStr && <div style={{fontSize:11,color:'#aaa',flexShrink:0}}>Confirm on {pb.dueDate}</div>}
            {(pb.status==='Pending'||pb.status==='Paid') && (
              <div style={{display:'flex',gap:5,flexShrink:0}}>
                <Btn sm onClick={()=>setReceiptFor(pb)}>🧾 Receipt</Btn>
                {pb.status==='Pending' && <Btn sm onClick={()=>markPaid(pb)}>✓ Mark paid</Btn>}
              </div>
            )}
          </div>
        );
      })}
      {showDupes && <DuplicateModal list={referralPartners} labelKey="name" onClose={()=>setShowDupes(false)}/>}
      {pendingDupe && (
        <Modal onClose={()=>setPendingDupe(null)} maxWidth={420}>
          <div style={{fontSize:15,fontWeight:700,marginBottom:8}}>⚠ Possible duplicate</div>
          <div style={{fontSize:13,color:'#555',marginBottom:14}}>A partner named <strong>{pendingDupe.existing.name}</strong> already has phone number <strong>{pendingDupe.existing.phone}</strong>. Save anyway?</div>
          <div style={{display:'flex',gap:8}}>
            <Btn prim onClick={confirmDupeSave}>Save anyway</Btn>
            <Btn onClick={()=>setPendingDupe(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
      {agreementFor && (
        <PartnerAgreementModal partner={agreementFor} profile={profile}
          existingAgreement={partnerAgreements.find(a=>a.partnerId===agreementFor.id)}
          onClose={()=>setAgreementFor(null)}
          addAct={addAct}
          onSigned={(rec)=>setPartnerAgreements(ps => ps.some(a=>a.id===rec.id) ? ps.map(a=>a.id===rec.id?rec:a) : [...ps, rec])}/>
      )}
      {receiptFor && (
        <PayoutReceiptModal payable={receiptFor} profile={profile} onClose={()=>setReceiptFor(null)}/>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
function DashboardTab({candidates, clients, placements, activity, referralPayables, user, clientAgreements=[], candidateSubmissions=[], clientSubmissions=[], waChats=[], partnerApplications=[], referralSubmissions=[], goTab}) {
  const now = new Date();
  const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  const thisMonth = monthKey(now);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth()-1, 1);
  const lastMonth = monthKey(lastMonthDate);

  const plMonth = (p) => { const d = new Date(p.date); return isNaN(d)?'':monthKey(d); };
  const revThis = placements.filter(p=>plMonth(p)===thisMonth).reduce((a,p)=>a+(p.total||0),0);
  const revLast = placements.filter(p=>plMonth(p)===lastMonth).reduce((a,p)=>a+(p.total||0),0);
  const delta = revLast>0 ? Math.round(((revThis-revLast)/revLast)*100) : (revThis>0?100:0);

  const months = [];
  for (let i=5;i>=0;i--){ const d = new Date(now.getFullYear(), now.getMonth()-i, 1); months.push({key:monthKey(d), label:d.toLocaleDateString('en-US',{month:'short'})}); }
  const monthTotals = months.map(m => ({...m, total: placements.filter(p=>plMonth(p)===m.key).reduce((a,p)=>a+(p.total||0),0)}));
  const maxTotal = Math.max(1, ...monthTotals.map(m=>m.total));

  const stageCounts = PIPELINE_STAGES.map(s => ({stage:s, n: candidates.filter(c=>(c.stage||'New')===s).reduce((a,c)=>a+personCount(c),0)}));
  const maxStage = Math.max(1, ...stageCounts.map(s=>s.n));

  const todayStr = now.toISOString().slice(0,10);
  const followUpsDue = [...candidates, ...clients].filter(c=>c.followUpDate && c.followUpDate<=addDaysISO(now,7)).length;
  const referralOwed = referralPayables.filter(p=>p.status==='Pending').reduce((a,p)=>a+(parseFloat(p.amount)||0),0);
  const referralOverdue = referralPayables.filter(p=>p.status==='Pending' && p.dueDate<todayStr).length;
  const referralAwaiting = referralPayables.filter(p=>p.status==='Scheduled' && p.dueDate<=todayStr).length;
  const recentActivity = [...activity].sort((a,b)=>(b.ts||'').localeCompare(a.ts||'')).slice(0,6);

  // "Needs attention": things that otherwise sit unnoticed
  const unpaid = placements.filter(p => ((p.total||0) - parseFloat(p.alreadyPaid||0)) > 0.01);
  const unpaidTotal = unpaid.reduce((a,p)=>a+((p.total||0)-parseFloat(p.alreadyPaid||0)),0);
  const overdueInv = unpaid.filter(p => (p.dueDate||p.date) && (p.dueDate||p.date) < todayStr).length;
  const unsignedClients = clients.filter(cl => { const a = clientAgreements.find(x=>x.clientId===cl.id); return !a || !a.signedAt; }).length;
  const newCandSubs = candidateSubmissions.filter(x=>(x.status||'New')==='New').length;
  const newClientSubs = clientSubmissions.filter(x=>(x.status||'New')==='New').length;
  const waUnread = waChats.filter(c=>(c.unread||0)>0).length;
  const newPartnerApps = partnerApplications.filter(x=>(x.status||'New')==='New').length;
  const newReferralSubs = referralSubmissions.filter(x=>(x.status||'New')==='New').length;
  const attention = [
    unpaid.length>0 && {k:'inv', tab:'placements', icon:'💵', text:`${unpaid.length} unpaid invoice${unpaid.length===1?'':'s'} — ${fmtUSD(unpaidTotal)} outstanding${overdueInv?` (${overdueInv} overdue)`:''}`, hot:overdueInv>0},
    unsignedClients>0 && {k:'ctr', tab:'clients', icon:'📜', text:`${unsignedClients} client${unsignedClients===1?'':'s'} haven't signed their contract`},
    newCandSubs>0 && {k:'cs', tab:'candidates', icon:'🆕', text:`${newCandSubs} new candidate sign-up${newCandSubs===1?'':'s'} to review`, hot:true},
    waUnread>0 && {k:'wa', tab:'whatsapp', icon:'💬', text:`${waUnread} WhatsApp conversation${waUnread===1?'':'s'} waiting for a reply`, hot:true},
    newClientSubs>0 && {k:'cl', tab:'clients', icon:'🏢', text:`${newClientSubs} new client request${newClientSubs===1?'':'s'} to review`, hot:true},
    newReferralSubs>0 && {k:'rs', tab:'referrals', icon:'🤝', text:`${newReferralSubs} new referred candidate${newReferralSubs===1?'':'s'} to review`, hot:true},
    newPartnerApps>0 && {k:'pa', tab:'referrals', icon:'📝', text:`${newPartnerApps} new partner application${newPartnerApps===1?'':'s'}`, hot:true},
    referralAwaiting>0 && {k:'ra', tab:'referrals', icon:'⏳', text:`${referralAwaiting} referral fee${referralAwaiting===1?'':'s'} ready to confirm (30 days passed)`, hot:true},
    referralOverdue>0 && {k:'ro', tab:'referrals', icon:'⚠️', text:`${referralOverdue} referral payment${referralOverdue===1?'':'s'} overdue`, hot:true},
    followUpsDue>0 && {k:'fu', tab:'reminders', icon:'⏰', text:`${followUpsDue} follow-up${followUpsDue===1?'':'s'} due in the next 7 days`},
  ].filter(Boolean);

  return (
    <div>
      <div style={{fontSize:15,fontWeight:700,marginBottom:2}}>Welcome back{user?.name?', '+user.name.split(' ')[0]:''} 👋</div>
      <div style={{fontSize:12,color:'#888',marginBottom:16}}>{now.toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>

      <div style={{background:attention.length?'#FFF8E6':'#EAF3DE',border:`0.5px solid ${attention.length?'#f0dcb0':'#cde3b0'}`,borderRadius:12,padding:'12px 16px',marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:700,marginBottom:attention.length?8:0}}>{attention.length?`🔔 Needs attention (${attention.length})`:'✅ All caught up — nothing needs attention'}</div>
        {attention.map(a => (
          <div key={a.k} onClick={()=>goTab&&goTab(a.tab)} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0',borderTop:'0.5px solid #f0dcb0',cursor:goTab?'pointer':'default',fontSize:12.5,fontWeight:a.hot?600:400,color:a.hot?'#7a4a00':tx}}>
            <span>{a.icon}</span><span style={{flex:1}}>{a.text}</span><span style={{color:'#b07a10'}}>›</span>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={fmtUSD(revThis)} label={`Revenue this month ${delta!==0?(delta>0?`▲${delta}%`:`▼${Math.abs(delta)}%`):''}`}/>
        <StatCard n={placements.reduce((a,p)=>a+(p.headcount||1),0)} label="People placed"/>
        <StatCard n={candidates.filter(c=>(c.stage||'New')!=='Placed'&&(c.stage||'New')!=='Rejected').reduce((a,c)=>a+personCount(c),0)} label="Active pipeline"/>
        <StatCard n={candidates.filter(c=>c.stage==='Interviewing').reduce((a,c)=>a+personCount(c),0)} label="Ongoing interviews"/>
        <StatCard n={followUpsDue} label="Follow-ups due (7d)"/>
      </div>

      <div style={S.card}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Revenue — last 6 months</div>
        <div style={{display:'flex',alignItems:'flex-end',gap:14,height:120}}>
          {monthTotals.map(m => (
            <div key={m.key} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
              <div style={{fontSize:10,color:'#888'}}>{m.total>0?fmtUSD(m.total):''}</div>
              <div style={{width:'100%',maxWidth:36,height:Math.max(4,(m.total/maxTotal)*90),background:bl,borderRadius:'4px 4px 0 0'}}/>
              <div style={{fontSize:11,color:'#666'}}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Pipeline funnel</div>
        {stageCounts.map(s => (
          <div key={s.stage} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
            <div style={{width:100,fontSize:12,color:'#555',flexShrink:0}}>{s.stage}</div>
            <div style={{flex:1,background:'#f0efe9',borderRadius:5,height:18,overflow:'hidden'}}>
              <div style={{width:`${(s.n/maxStage)*100}%`,height:'100%',background:STAGE_BG[s.stage],borderRadius:5,display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:6}}/>
            </div>
            <div style={{width:24,fontSize:12,fontWeight:700,textAlign:'right'}}>{s.n}</div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>Referral fees</div>
          <div style={{fontSize:22,fontWeight:800,color:bl}}>{fmtUSD(referralOwed)}</div>
          <div style={{fontSize:12,color:'#888',marginTop:2}}>confirmed & owed{referralOverdue>0?` · ${referralOverdue} overdue`:''}{referralAwaiting>0?` · ${referralAwaiting} awaiting confirmation`:''}</div>
        </div>
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>Recent activity</div>
          {recentActivity.length===0 && <div style={{fontSize:12,color:'#aaa'}}>Nothing yet.</div>}
          {recentActivity.map(a => <div key={a.id} style={{fontSize:12,color:'#555',padding:'3px 0',borderBottom:'0.5px solid #f0efe9'}}>{a.text}</div>)}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EMPLOYEE'S OWN DASHBOARD (no company-wide/admin data)
// ═══════════════════════════════════════════════════════════════════════════════
function MyDashboardTab({candidates:allCandidates, placements, referralPartners, referralPayables, user}) {
  const candidates = allCandidates.filter(c=>c.addedBy===user?.uid);
  const now = new Date();
  const todayStr = now.toISOString().slice(0,10);

  // Match this employee to their "employee" referrer entry (linked by name, or employeeId if set)
  const myReferrerEntry = referralPartners.find(rp =>
    rp.refType==='employee' && (
      (user?.employeeId && rp.employeeId && rp.employeeId===user.employeeId) ||
      (rp.name||'').trim().toLowerCase() === (user?.name||'').trim().toLowerCase()
    )
  );

  const myReferred = myReferrerEntry ? candidates.filter(c=>c.referralPartnerId===myReferrerEntry.id) : [];
  const myReferredIds = new Set(myReferred.map(c=>c.id));
  const myPlaced = placements.filter(p=>myReferredIds.has(p.candidateId));
  const myPayables = myReferrerEntry ? referralPayables.filter(p=>p.partnerId===myReferrerEntry.id) : [];
  const myOwed = myPayables.filter(p=>p.status==='Pending').reduce((a,p)=>a+(parseFloat(p.amount)||0),0);
  const myEarned = myPayables.filter(p=>p.status==='Paid').reduce((a,p)=>a+(parseFloat(p.amount)||0),0);
  const myAwaitingConfirm = myPayables.filter(p=>p.status==='Scheduled' && p.dueDate<=todayStr).length;

  const stageCounts = PIPELINE_STAGES.map(s => ({stage:s, n: candidates.filter(c=>(c.stage||'New')===s).reduce((a,c)=>a+personCount(c),0)}));
  const maxStage = Math.max(1, ...stageCounts.map(s=>s.n));

  return (
    <div>
      <div style={{fontSize:15,fontWeight:700,marginBottom:2}}>Welcome back{user?.name?', '+user.name.split(' ')[0]:''} 👋</div>
      <div style={{fontSize:12,color:'#888',marginBottom:16}}>{now.toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}{user?.employeeId?` · ${user.employeeId}`:''}</div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,marginBottom:16}}>
        <StatCard n={candidates.length} label="Candidates you've added"/>
        <StatCard n={candidates.filter(c=>(c.stage||'New')!=='Placed'&&(c.stage||'New')!=='Rejected').reduce((a,c)=>a+personCount(c),0)} label="Active pipeline"/>
      </div>

      <div style={S.card}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:14}}>Pipeline funnel</div>
        {stageCounts.map(s => (
          <div key={s.stage} style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
            <div style={{width:100,fontSize:12,color:'#555',flexShrink:0}}>{s.stage}</div>
            <div style={{flex:1,background:'#f0efe9',borderRadius:5,height:18,overflow:'hidden'}}>
              <div style={{width:`${(s.n/maxStage)*100}%`,height:'100%',background:STAGE_BG[s.stage],borderRadius:5}}/>
            </div>
            <div style={{width:24,fontSize:12,fontWeight:700,textAlign:'right'}}>{s.n}</div>
          </div>
        ))}
      </div>

      {myReferrerEntry ? (
        <div style={S.card}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>🤝 Your referral incentive</div>
          <div style={{fontSize:11,color:'#888',marginBottom:12}}>Candidates you've referred that get placed and stay 30 days earn you an incentive.</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,marginBottom:10}}>
            <StatCard n={myReferred.length} label="You referred"/>
            <StatCard n={myPlaced.reduce((a,p)=>a+(p.headcount||1),0)} label="Placed"/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10}}>
            <div style={{background:'#EAF3DE',borderRadius:8,padding:'10px 12px'}}>
              <div style={{fontSize:18,fontWeight:800,color:'#3B6D11'}}>{fmtUSD(myEarned)}</div>
              <div style={{fontSize:11,color:'#3B6D11'}}>Earned so far</div>
            </div>
            <div style={{background:'#FAEEDA',borderRadius:8,padding:'10px 12px'}}>
              <div style={{fontSize:18,fontWeight:800,color:'#7a5a10'}}>{fmtUSD(myOwed)}</div>
              <div style={{fontSize:11,color:'#7a5a10'}}>Confirmed, awaiting payout{myAwaitingConfirm>0?` · ${myAwaitingConfirm} more awaiting confirmation`:''}</div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{fontSize:12,color:'#aaa',textAlign:'center',padding:'12px 0'}}>You're not set up as a referral incentive earner yet — ask your admin to add you under Referrals if that applies to you.</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════════════════════
const ALL_TABS = [
  {id:'dashboard',  label:'Dashboard',  icon:'🏠', roles:['admin','employee']},
  {id:'candidates', label:'Candidates', icon:'👤', roles:['admin','employee']},
  {id:'pipeline',   label:'Pipeline',   icon:'📊', roles:['admin','employee']},
  {id:'clients',    label:'Clients',    icon:'🏢', roles:['admin']},
  {id:'match',      label:'Match',      icon:'⚡', roles:['admin','employee']},
  {id:'priority',   label:'Priority',   icon:'⭐', roles:['admin','employee']},
  {id:'placed',     label:'Placed',     icon:'📌', roles:['admin','employee']},
  {id:'openpositions', label:'Open Positions', icon:'📋', roles:['admin','employee']},
  {id:'map',        label:'Map',        icon:'🗺', roles:['admin','employee']},
  {id:'reminders',  label:'Reminders',  icon:'⏰', roles:['admin','employee']},
  {id:'whatsapp',   label:'WhatsApp',   icon:'💬', roles:['admin']},
  {id:'referrals',  label:'Referrals',  icon:'🤝', roles:['admin','employee']},
  {id:'placements', label:'Placements', icon:'📄', roles:['admin']},
  {id:'reports',    label:'Reports',    icon:'📈', roles:['admin']},
  {id:'activity',   label:'Activity',   icon:'📋', roles:['admin']},
  {id:'profile',    label:'My Business',icon:'⚙️', roles:['admin']},
  {id:'team',       label:'Team',       icon:'👥', roles:['admin']},
];

export default function App() {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const publicProfileForLoading = usePublicProfile(); // only needed for the brief pre-auth loading screen below
  const [tab, setTab] = useState('dashboard');
  const [candidates, setCandidates] = useState([]);
  const [clients, setClients] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [activity, setActivity] = useState([]);
  const [referralPartners, setReferralPartners] = useState([]);
  const [referralPayables, setReferralPayables] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [referralSubmissions, setReferralSubmissions] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [candidateSubmissions, setCandidateSubmissions] = useState([]);
  const [clientSubmissions, setClientSubmissions] = useState([]);
  const [waChats, setWaChats] = useState([]);
  const [partnerAgreements, setPartnerAgreements] = useState([]);
  const [clientAgreements, setClientAgreements] = useState([]);
  const [profile, setProfile] = useState({...DEFAULT_PROFILE});
  useFavicon(profile?.icon || profile?.logo);
  const [toastMsg, setToastMsg] = useState('');
  const [toastKey, setToastKey] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window!=='undefined' && window.innerWidth < 680);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 680);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const toast = useCallback(msg => { setToastMsg(msg); setToastKey(k=>k+1); }, []);
  const addAct = useCallback((text) => {
    logActivity({text, user: user?.name||''}).then(rec => setActivity(p => [...p, rec])).catch(()=>{});
  }, [user]);

  // Unread WhatsApp: tab-title badge, and a native notification when the window is in the background
  // (covers the desktop app; phones/closed browsers are handled by push notifications).
  const prevUnread = useRef({});
  useEffect(() => {
    const total = waChats.reduce((a,c)=>a+(c.unread||0),0);
    document.title = (total>0?`(${total}) `:'') + (document.title||'').replace(/^\(\d+\)\s*/,'');
    const prev = prevUnread.current; const first = Object.keys(prev).length===0;
    waChats.forEach(c => {
      if (!first && (c.unread||0) > (prev[c.phone]||0) && document.hidden && typeof Notification!=='undefined' && Notification.permission==='granted') {
        try { new Notification(`💬 ${c.name||('+'+c.phone)}`, {body:(c.lastText||'').slice(0,140), tag:'wa-'+c.phone, icon:'/icon-192.png'}); } catch(e) {}
      }
    });
    prevUnread.current = Object.fromEntries(waChats.map(c=>[c.phone,c.unread||0]));
  }, [waChats]);

  // ── Init + remember session ─────────────────────────────────────────────────
  useEffect(() => {
    let unsubFn = null;
    async function init() {
      // Always show UI within 6 seconds no matter what
      const safetyTimer = setTimeout(() => setReady(true), 6000);

      if (FIREBASE_ENABLED) {
        const ok = await initFirebase();
        if (!ok) { clearTimeout(safetyTimer); setReady(true); return; }
        try {
          // setPersistence so Firebase remembers login on this device
          const { getAuth: fbGetAuthFn, setPersistence, browserLocalPersistence } = await import('firebase/auth');
          const fbAuth = fbGetAuthFn();
          await setPersistence(fbAuth, browserLocalPersistence);
          unsubFn = fbAuth.onAuthStateChanged(async fbUser => {
            clearTimeout(safetyTimer);
            if (fbUser) {
              try {
                const userDoc = await fbGetDoc('users', fbUser.uid);
                // Never default a missing/unreadable account record to a
                // privileged role — that would mean a revoked or deleted login
                // silently regains staff-level access instead of losing it.
                setUser({uid:fbUser.uid, email:fbUser.email, role:userDoc?.role||null, name:userDoc?.name||fbUser.email, clientId:userDoc?.clientId||''});
              } catch { setUser({uid:fbUser.uid, email:fbUser.email, role:null, name:fbUser.email}); }
            }
            setReady(true);
          }, () => { clearTimeout(safetyTimer); setReady(true); });
        } catch { clearTimeout(safetyTimer); setReady(true); }
      } else {
        // Local mode: check for saved session
        const saved = LS.get('t9_session', null);
        if (saved) setUser(saved);
        clearTimeout(safetyTimer);
        setReady(true);
      }
    }
    init();
    return () => { if (unsubFn) unsubFn(); };
  }, []);

  // Save local session when user logs in
  useEffect(() => {
    if (!FIREBASE_ENABLED) {
      if (user) LS.set('t9_session', user);
      else localStorage.removeItem('t9_session');
    }
  }, [user]);

  // ── Load data after login, then keep syncing live in the background ────────
  useEffect(() => {
    // Client-role accounts use their own scoped fetches inside ClientAppView —
    // they never get `list` access to these collections, so don't even try.
    if (!user || user.role === 'client') return;
    let unsubs = [];
    let cancelled = false;
    (async () => {
      const subs = await Promise.all([
        DB.listenAll('candidates', d => !cancelled && setCandidates(d||[])),
        DB.listenAll('clients', d => !cancelled && setClients(d||[])),
        DB.listenAll('placements', d => !cancelled && setPlacements(d||[])),
        DB.listenAll('activity', d => !cancelled && setActivity(d||[])),
        DB.listenAll('referralPartners', d => !cancelled && setReferralPartners(d||[])),
        DB.listenAll('referralPayables', d => !cancelled && setReferralPayables(d||[])),
        DB.listenAll('contracts', d => !cancelled && setContracts(d||[])),
        DB.listenAll('referralSubmissions', d => !cancelled && setReferralSubmissions(d||[])),
        DB.listenAll('partnerApplications', d => !cancelled && setPartnerApplications(d||[])),
        DB.listenAll('candidateSubmissions', d => !cancelled && setCandidateSubmissions(d||[])),
        DB.listenAll('clientSubmissions', d => !cancelled && setClientSubmissions(d||[])),
        FIREBASE_ENABLED ? DB.listenAll('waChats', d => !cancelled && setWaChats(d||[])) : null,
        DB.listenAll('partnerAgreements', d => !cancelled && setPartnerAgreements(d||[])),
        DB.listenAll('clientAgreements', d => !cancelled && setClientAgreements(d||[])),
        DB.listenProfile(d => !cancelled && d && setProfile({...DEFAULT_PROFILE,...d})),
      ]);
      if (cancelled) { subs.forEach(u=>u&&u()); return; }
      unsubs = subs;
    })();
    return () => { cancelled = true; unsubs.forEach(u=>u&&u()); };
  }, [user]);

  // ── Render ──────────────────────────────────────────────────────────────────
  if (typeof window !== 'undefined') {
    const path = window.location.pathname.replace(/\/$/,'');
    if (path.startsWith('/refer')) return <ReferralSubmitPage/>;
    if (path.startsWith('/partner-signup')) return <PartnerSignupPage/>;
    if (path.startsWith('/candidate-signup')) return <CandidateSignupPage/>;
    if (path.startsWith('/candidate-photo')) return <CandidatePhotoPage/>;
    if (path.startsWith('/candidate')) return <CandidateProfilePage/>;
    if (path.startsWith('/sign-contract')) return <ContractSignPage/>;
    if (path.startsWith('/invoice')) return <InvoicePublicPage/>;
    if (path.startsWith('/client-signup')) return <ClientSignupPage/>;
    if (path.startsWith('/client')) return <ClientPortalPage/>;
  }

  if (!ready) return (
    <div style={{minHeight:'100vh',background:`linear-gradient(135deg,#0f2c4a 0%,${bl} 60%,#1a7ab8 100%)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{textAlign:'center',color:'#fff'}}>
        <BrandBadge profile={publicProfileForLoading} size={64} preferIcon/>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>{publicProfileForLoading?.companyName || 'Three9 CRM'}</div>
        <div style={{fontSize:13,color:'rgba(255,255,255,.7)',marginBottom:20}}>{FIREBASE_ENABLED?'Connecting to cloud...':'Loading...'}</div>
        <div style={{display:'flex',gap:6,justifyContent:'center'}}>
          {[0,1,2].map(i=><div key={i} style={{width:8,height:8,borderRadius:'50%',background:'rgba(255,255,255,.5)',animation:`pulse 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}
        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );

  if (!user) return <LoginPage onLogin={u => setUser(u)} profile={profile}/>;

  const handleLogout = async () => {
    if (FIREBASE_ENABLED) { try { await logoutFirebase(); } catch {} }
    setUser(null);
    setCandidates([]); setClients([]); setPlacements([]); setActivity([]);
    setReferralPartners([]); setReferralPayables([]); setContracts([]); setReferralSubmissions([]);
    setPartnerApplications([]); setPartnerAgreements([]); setClientAgreements([]); setCandidateSubmissions([]); setClientSubmissions([]); setWaChats([]);
    setTab('dashboard');
  };

  // Client-role accounts get an entirely separate, narrowly-scoped view — never
  // the internal staff dashboard, and never the shared realtime data below (which
  // would need broad `list` access this role intentionally doesn't have).
  if (user.role === 'client') return <ClientAppView user={user} onLogout={handleLogout} profile={profile}/>;

  // Anything that isn't a recognized role (missing/deleted account record, a
  // "🗑 Delete login" from Clients, etc.) gets NO access — never fall through
  // to the staff dashboard below just because a role wasn't matched above.
  if (user.role !== 'admin' && user.role !== 'employee') return (
    <div style={{minHeight:'100vh',background:'#f0efe9',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',border:`0.5px solid ${br}`,borderRadius:14,padding:30,textAlign:'center',maxWidth:360}}>
        <div style={{fontSize:32,marginBottom:10}}>🔒</div>
        <div style={{fontSize:14,color:'#666',marginBottom:16}}>This account no longer has access. Contact {profile?.companyName||DEFAULT_PROFILE.companyName} if you think this is a mistake.</div>
        <Btn sm onClick={handleLogout}>Sign out</Btn>
      </div>
    </div>
  );

  const isAdmin = user.role === 'admin';
  const MORE_TAB_IDS = ['placed','reports','activity','profile','team'];
  const tabs = ALL_TABS.filter(t => t.roles.includes(user.role) && !MORE_TAB_IDS.includes(t.id));
  const moreTabs = ALL_TABS.filter(t => t.roles.includes(user.role) && MORE_TAB_IDS.includes(t.id));
  const allVisibleTabs = ALL_TABS.filter(t => t.roles.includes(user.role));
  const currentTab = allVisibleTabs.find(t=>t.id===tab) ? tab : 'dashboard';

  return (
    <div style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div style={{background:'#fff',borderBottom:`0.5px solid ${br}`,paddingTop:'env(safe-area-inset-top)',flexShrink:0}}>
      <div style={{padding:isMobile?'0 8px':'0 20px',display:'flex',alignItems:'center',height:52}}>
        {!isMobile && (
          <div style={{display:'flex',alignItems:'center',gap:8,marginRight:20,flexShrink:0}}>
            {profile.logo?<img src={profile.logo} alt="logo" style={{height:30,maxWidth:90,objectFit:'contain'}}/>:<div style={{fontSize:15,fontWeight:800,color:bl,letterSpacing:'-0.4px'}}>Three9</div>}
          </div>
        )}
        <div style={{display:'flex',alignItems:'center',flex:1,overflowX:'auto'}}>
          {tabs.map(t => (
            <div key={t.id} onClick={()=>setTab(t.id)} title={t.label} style={{padding:isMobile?'0 8px':'0 11px',height:52,display:'flex',alignItems:'center',gap:4,cursor:'pointer',fontSize:12.5,color:currentTab===t.id?bl:'#666',borderBottom:currentTab===t.id?`2px solid ${bl}`:'2px solid transparent',fontWeight:currentTab===t.id?600:400,whiteSpace:'nowrap',flexShrink:0}}>
              <span style={{fontSize:isMobile?16:12}}>{t.icon}</span> {!isMobile && t.label}
              {t.id==='match'&&candidates.length>0&&clients.length>0&&<span style={{background:rd,color:'#fff',fontSize:10,padding:'1px 5px',borderRadius:10,fontWeight:700,marginLeft:2}}>{clients.length}</span>}
              {t.id==='reminders'&&(()=>{const n=[...candidates,...clients].filter(c=>c.followUpDate&&c.followUpDate<new Date().toISOString().slice(0,10)).length; return n>0?<span style={{background:rd,color:'#fff',fontSize:10,padding:'1px 5px',borderRadius:10,fontWeight:700,marginLeft:2}}>{n}</span>:null;})()}
              {t.id==='referrals'&&(()=>{const n=referralPayables.filter(p=>(p.status==='Scheduled'||p.status==='Pending')&&p.dueDate<new Date().toISOString().slice(0,10)).length + referralSubmissions.filter(s=>s.status==='New').length + partnerApplications.filter(a=>a.status==='New').length; return n>0?<span style={{background:rd,color:'#fff',fontSize:10,padding:'1px 5px',borderRadius:10,fontWeight:700,marginLeft:2}}>{n}</span>:null;})()}
            </div>
          ))}
        </div>
        {moreTabs.length>0 && (
          <div style={{position:'relative',flexShrink:0}}>
            <div onClick={()=>setShowMore(s=>!s)} title="More" style={{padding:isMobile?'0 8px':'0 11px',height:52,display:'flex',alignItems:'center',gap:4,cursor:'pointer',fontSize:12.5,color:moreTabs.some(t=>t.id===currentTab)?bl:'#666',borderBottom:moreTabs.some(t=>t.id===currentTab)?`2px solid ${bl}`:'2px solid transparent',fontWeight:moreTabs.some(t=>t.id===currentTab)?600:400,whiteSpace:'nowrap'}}>
              <span style={{fontSize:isMobile?16:12}}>⋯</span> {!isMobile && 'More'}
            </div>
            {showMore && (
              <>
                <div onClick={()=>setShowMore(false)} style={{position:'fixed',inset:0,zIndex:40}}/>
                <div style={{position:'absolute',top:52,right:0,background:'#fff',border:`0.5px solid ${br}`,borderRadius:8,boxShadow:'0 6px 20px rgba(0,0,0,.12)',minWidth:170,zIndex:41,padding:6}}>
                  {moreTabs.map(t => (
                    <div key={t.id} onClick={()=>{setTab(t.id);setShowMore(false);}} style={{padding:'8px 10px',borderRadius:6,cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',gap:7,color:currentTab===t.id?bl:tx,background:currentTab===t.id?'#E6F1FB':'transparent',fontWeight:currentTab===t.id?600:400}}>
                      <span>{t.icon}</span> {t.label}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <div style={{display:'flex',alignItems:'center',gap:isMobile?6:10,flexShrink:0,marginLeft:isMobile?6:12}}>
          {!isMobile && (
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:12,fontWeight:600,color:tx}}>{user.name}</div>
              <div style={{fontSize:10,color:isAdmin?bl:'#888'}}>{isAdmin?'👑 Admin':'👤 Employee'}</div>
            </div>
          )}
          <Btn sm onClick={handleLogout}>{isMobile?'⏻':'Sign out'}</Btn>
        </div>
      </div>
      </div>
      <div style={{flex:1,overflowY:'auto'}}>
        <div style={{padding:20,maxWidth:960,margin:'0 auto',width:'100%'}}>
          {currentTab==='dashboard' && (isAdmin
            ? <DashboardTab candidates={candidates} clients={clients} placements={placements} activity={activity} referralPayables={referralPayables} user={user} clientAgreements={clientAgreements} candidateSubmissions={candidateSubmissions} clientSubmissions={clientSubmissions} waChats={waChats} partnerApplications={partnerApplications} referralSubmissions={referralSubmissions} goTab={setTab}/>
            : <MyDashboardTab candidates={candidates} placements={placements} referralPartners={referralPartners} referralPayables={referralPayables} user={user}/>)}
          {currentTab==='candidates' && <CandidatesTab candidates={candidates} setCandidates={setCandidates} toast={toast} profile={profile} addAct={addAct} referralPartners={referralPartners} user={user} isAdmin={isAdmin} candidateSubmissions={candidateSubmissions} setCandidateSubmissions={setCandidateSubmissions}/>}
          {currentTab==='pipeline' && <PipelineTab candidates={candidates} setCandidates={setCandidates} clients={clients} setClients={setClients} placements={placements} setPlacements={setPlacements} referralPartners={referralPartners} setReferralPayables={setReferralPayables} toast={toast} addAct={addAct} user={user} isAdmin={isAdmin}/>}
          {currentTab==='clients' && isAdmin && <ClientsTab clients={clients} setClients={setClients} toast={toast} addAct={addAct} profile={profile} clientAgreements={clientAgreements} setClientAgreements={setClientAgreements} candidates={candidates} clientSubmissions={clientSubmissions} setClientSubmissions={setClientSubmissions}/>}
          {currentTab==='match' && <MatchTab candidates={candidates} clients={clients} setClients={setClients} placements={placements} setPlacements={setPlacements} toast={toast} isAdmin={isAdmin} setCandidates={setCandidates} addAct={addAct} referralPartners={referralPartners} setReferralPayables={setReferralPayables} user={user}/>}
          {currentTab==='placed' && <PlacedTab candidates={candidates} placements={placements} clients={clients} isAdmin={isAdmin} user={user}/>}
          {currentTab==='whatsapp' && isAdmin && <WhatsAppTab chats={waChats} candidates={candidates} clients={clients} profile={profile} toast={toast}/>}
          {currentTab==='priority' && <PriorityTab clients={clients} setClients={setClients} candidates={candidates} placements={placements} isAdmin={isAdmin} toast={toast} addAct={addAct}/>}
          {currentTab==='openpositions' && <OpenPositionsTab clients={clients} candidates={candidates} placements={placements}/>}
          {currentTab==='map' && <MapTab clients={clients} candidates={candidates} placements={placements}/>}
          {currentTab==='reminders' && <RemindersTab candidates={candidates} clients={clients} setCandidates={setCandidates} setClients={setClients} toast={toast} addAct={addAct} user={user} isAdmin={isAdmin}/>}
          {currentTab==='referrals' && <ReferralsTab referralPartners={referralPartners} setReferralPartners={setReferralPartners} referralPayables={referralPayables} setReferralPayables={setReferralPayables} referralSubmissions={referralSubmissions} setReferralSubmissions={setReferralSubmissions} candidates={candidates} setCandidates={setCandidates} toast={toast} addAct={addAct} profile={profile} placements={placements} partnerApplications={partnerApplications} setPartnerApplications={setPartnerApplications} partnerAgreements={partnerAgreements} setPartnerAgreements={setPartnerAgreements} isAdmin={isAdmin}/>}
          {currentTab==='placements' && isAdmin && <PlacementsTab placements={placements} setPlacements={setPlacements} clients={clients} profile={profile} toast={toast}/>}
          {currentTab==='reports' && isAdmin && <ReportsTab placements={placements} candidates={candidates} clients={clients}/>}
          {currentTab==='activity' && isAdmin && <ActivityTab activity={activity}/>}
          {currentTab==='profile' && isAdmin && <ProfileTab profile={profile} setProfile={setProfile} toast={toast} candidates={candidates} clients={clients} placements={placements} activity={activity} referralPartners={referralPartners} referralPayables={referralPayables} setCandidates={setCandidates} setClients={setClients} setPlacements={setPlacements} setActivity={setActivity} setReferralPartners={setReferralPartners} setReferralPayables={setReferralPayables} addAct={addAct}/>}
          {currentTab==='team' && isAdmin && <TeamTab currentUser={user} toast={toast} candidates={candidates} placements={placements} referralPartners={referralPartners}/>}
        </div>
      </div>
      {toastMsg && <Toast key={toastKey} msg={toastMsg} onDone={()=>setToastMsg('')}/>}
    </div>
  );
}
